<!-- resources/js/components/EmailCampaigns.vue -->
<template>
  <div class="campaigns-manager">
    <!-- Lista de Campanhas -->
    <div v-if="!showCampaignForm" class="campaigns-view">
      <div class="header-section modern-header">
        <div class="title-wrapper">
          <div class="icon-circle">
            <i class="fa-solid fa-paper-plane"></i>
          </div>
          <h2>Campanhas de Email</h2>
        </div>

        <div class="header-actions">
          <button @click="loadCampaigns" class="btn btn-refresh" :disabled="loading">
            <i class="fa-solid fa-arrows-rotate"></i>
            {{ loading ? 'Carregando...' : 'Atualizar' }}
          </button>
          <button @click="createNewCampaign" class="btn btn-primary">
            <i class="fa-solid fa-circle-plus"></i>
            Nova Campanha
          </button>
        </div>
      </div>
      <div class="quota-div">
        <!-- 🆕 Badge de E-mails Restantes com Data de Renovação -->
        <div class="quota-badge" v-if="emailQuota">
          <div class="quota-badge-icon">
            <i class="fa-solid fa-envelope-circle-check"></i>
          </div>
          <div class="quota-badge-content">
            <span class="quota-badge-header">E-MAILS RESTANTES</span>
            <div class="quota-badge-values">
              <div class="quota-badge-item">
                <span class="quota-badge-label">Usado</span>
                <span class="quota-badge-number quota-badge-total">{{ formatNumber(emailQuota.used) }}</span>
              </div>
              <span class="quota-badge-separator">/</span>
              <div class="quota-badge-item">
                <span class="quota-badge-label">Restantes</span>
                <span class="quota-badge-number quota-badge-remaining">{{ formatNumber(emailQuota.remaining) }}</span>
              </div>
              <span class="quota-badge-separator">/</span>
              <div class="quota-badge-item">
                <span class="quota-badge-label">Total do Plano</span>
                <span class="quota-badge-number quota-badge-total">{{ formatNumber(emailQuota.total) }}</span>
              </div>
            </div>
            <div class="quota-badge-progress">
              <div class="quota-badge-progress-bar" :style="{ width: quotaPercentage + '%' }" :class="getQuotaClass()">
              </div>
            </div>
            <div class="quota-badge-footer">
              <span class="quota-badge-percentage">{{ quotaPercentage }}% disponível</span>
              <span class="quota-badge-renewal" v-if="emailQuota.end_date">
                <i class="fa-solid fa-calendar-check"></i>
                Renova em {{ formatDateBR(emailQuota.end_date) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <hr>

      <div class="filter-buttons">
        <div class="filter-label">
          <i class="fa-solid fa-filter"></i>
          <span>Filtrar</span>
        </div>

        <button @click="filterStatus = 'all'" class="btn btn-sm btn-primary">
          <i class="fa-solid fa-list"></i> Todas ({{ campaigns.length }})
        </button>
        <button @click="filterStatus = 'sent'" class="btn btn-sm btn-success">
          <i class="fa-solid fa-check-circle"></i> Enviadas ({{ sentCount }})
        </button>
        <button @click="filterStatus = 'scheduled'" class="btn btn-sm btn-info">
          <i class="fa-solid fa-clock"></i> Agendadas ({{ scheduledCount }})
        </button>
        <button @click="filterStatus = 'draft'" class="btn btn-sm btn-secondary">
          <i class="fa-solid fa-file-lines"></i> Rascunhos ({{ draftCount }})
        </button>
        <button @click="filterStatus = 'archived'" class="btn btn-sm btn-warning">
          <i class="fa-solid fa-archive"></i> Arquivadas ({{ archivedCount }})
        </button>
      </div>

      <!-- Mensagens -->
      <div v-if="errorMessage" class="alert alert-error">
        <span class="icon"><i class="fa-solid fa-triangle-exclamation"></i></span>
        {{ errorMessage }}
        <button @click="errorMessage = ''" class="alert-close"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <div v-if="successMessage" class="alert alert-success">
        <span class="icon"><i class="fa-solid fa-circle-check"></i></span>
        {{ successMessage }}
        <button @click="successMessage = ''" class="alert-close"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Carregando campanhas...</p>
      </div>

      <!-- Lista de campanhas -->
      <div v-else class="campaigns-grid">
        <div v-for="campaign in paginatedCampaigns" :key="campaign.id" class="campaign-card">
          <div class="campaign-header">
            <div class="campaign-status-badge" :class="getStatusClass(campaign.status)">
              <i :class="getStatusIcon(campaign.status)"></i>
              <span>{{ getStatusLabel(campaign.status) }}</span>
            </div>
            <div class="campaign-id">ID: {{ campaign.id }}</div>
          </div>

          <!-- No loop de campanhas, adicione após a parte de scheduledAt -->
          <div class="campaign-info">
            <h3>{{ campaign.name }}</h3>
            <p class="campaign-subject">
              <strong>Assunto:</strong> {{ campaign.subject }}
            </p>
            <p class="campaign-date">
              <strong>Criada:</strong> {{ formatDate(campaign.createdAt) }}<br>
              <strong>Modificada:</strong> {{ formatDate(campaign.modifiedAt) }}<br>
              <template v-if="campaign.status === 'suspended'">
                <strong>Agendamento:</strong> {{ formatDate(campaign.scheduledAt) }}
              </template>
            </p>

            <!-- Mostra data de agendamento para campanhas agendadas
            <p v-if="campaign.scheduledAt && campaign.status === 'scheduled'" class="campaign-scheduled">
              <i class="fa-solid fa-calendar-check"></i>
              <strong>Agendada para:</strong>
              <span class="scheduled-date">{{ formatDate(campaign.scheduledAt) }}</span>
            </p> -->

            <!-- NOVO: Mostra data de agendamento para campanhas na fila -->
            <p v-if="campaign.scheduledAt && campaign.status === 'queued' || campaign.status === 'scheduled'"
              class="campaign-queued">
              <i class="fa-solid fa-calendar-check"></i>
              <strong>Agendada para:</strong>
              <span class="scheduled-date">{{ formatDate(campaign.scheduledAt) }}</span>
            </p>

            <p v-if="campaign.scheduledAt && campaign.status === 'suspended'" class="campaign-suspended">
              <i class="fa-solid fa-calendar-check"></i>
              <strong>Suspensa em:</strong>
              <span class="scheduled-date">{{ formatDate(campaign.modifiedAt) }}</span>
            </p>

            <!-- Template e Lote -->
            <div v-if="campaign.lista_template || campaign.lista_arquivo" class="campaign-list-info">
              <p class="campaign-list-info-item" v-if="campaign.lista_template">
                <i class="fa-solid fa-file-lines"></i>
                <strong>Template:</strong>
                <span>{{ campaign.lista_template }}</span>
              </p>
              <p class="campaign-list-info-item" v-if="campaign.lista_arquivo">
                <i class="fa-solid fa-box-archive"></i>
                <strong>Lote:</strong>
                <span class="lote-text" :title="campaign.lista_arquivo">
                  {{ formatLote(campaign.lista_arquivo) }}
                </span>
              </p>
            </div>

            <!-- Mostra data de envio para campanhas enviadas -->
            <p v-if="campaign.sentDate && campaign.status === 'sent'" class="campaign-sent-date">
              <i class="fa-solid fa-paper-plane"></i>
              <strong>Enviada em:</strong> {{ formatDate(campaign.sentDate) }}
            </p>

            <!-- Mostra status de arquivo -->
            <p v-if="campaign.isArchived" class="campaign-archived-status">
              <i class="fa-solid fa-archive"></i>
              <strong>Arquivada em:</strong> {{ formatDate(campaign.modifiedAt) }}
            </p>
          </div>

          <!-- Ações do Card de Campanha - ATUALIZADO -->
          <div class="campaign-actions">
            <!-- Editar (somente rascunhos) -->
            <button v-if="campaign.status === 'draft'" @click="editCampaign(campaign.id)" class="btn btn-sm btn-primary"
              :disabled="loading">
              <i class="fa-solid fa-pen"></i> Editar
            </button>

            <!-- 🔥 NOVO: Ver Progresso (campanhas enviando) -->
            <button v-if="campaign.status === 'sending'" @click="viewDetailedMetrics(campaign)"
              class="btn btn-sm btn-warning" :disabled="loading">
              <i class="fa-solid fa-spinner fa-spin"></i> Ver Progresso
            </button>

            <!-- Ver Métricas (somente enviadas) -->
            <button v-if="campaign.status === 'sent'" @click="viewDetailedMetrics(campaign)" class="btn btn-sm btn-info"
              :disabled="loading">
              <i class="fa-solid fa-chart-bar"></i> Ver Métricas
            </button>

            <!-- Suspender (somente agendadas) -->
            <button v-if="campaign.status === 'queued'" @click="openSuspendModal(campaign.id, campaign.name)"
              class="btn btn-sm btn-warning" :disabled="loading">
              <i class="fa-solid fa-pause"></i> Suspender
            </button>

            <!-- Retomar (somente suspensas) -->
            <button v-if="campaign.status === 'suspended'" @click="openResumeModal(campaign.id, campaign.name)"
              class="btn btn-sm btn-success" :disabled="loading">
              <i class="fa-solid fa-play"></i> Retomar
            </button>

            <!-- Arquivar (não arquivadas) -->
            <button v-if="!campaign.isArchived" @click="openArchiveModal(campaign.id, campaign.name)"
              class="btn btn-sm btn-secondary" :disabled="loading">
              <i class="fa-solid fa-archive"></i> Arquivar
            </button>

            <!-- Desarquivar (apenas arquivadas) -->
            <button v-if="campaign.isArchived" @click="openUnarchiveModal(campaign.id, campaign.name)"
              class="btn btn-sm btn-info" :disabled="loading">
              <i class="fa-solid fa-box-open"></i> Desarquivar
            </button>

            <!-- Excluir (rascunhos e agendadas) -->
            <button v-if="campaign.status === 'draft' || campaign.status === 'scheduled'"
              @click="openDeleteModal(campaign.id, campaign.name)" class="btn btn-sm btn-danger" :disabled="loading">
              <i class="fa-solid fa-trash"></i> Excluir
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredCampaigns.length === 0 && !loading" class="empty-state">
          <div class="empty-icon"><i class="fa-solid fa-paper-plane"></i></div>
          <h3>{{ getEmptyStateMessage() }}</h3>
          <p v-if="filterStatus === 'all' || filterStatus === 'archived'">Nenhuma campanha encontrada</p>
          <p v-else>Tente alterar o filtro para ver outras campanhas</p>
        </div>


      </div>
      <!-- Paginação -->
      <div v-if="filteredCampaigns.length > 0 && totalPages > 1" class="pagination-container">
        <div class="pagination-info">
          Mostrando {{ showingFrom }} - {{ showingTo }} de {{ filteredCampaigns.length }} campanhas
        </div>

        <div class="pagination-controls">
          <button @click="previousPage" class="btn btn-sm btn-secondary" :disabled="currentPage === 1">
            <i class="fa-solid fa-chevron-left"></i> Anterior
          </button>

          <div class="pagination-pages">
            <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="btn btn-sm"
              :class="{ 'btn-primary': page === currentPage, 'btn-secondary': page !== currentPage }">
              {{ page }}
            </button>
          </div>

          <button @click="nextPage" class="btn btn-sm btn-secondary" :disabled="currentPage === totalPages">
            Próximo <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Formulário de Campanha -->
    <div v-else class="campaign-form-view">
      <div class="form-header">
        <button @click="closeCampaignForm" class="btn btn-secondary">
          <i class="fa-solid fa-arrow-left"></i> Voltar
        </button>
        <h2>{{ currentCampaignId ? 'Editar Campanha' : 'Nova Campanha' }}</h2>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps">
        <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <div class="step-label">Informações</div>
        </div>
        <div class="step-line" :class="{ completed: currentStep > 1 }"></div>
        <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
          <div class="step-number">2</div>
          <div class="step-label">Template</div>
        </div>
        <div class="step-line" :class="{ completed: currentStep > 2 }"></div>
        <div class="step" :class="{ active: currentStep === 3, completed: currentStep > 3 }">
          <div class="step-number">3</div>
          <div class="step-label">Destinatários</div>
        </div>
        <div class="step-line" :class="{ completed: currentStep > 3 }"></div>
        <div class="step" :class="{ active: currentStep === 4 }">
          <div class="step-number">4</div>
          <div class="step-label">Enviar</div>
        </div>
      </div>

      <div class="form-content">
        <!-- Passo 1: Informações Básicas -->
        <div class="form-section" v-show="currentStep === 1">
          <h3><i class="fa-solid fa-info-circle"></i> Informações da Campanha</h3>

          <!-- Nome da Campanha -->
          <div class="form-group">
            <label>Nome da Campanha *</label>
            <input v-model="campaignForm.name" type="text" class="form-control" :class="{ 'is-invalid': errors.name }"
              placeholder="Ex: Newsletter Dezembro 2024" maxlength="60">
            <small class="form-text text-muted">
              <i class="fa-solid fa-tag"></i>
              {{ campaignForm.name.length }}/60 caracteres
            </small>
            <small v-if="errors.name" class="error-msg">{{ errors.name }}</small>
          </div>

          <!-- Assunto -->
          <!-- <div class="form-group">
            <label>
              Assunto do Email *
              <span v-if="campaignForm.subject.length > 60" style="color: #f59e0b; font-weight: bold;">
                ⚠️ Pode ser cortado em dispositivos móveis
              </span>
            </label>
            <input v-model="campaignForm.subject" type="text" class="form-control"
              :class="{ 'is-invalid': errors.subject }" placeholder="Ex: Novidades incríveis para você!" maxlength="60">
            <small class="form-text text-muted">
              <i class="fa-solid fa-envelope"></i>
              {{ campaignForm.subject.length }}/60 caracteres
              <span v-if="campaignForm.subject.length <= 60" style="color: #10b981; font-weight: bold;">
                ✓ Ideal para Gmail/Outlook (Desktop e Mobile)
              </span>
            </small>
            <small v-if="errors.subject" class="error-msg">{{ errors.subject }}</small>
          </div> -->

          <!-- Preview Text -->
          <div class="form-group">
            <label>
              Preview Text (Opcional)
              <span v-if="campaignForm.previewText.length > 100" style="color: #f59e0b; font-weight: bold;">
                ⚠️ Pode ser cortado em dispositivos móveis
              </span>
            </label>
            <input v-model="campaignForm.previewText" type="text" class="form-control"
              placeholder="Texto que aparece no preview do email" maxlength="100">
            <small class="form-text text-muted">
              <i class="fa-solid fa-mobile-screen"></i>
              {{ campaignForm.previewText.length }}/100 caracteres
              <span v-if="campaignForm.previewText.length <= 100" style="color: #10b981; font-weight: bold;">
                ✓ Ideal para mobile
              </span>
            </small>
          </div>

          <!-- Nome do Remetente -->
          <div class="form-group">
            <label>Nome do Remetente *</label>
            <input v-model="configEmail.senderName" type="text" class="form-control" disabled
              :class="{ 'is-invalid': errors.senderName }" placeholder="Ex: Sua Empresa" maxlength="35">
            <small class="form-text text-muted">
              <i class="fa-solid fa-user"></i>
              {{ configEmail.senderName.length }}/ 35 caracteres
            </small>
            <small v-if="errors.senderName" class="error-msg">{{ errors.senderName }}</small>
          </div>

          <!-- Email do Remetente -->
          <div class="form-group">
            <label>Email do Remetente *</label>
            <input v-model="configEmail.senderEmail" type="email" class="form-control" disabled
              placeholder="Email preenchido automaticamente">
            <small class="form-text text-muted">
              Este email será usado como remetente e é preenchido automaticamente a partir da configuração do sistema.
            </small>
          </div>

          <!-- Email de Resposta -->
          <div class="form-group">
            <label>Email de Resposta (Opcional)</label>
            <input v-model="campaignForm.replyTo" type="email" class="form-control"
              :class="{ 'is-invalid': errors.replyTo }" placeholder="Ex: resposta@suaempresa.com">
            <small class="form-text text-muted">
              Deixe vazio para utilizar o remetente como email de resposta padrão (Ao editar, se estiver como
              [DEFAULT_REPLY_TO] basta removê-lo).
            </small>
            <small v-if="errors.replyTo" class="error-msg">{{ errors.replyTo }}</small>
          </div>

          <div class="form-actions">
            <button @click="nextStep" class="btn btn-primary">
              Próximo <i class="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Passo 2: Selecionar Template -->
        <div class="form-section" v-show="currentStep === 2">
          <h3><i class="fa-solid fa-file-lines"></i> Selecionar Template</h3>

          <div v-if="loadingTemplates" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando templates...</p>
          </div>

          <div v-else class="templates-selection">
            <div v-for="template in paginatedTemplates" :key="template.id" class="template-select-card"
              :class="{ selected: campaignForm.templateId === template.id }">

              <!-- Thumbnail do Template -->
              <div class="template-thumbnail" @click="selectTemplate(template.id)">
                <div v-if="template.thumbnailUrl" class="thumbnail-image">
                  <img :src="template.thumbnailUrl" :alt="template.name" loading="lazy">
                </div>
                <div v-else-if="template.htmlContent" class="thumbnail-placeholder">
                  <i class="fa-solid fa-envelope-open"></i>
                </div>
                <div v-else class="thumbnail-empty">
                  <i class="fa-solid fa-file-lines"></i>
                </div>
              </div>

              <!-- Informações do Template -->
              <div class="template-select-info" @click="selectTemplate(template.id)">
                <h4>{{ template.name }}</h4>
                <p v-if="template.subject" class="template-subject">{{ template.subject }}</p>
              </div>

              <!-- Botões de Ação -->
              <div class="template-actions">
                <button @click.stop="openTemplatePreview(template)" class="btn btn-sm btn-info"
                  title="Visualizar template completo">
                  <i class="fa-solid fa-eye"></i> Preview
                </button>
              </div>

              <!-- Indicador de Seleção -->
              <div v-if="campaignForm.templateId === template.id" class="selected-indicator-templates">
                <i class="fa-solid fa-check-circle"></i>
              </div>
            </div>

            <div v-if="activeTemplates.length === 0" class="empty-state">
              <p>Nenhum template ativo encontrado. Crie um template primeiro.</p>
            </div>
          </div>

          <!-- Paginação de Templates -->
          <div v-if="activeTemplates.length > templatesPerPage" class="template-pagination">
            <div class="pagination-info-template">
              Mostrando {{ templateStartIndex + 1 }} - {{ templateEndIndex }} de {{ activeTemplates.length }} templates
            </div>

            <div class="pagination-controls-template">
              <!-- Botão Anterior -->
              <button @click="previousTemplatePage" class="btn-pagination" :disabled="currentTemplatePage === 1">
                <i class="fa-solid fa-chevron-left"></i>
                Anterior
              </button>

              <!-- Números das Páginas -->
              <div class="pagination-numbers">
                <button v-for="page in totalTemplatePages" :key="page" @click="goToTemplatePage(page)"
                  class="btn-pagination-number" :class="{ 'active': page === currentTemplatePage }">
                  {{ page }}
                </button>
              </div>

              <!-- Botão Próximo -->
              <button @click="nextTemplatePage" class="btn-pagination"
                :disabled="currentTemplatePage === totalTemplatePages">
                Próximo
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>

          <div class="form-actions">
            <button @click="previousStep" class="btn btn-secondary">
              <i class="fa-solid fa-arrow-left"></i> Voltar
            </button>
            <button @click="nextStep" class="btn btn-primary" :disabled="!campaignForm.templateId">
              Próximo <i class="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Passo 3: Selecionar Contatos -->
        <div class="form-section" v-show="currentStep === 3">
          <div class="section-header-with-actions">
            <h3><i class="fa-solid fa-users"></i> Selecionar Destinatários</h3>

            <!-- Botões de Ação no Topo Direito -->
            <div class="actions-top-right" v-if="recipientType === 'lists'">
              <button @click="showCreateListModal = true" class="btn btn-sm btn-success">
                <i class="fa-solid fa-plus-circle"></i>
                <span>Criar Nova Lista</span>
              </button>
              <button @click="showAddContactModal = true" class="btn btn-sm btn-primary">
                <i class="fa-solid fa-user-plus"></i>
                <span>Adicionar Contato</span>
              </button>
              <button @click="showBulkAddContactModal = true" class="btn btn-sm btn-info">
                <i class="fa-solid fa-users"></i>
                <span>Adicionar em Massa</span>
              </button>
            </div>

            <div class="actions-top-right" v-else-if="recipientType === 'segments'">
              <button @click="showCreateSegmentModal = true" class="btn btn-sm btn-success">
                <i class="fa-solid fa-plus-circle"></i>
                <span>Criar Novo Segmento</span>
              </button>
            </div>
          </div>

          <div v-if="loadingLists" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando listas...</p>
          </div>

          <!-- Passo 3 do formulário - Seleção de Listas -->
          <div v-else-if="recipientType === 'lists'" class="lists-selection">

            <!-- 🎯 MENSAGEM INFORMATIVA -->
            <div v-if="campaignForm.listIds.length === 0" class="selection-reminder">
              <div class="selection-reminder-icon">👇</div>
              <div class="selection-reminder-text">
                <strong>Selecione uma lista de contatos abaixo</strong> para continuar com o envio da campanha
              </div>
            </div>

            <!-- Grid de Listas -->
            <div class="lists-grid">
              <!-- Card de cada lista -->
              <div v-for="list in paginatedContactLists" :key="list.id" class="list-select-card"
                :class="{ selected: campaignForm.listIds.includes(list.id) }">
                <!-- Área clicável do card -->
                <div class="list-select-content" @click="toggleList(list.id)" style="cursor: pointer;">
                  <div class="list-select-info">
                    <h4 class="list-name">{{ list.name }}</h4>
                    <p class="list-count">
                      <i class="fa-solid fa-users"></i>
                      {{ list.uniqueSubscribers || 0 }} contatos
                    </p>
                  </div>

                  <!-- ✅ Indicador SEMPRE VISÍVEL -->
                  <div class="selected-indicator" :class="{ 'is-selected': campaignForm.listIds.includes(list.id) }">
                    <i class="fa-solid fa-check-circle"></i>
                  </div>
                </div>

                <!-- Área de botões lado a lado -->
                <div class="list-actions-buttons">
                  <button @click.stop="viewListContacts(list.id, list.name)" class="btn btn-sm btn-info">
                    <i class="fa-solid fa-eye"></i> Ver Contatos
                  </button>

                  <button type="button" @click.stop="importLatestFile(list.id, list.name)"
                    class="btn btn-sm btn-success" :disabled="loadingImport">
                    <i class="fa-solid fa-file-import"></i>
                    {{ loadingImport ? 'Importando...' : 'Importar JSON' }}
                  </button>

                  <!-- NOVOS BOTÕES -->
                  <button @click.stop="openEditListModal(list.id, list.name)" class="btn btn-sm btn-warning"
                    title="Editar Lista">
                    <i class="fa-solid fa-pen"></i>
                  </button>
                  <button @click.stop="openDeleteListModal(list.id, list.name)" class="btn btn-sm btn-danger"
                    title="Excluir Lista">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>

              <!-- Estado vazio -->
              <div v-if="contactLists.length === 0 && !loadingLists" class="empty-state">
                <p>Nenhuma lista de contatos encontrada.</p>
              </div>
            </div>
          </div>


          <!-- Mesmo para segmentos -->
          <div v-else-if="recipientType === 'segments'" class="segments-selection">
            <!-- Cards dos Segmentos -->
            <div class="segments-grid">
              <div v-for="segment in segments" :key="segment.id" class="segment-select-card"
                :class="{ selected: campaignForm.segmentIds.includes(segment.id) }">

                <div @click="toggleSegment(segment.id)" style="flex: 1; cursor: pointer;">
                  <div class="segment-select-info">
                    <h4>{{ segment.name }}</h4>
                    <p class="segment-count">
                      <i class="fa-solid fa-filter"></i> Segmento dinâmico
                    </p>
                  </div>

                  <div v-if="campaignForm.segmentIds.includes(segment.id)" class="selected-indicator">
                    <i class="fa-solid fa-check-circle"></i>
                  </div>
                </div>

                <!-- Botão para ver contatos do segmento -->
                <button @click.stop="viewSegmentContacts(segment.id, segment.name)" class="btn btn-sm btn-info"
                  style="margin-top: 10px;">
                  <i class="fa-solid fa-eye"></i> Ver Contatos
                </button>
              </div>

              <div v-if="segments.length === 0" class="empty-state">
                <p>Nenhum segmento encontrado.</p>
              </div>
            </div>
          </div>

          <!-- Paginação de Listas -->
          <div v-if="contactLists.length > listsPerPage && listsTotalPages > 1" class="pagination-container"
            style="margin-top: 30px;">
            <div class="pagination-info">
              Mostrando {{ listsShowingFrom }} - {{ listsShowingTo }} de {{ contactLists.length }} listas
            </div>

            <div class="pagination-controls">
              <button @click="listsPreviousPage" class="btn btn-sm btn-secondary" :disabled="listsCurrentPage === 1">
                <i class="fa-solid fa-chevron-left"></i> Anterior
              </button>

              <div class="pagination-pages">
                <button v-for="page in listsTotalPages" :key="page" @click="listsGoToPage(page)" class="btn btn-sm"
                  :class="{ 'btn-primary': page === listsCurrentPage, 'btn-secondary': page !== listsCurrentPage }">
                  {{ page }}
                </button>
              </div>

              <button @click="listsNextPage" class="btn btn-sm btn-secondary"
                :disabled="listsCurrentPage === listsTotalPages">
                Próximo <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>

          <div class="recipient-summary">
            <strong>Destinatários selecionados:</strong>
            {{ getTotalRecipients() }} contatos
          </div>

          <div class="form-actions">
            <button @click="previousStep" class="btn btn-secondary">
              <i class="fa-solid fa-arrow-left"></i> Voltar
            </button>
            <button @click="nextStep" class="btn btn-primary"
              :disabled="campaignForm.listIds.length === 0 && campaignForm.segmentIds.length === 0">
              Próximo <i class="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Passo 4: Agendar/Enviar -->
        <div class="form-section" v-show="currentStep === 4">
          <h3><i class="fa-solid fa-calendar-check"></i> Agendar Envio</h3>

          <div class="send-options">
            <div class="form-group">
              <label>
                <input type="radio" v-model="sendType" value="now">
                Enviar Imediatamente
              </label>
            </div>

            <div class="form-group">
              <label>
                <input type="radio" v-model="sendType" value="schedule">
                Agendar Envio
              </label>
            </div>

            <div class="form-group">
              <label>
                <input type="radio" v-model="sendType" value="draft">
                Salvar como Rascunho
              </label>
            </div>
          </div>

          <div v-if="sendType === 'schedule'" class="schedule-options">
            <div class="form-group">
              <label>Data e Hora do Envio *</label>
              <input v-model="campaignForm.scheduledAt" type="datetime-local" class="form-control" required>
              <small class="form-text">Selecione uma data e hora futura para o envio automático</small>
            </div>
          </div>

          <div class="campaign-summary">
            <h4>Resumo da Campanha</h4>
            <ul>
              <li><strong>Nome:</strong> {{ campaignForm.name }}</li>
              <li>
                <strong>Assunto:</strong>
                <span style="color: #6b7280; font-style: italic;">
                  (Será usado o assunto do template selecionado)
                </span>
              </li>
              <li><strong>Template:</strong> {{ getTemplateName(campaignForm.templateId) }}</li>
              <li><strong>Destinatários:</strong> {{ getTotalRecipients() }} contatos</li>
              <li v-if="sendType === 'schedule' && campaignForm.scheduledAt">
                <strong>Envio Agendado:</strong>
                <span class="highlight-date">{{ formatDateTime(campaignForm.scheduledAt) }}</span>
              </li>
            </ul>
          </div>

          <div class="form-actions">
            <button @click="previousStep" class="btn btn-secondary">
              <i class="fa-solid fa-arrow-left"></i> Voltar
            </button>
            <button @click="saveCampaign" class="btn btn-success" :disabled="sending">
              <i class="fa-solid fa-paper-plane"></i>
              {{ getSendButtonText() }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Métricas Detalhadas - ATUALIZADO -->
    <div v-if="showMetricsModal" class="modal-overlay">
      <div class="modal-content-2 modal-large">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-chart-line"></i>
            Métricas Detalhadas
          </h3>
          <span v-if="selectedCampaignMetrics?.status === 'sending'" class="badge-sending">
            <i class="fa-solid fa-spinner fa-spin"></i> Enviando
          </span>
          <button @click="closeMetricsModal" class="modal-close-btn" style="cursor: pointer;">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loadingMetrics" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando métricas...</p>
          </div>

          <div v-else-if="selectedCampaignMetrics" class="detailed-metrics">
            <h4>{{ selectedCampaignMetrics.name }}</h4>

            <!-- 🔥 BARRA DE PROGRESSO -->
            <div v-if="selectedCampaignMetrics.progresso" class="progress-section">
              <div class="progress-header">
                <span class="progress-label">Progresso do Envio</span>
                <span class="progress-percentage">{{ selectedCampaignMetrics.progresso.percentual }}%</span>
              </div>

              <div class="progress-bar-container">
                <div class="progress-bar-fill" :style="{ width: selectedCampaignMetrics.progresso.percentual + '%' }">
                </div>
              </div>

              <div class="progress-info">
                <span>
                  <i class="fa-solid fa-paper-plane"></i>
                  {{ selectedCampaignMetrics.progresso.enviados }} enviados
                </span>
                <span v-if="selectedCampaignMetrics.progresso.faltam > 0">
                  <i class="fa-solid fa-clock"></i>
                  {{ selectedCampaignMetrics.progresso.faltam }} restantes
                </span>
                <span v-if="selectedCampaignMetrics.progresso.tempo_estimado">
                  <i class="fa-solid fa-hourglass-half"></i>
                  {{ selectedCampaignMetrics.progresso.tempo_estimado }}
                </span>
              </div>
            </div>

            <!-- 🔥 MÉTRICAS PRINCIPAIS -->
            <div class="metrics-overview">
              <!-- Total Enviado -->
              <div class="metric-box">
                <i class="fa-solid fa-envelope"></i>
                <div>
                  <span class="metric-label">Total Enviado</span>
                  <span class="metric-value large">
                    {{ formatNumber(selectedCampaignMetrics.globalStats?.sent || 0) }}
                  </span>
                </div>
              </div>

              <!-- Aberturas -->
              <div class="metric-box success">
                <i class="fa-solid fa-envelope-open"></i>
                <div>
                  <span class="metric-label">Aberturas</span>
                  <span class="metric-value large">
                    {{ formatNumber(selectedCampaignMetrics.globalStats?.uniqueViews || 0) }}
                    <span class="metric-sublabel">únicas</span>
                  </span>
                  <span class="metric-sublabel-extra">
                    {{ formatNumber(selectedCampaignMetrics.globalStats?.totalViews || 0) }} totais
                  </span>
                </div>
              </div>

              <!-- Cliques -->
              <div class="metric-box info">
                <i class="fa-solid fa-mouse-pointer"></i>
                <div>
                  <span class="metric-label">Cliques</span>
                  <span class="metric-value large">
                    {{ formatNumber(selectedCampaignMetrics.globalStats?.uniqueClicks || 0) }}
                    <span class="metric-sublabel">únicos</span>
                  </span>
                  <span class="metric-sublabel-extra">
                    {{ formatNumber(selectedCampaignMetrics.globalStats?.totalClicks || 0) }} totais
                  </span>
                </div>
              </div>
            </div>

            <!-- 🔥 TAXAS -->
            <div class="metrics-details-grid" v-if="selectedCampaignMetrics.taxas">
              <div class="metric-detail-card">
                <h5>📊 Taxas de Engajamento</h5>

                <div class="metric-row">
                  <span class="metric-label-with-tooltip">
                    Taxa de Entrega:
                    <span class="tooltip-icon" title="Percentual de emails que foram entregues com sucesso">
                      <i class="fa-solid fa-circle-info"></i>
                    </span>
                  </span>
                  <span class="badge badge-success">{{ selectedCampaignMetrics.taxas.entrega }}%</span>
                </div>

                <div class="metric-row">
                  <span class="metric-label-with-tooltip">
                    Taxa de Abertura:
                    <span class="tooltip-icon"
                      title="Percentual de destinatários únicos que abriram o email pelo menos uma vez">
                      <i class="fa-solid fa-circle-info"></i>
                    </span>
                  </span>
                  <span class="badge badge-info">{{ selectedCampaignMetrics.taxas.abertura_unica }}%</span>
                </div>

                <div class="metric-row">
                  <span class="metric-label-with-tooltip">
                    Taxa de Cliques:
                    <span class="tooltip-icon"
                      title="Percentual de destinatários únicos que clicaram em algum link do email">
                      <i class="fa-solid fa-circle-info"></i>
                    </span>
                  </span>
                  <span class="badge badge-primary">{{ selectedCampaignMetrics.taxas.clique_unico }}%</span>
                </div>

                <div class="metric-row">
                  <span class="metric-label-with-tooltip">
                    Taxa de Erros:
                    <span class="tooltip-icon" title="Percentual de emails enviados que não foram entregues">
                      <i class="fa-solid fa-circle-info"></i>
                    </span>
                  </span>
                  <span class="badge badge-danger">{{ selectedCampaignMetrics.taxas.erro }}%</span>
                </div>
              </div>

              <div class="metric-detail-card">
                <h5>📈 Detalhes</h5>
                <div class="metric-row">
                  <span>Entregues:</span>
                  <span>{{ formatNumber(selectedCampaignMetrics.globalStats?.delivered || 0) }}</span>
                </div>
                <div class="metric-row">
                  <span>Aberturas Totais:</span>
                  <span>{{ formatNumber(selectedCampaignMetrics.globalStats?.totalViews || 0) }}</span>
                </div>
                <div class="metric-row">
                  <span>Cliques Totais:</span>
                  <span>{{ formatNumber(selectedCampaignMetrics.globalStats?.totalClicks || 0) }}</span>
                </div>
                <div class="metric-row">
                  <span>Erros:</span>
                  <span class="text-danger">{{ formatNumber(selectedCampaignMetrics.globalStats?.errors || 0) }}</span>
                </div>
              </div>
            </div>

            <!-- 🔥 ERROS
            <div v-if="selectedCampaignMetrics.erros && selectedCampaignMetrics.erros.total > 0" class="errors-inline">
              <span class="errors-label" @click="showErrorsDropdown = !showErrorsDropdown">
                <i class="fa-solid fa-exclamation-triangle"></i>
                {{ selectedCampaignMetrics.erros.total }} {{ selectedCampaignMetrics.erros.total === 1 ? 'erro' :
                  'erros' }}
                <i class="fa-solid fa-chevron-down errors-chevron" :class="{ 'rotated': showErrorsDropdown }"></i>
              </span>

              <transition name="slide-down">
                <ul v-if="showErrorsDropdown" class="errors-list">
                  <li v-for="(erro, idx) in selectedCampaignMetrics.erros.detalhados" :key="idx">
                    <i class="fa-solid fa-envelope"></i>
                    <strong>{{ erro.email }}</strong> - {{ erro.erro }}
                    <small>({{ formatDateTime(erro.enviado_em) }})</small>
                  </li>
                </ul>
              </transition>
            </div> -->

            <!-- 🔥 BOTÃO LINHA DO TEMPO -->
            <div class="timeline-toggle" v-if="!showTimeline">
              <button @click="loadTimeline(selectedCampaignMetrics.id)" class="btn btn-info">
                <i class="fa-solid fa-timeline"></i> Ver Linha do Tempo
              </button>
            </div>

            <!-- 🔥 LINHA DO TEMPO - COM PAGINAÇÃO DE EVENTOS -->
            <div v-if="showTimeline" class="timeline-section">
              <h5>
                <i class="fa-solid fa-timeline"></i> Linha do Tempo
                <button @click="showTimeline = false; expandedTimelineEmails = []; currentEventPages = {}"
                  class="btn btn-sm btn-secondary">
                  <i class="fa-solid fa-xmark"></i> Fechar
                </button>
              </h5>

              <div v-if="loadingTimeline" class="loading-state">
                <div class="spinner"></div>
                <p>Carregando timeline...</p>
              </div>

              <div v-else-if="timelineGroupedByEmail.length > 0" class="timeline-container">

                <!-- Info do total -->
                <div class="timeline-info">
                  <i class="fa-solid fa-info-circle"></i>
                  {{ timelineGroupedByEmail.length }} {{ timelineGroupedByEmail.length === 1 ? 'email' : 'emails' }} com
                  eventos
                </div>

                <!-- Accordion de Emails -->
                <div class="timeline-accordion">
                  <div v-for="(group, index) in paginatedTimelineEmails" :key="group.email"
                    class="timeline-email-group">

                    <!-- Header do Email -->
                    <div class="timeline-email-header" @click="toggleTimelineEmail(group.email)">
                      <div class="timeline-email-avatar">
                        <i class="fa-solid fa-user"></i>
                      </div>
                      <div class="timeline-email-info">
                        <span class="timeline-email-address">{{ group.email }}</span>
                        <span class="timeline-email-meta">
                          {{ group.eventos.length }} {{ group.eventos.length === 1 ? 'evento' : 'eventos' }}
                        </span>
                      </div>
                      <i class="fa-solid fa-chevron-down timeline-chevron"
                        :class="{ 'rotated': expandedTimelineEmails.includes(group.email) }"></i>
                    </div>

                    <!-- Lista de Eventos com Paginação -->
                    <transition name="slide-fade">
                      <div v-if="expandedTimelineEmails.includes(group.email)" class="timeline-events-wrapper">

                        <!-- 🔥 Info de Paginação de Eventos (se tiver mais de 10) -->
                        <div v-if="group.eventos.length > eventsPerEmail" class="events-pagination-info">
                          <i class="fa-solid fa-layer-group"></i>
                          Mostrando
                          {{ ((currentEventPages[group.email] || 1) - 1) * eventsPerEmail + 1 }} -
                          {{ Math.min((currentEventPages[group.email] || 1) * eventsPerEmail, group.eventos.length) }}
                          de {{ group.eventos.length }} eventos
                        </div>

                        <div class="timeline-events-list">

                          <!-- Eventos Paginados -->
                          <div v-for="(evento, idx) in getPaginatedEvents(group.email, group.eventos)" :key="idx"
                            class="timeline-event-item">

                            <!-- Linha Vertical -->
                            <div class="timeline-line"
                              v-if="idx < getPaginatedEvents(group.email, group.eventos).length - 1">
                            </div>

                            <!-- Ícone do Evento -->
                            <div class="timeline-event-dot" :class="'dot-' + evento.cor">
                              <i :class="'fa-solid fa-' + evento.icone"></i>
                            </div>

                            <!-- Card do Evento -->
                            <div class="timeline-event-card" :class="'card-' + evento.cor">
                              <div class="event-card-header">
                                <span class="event-type">{{ evento.tipo }}</span>
                                <span class="event-time">{{ formatDateTime(evento.timestamp) }}</span>
                              </div>

                              <!-- Link -->
                              <div v-if="evento.link" class="event-detail link-detail">
                                <i class="fa-solid fa-link"></i>
                                <a :href="evento.link" target="_blank" :title="evento.link">
                                  {{ truncateUrl(evento.link, 45) }}
                                </a>
                              </div>

                              <!-- Extras -->
                              <div v-if="evento.extras" class="event-detail info-detail">
                                <i class="fa-solid fa-circle-info"></i>
                                {{ evento.extras }}
                              </div>

                              <!-- Erro -->
                              <div v-if="evento.mensagem" class="event-detail error-detail">
                                <i class="fa-solid fa-triangle-exclamation"></i>
                                {{ evento.mensagem }}
                              </div>
                            </div>
                          </div>

                        </div>

                        <!-- 🔥 Paginação de Eventos (se tiver mais de 10) -->
                        <div v-if="getTotalEventPages(group.eventos) > 1" class="events-pagination">
                          <button @click="previousEventPage(group.email)" class="btn btn-xs btn-secondary"
                            :disabled="(currentEventPages[group.email] || 1) === 1">
                            <i class="fa-solid fa-chevron-left"></i>
                          </button>

                          <div class="events-pagination-pages">
                            <button v-for="page in getTotalEventPages(group.eventos)" :key="page"
                              @click="goToEventPage(group.email, page)" class="btn btn-xs"
                              :class="page === (currentEventPages[group.email] || 1) ? 'btn-primary' : 'btn-secondary'">
                              {{ page }}
                            </button>
                          </div>

                          <button @click="nextEventPage(group.email, getTotalEventPages(group.eventos))"
                            class="btn btn-xs btn-secondary"
                            :disabled="(currentEventPages[group.email] || 1) === getTotalEventPages(group.eventos)">
                            <i class="fa-solid fa-chevron-right"></i>
                          </button>
                        </div>

                      </div>
                    </transition>
                  </div>
                </div>

                <!-- Paginação de Emails -->
                <div v-if="timelineTotalPages > 1" class="timeline-pagination">
                  <div class="pagination-info">
                    Página {{ timelineCurrentPage }} de {{ timelineTotalPages }}
                  </div>

                  <div class="pagination-controls">
                    <button @click="timelinePreviousPage" class="btn btn-sm btn-secondary"
                      :disabled="timelineCurrentPage === 1">
                      <i class="fa-solid fa-chevron-left"></i> Anterior
                    </button>

                    <div class="pagination-pages">
                      <button v-if="currentPage > 5" @click="goToPage(1)" class="btn btn-sm btn-secondary">1</button>
                      <span v-if="currentPage > 5">...</span>

                      <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="btn btn-sm"
                        :class="{ 'btn-primary': page === currentPage, 'btn-secondary': page !== currentPage }">
                        {{ page }}
                      </button>

                      <span v-if="currentPage < totalPages - 4">...</span>
                      <button v-if="currentPage < totalPages - 4" @click="goToPage(totalPages)"
                        class="btn btn-sm btn-secondary">
                        {{ totalPages }}
                      </button>
                    </div>

                    <button @click="timelineNextPage" class="btn btn-sm btn-secondary"
                      :disabled="timelineCurrentPage === timelineTotalPages">
                      Próximo <i class="fa-solid fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div v-else class="empty-state">
                <i class="fa-solid fa-inbox"></i>
                <p>Nenhum evento registrado ainda</p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <!-- Botão Ver Links -->
          <button v-if="selectedCampaignMetrics" @click="viewLinksFromMetrics" class="btn btn-success"
            style="cursor: pointer; margin-right: auto;">
            <i class="fa-solid fa-link"></i> Ver Links
          </button>

          <button v-if="selectedCampaignMetrics && selectedCampaignMetrics.globalStats.errors > 0"
            @click="viewErrorsFromMetrics" class="btn btn-danger" style="cursor: pointer; margin-right: auto;">
            <i class="fa-solid fa-triangle-exclamation"></i>
            Ver Erros ({{ selectedCampaignMetrics.globalStats.errors }})
          </button>

          <!-- Botão Atualizar (se estiver enviando) -->
          <button v-if="selectedCampaignMetrics?.status === 'sending'"
            @click="refreshMetrics(selectedCampaignMetrics.id)" class="btn btn-info">
            <i class="fa-solid fa-refresh"></i> Atualizar
          </button>

          <button @click="closeMetricsModal" class="btn btn-secondary" style="cursor: pointer;">
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Estatísticas de Links - ATUALIZADO -->
    <div v-if="showLinksModal" class="modal-overlay">
      <div class="modal-content-2 modal-large">
        <div class="modal-header">
          <h3><i class="fa-solid fa-link"></i> {{ linksModalTitle }}</h3>
          <button @click="closeLinksModalProperly" class="modal-close-btn" style="cursor: pointer;">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loadingLinks" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando estatísticas...</p>
          </div>

          <div v-else-if="linksModalData.length > 0" class="links-list">
            <div class="links-table">
              <table>
                <thead>
                  <tr>
                    <th>URL</th>
                    <th style="width: 120px; text-align: center;">Cliques Totais</th>
                    <th style="width: 120px; text-align: center;">Cliques Únicos</th>
                    <th style="width: 100px; text-align: center;">Taxa</th>
                    <th style="width: 100px; text-align: center;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(link, index) in paginatedLinks" :key="index">
                    <!-- URL -->
                    <td>
                      <a :href="link.url" target="_blank" class="link-url" :title="link.url">
                        {{ truncateUrl(link.url, 60) }}
                      </a>
                    </td>

                    <!-- Cliques Totais -->
                    <td style="text-align: center;">
                      <span class="badge badge-primary">
                        <i class="fa-solid fa-mouse-pointer"></i>
                        {{ link.totalClicks }}
                      </span>
                    </td>

                    <!-- Cliques Únicos -->
                    <td style="text-align: center;">
                      <span class="badge badge-info">
                        <i class="fa-solid fa-user"></i>
                        {{ link.uniqueClicks }}
                      </span>
                    </td>

                    <!-- Taxa -->
                    <td style="text-align: center;">
                      <span class="badge badge-success">
                        {{ link.clickRate }}
                      </span>
                    </td>

                    <!-- Ações -->
                    <td style="text-align: center;">
                      <button v-if="link.uniqueClicks > 0" @click.stop="viewLinkClickers(link)"
                        class="btn btn-sm btn-primary" title="Ver quem clicou">
                        <i class="fa-solid fa-users"></i>
                      </button>
                      <span v-else style="color: #999;">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginação -->
            <div v-if="linksModalData.length > linksPerPage && linksTotalPages > 1" class="pagination-container">
              <div class="pagination-info">
                Mostrando {{ (linksCurrentPage - 1) * linksPerPage + 1 }} -
                {{ Math.min(linksCurrentPage * linksPerPage, linksModalData.length) }}
                de {{ linksModalData.length }} links
              </div>

              <div class="pagination-controls">
                <button @click="linksPreviousPage" class="btn btn-sm btn-secondary" :disabled="linksCurrentPage === 1">
                  <i class="fa-solid fa-chevron-left"></i> Anterior
                </button>

                <div class="pagination-pages">
                  <button v-for="page in linksTotalPages" :key="page" @click="linksGoToPage(page)" class="btn btn-sm"
                    :class="{ 'btn-primary': page === linksCurrentPage, 'btn-secondary': page !== linksCurrentPage }">
                    {{ page }}
                  </button>
                </div>

                <button @click="linksNextPage" class="btn btn-sm btn-secondary"
                  :disabled="linksCurrentPage === linksTotalPages">
                  Próximo <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <!-- Resumo -->
            <div class="links-summary">
              <div class="summary-grid">
                <div class="summary-item">
                  <i class="fa-solid fa-link"></i>
                  <div>
                    <span class="summary-label">Total de Links:</span>
                    <span class="summary-value">{{ linksModalData.length }}</span>
                  </div>
                </div>

                <div class="summary-item">
                  <i class="fa-solid fa-check-circle"></i>
                  <div>
                    <span class="summary-label">Links com Cliques:</span>
                    <span class="summary-value">{{ linksWithClicks }}</span>
                  </div>
                </div>

                <div class="summary-item">
                  <i class="fa-solid fa-mouse-pointer"></i>
                  <div>
                    <span class="summary-label">Total de Cliques:</span>
                    <span class="summary-value">{{ calculateTotalClicks() }}</span>
                  </div>
                </div>

                <div class="summary-item">
                  <i class="fa-solid fa-users"></i>
                  <div>
                    <span class="summary-label">Cliques Únicos:</span>
                    <span class="summary-value">{{ calculateTotalUniqueClicks() }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <p>Nenhum link rastreado nesta campanha.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeLinksModalProperly" class="btn btn-secondary" style="cursor: pointer;">
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Erros Detalhados -->
    <div v-if="showErrorsModal" class="modal-overlay">
      <div class="modal-content-2 modal-large">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ errorsModalTitle }}
          </h3>
          <button @click="closeErrorsModal" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loadingErrors" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando erros...</p>
          </div>

          <div v-else-if="errorsModalData" class="errors-content">

            <!-- 📊 Resumo dos Erros -->
            <div class="errors-summary">
              <div class="summary-card">
                <div class="summary-icon" style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);">
                  <i class="fa-solid fa-paper-plane"></i>
                </div>
                <div class="summary-info">
                  <span class="summary-label">Total Enviado</span>
                  <span class="summary-value">{{ formatNumber(errorsModalData.total_enviados) }}</span>
                </div>
              </div>

              <div class="summary-card">
                <div class="summary-icon" style="background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);">
                  <i class="fa-solid fa-circle-xmark"></i>
                </div>
                <div class="summary-info">
                  <span class="summary-label">Total de Erros</span>
                  <span class="summary-value">{{ formatNumber(errorsModalData.total_erros) }}</span>
                </div>
              </div>

              <div class="summary-card">
                <div class="summary-icon" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
                  <i class="fa-solid fa-percent"></i>
                </div>
                <div class="summary-info">
                  <span class="summary-label">Taxa de Erro</span>
                  <span class="summary-value">{{ errorsModalData.percentual_erro }}%</span>
                </div>
              </div>
            </div>

            <!-- 🔍 Filtros por Tipo de Erro -->
            <div class="error-types-grid">
              <div class="error-type-card" :class="{ 'active': selectedErrorType === 'all' }"
                @click="filterErrorsByType('all')">
                <i class="fa-solid fa-list"></i>
                <span class="error-type-label">Todos</span>
                <span class="error-type-count">{{ errorsModalData.total_erros }}</span>
              </div>

              <div v-for="(count, tipo) in errorsModalData.erros_por_tipo" :key="tipo" class="error-type-card"
                :class="{ 'active': selectedErrorType === tipo }" @click="filterErrorsByType(tipo)">
                <i :class="getErrorIcon(tipo).icon" :style="{ color: getErrorIcon(tipo).color }"></i>
                <span class="error-type-label">{{ getErrorTypeLabel(tipo) }}</span>
                <span class="error-type-count">{{ count }}</span>
              </div>
            </div>

            <!-- 📋 Lista de Erros Detalhados -->
            <div v-if="filteredErrors.length > 0" class="errors-list-section">
              <div class="errors-list-header">
                <h4>
                  <i class="fa-solid fa-list-ul"></i>
                  Erros Detalhados
                  <span v-if="selectedErrorType !== 'all'" class="filter-badge">
                    {{ getErrorTypeLabel(selectedErrorType) }}
                  </span>
                </h4>
              </div>

              <div class="errors-table-wrapper">
                <table class="errors-table">
                  <thead>
                    <tr>
                      <th width="50"></th>
                      <th width="180">Email</th>
                      <th width="120">CPD</th>
                      <th width="120">Tipo</th>
                      <th>Mensagem de Erro</th>
                      <th width="150">Data/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(erro, index) in paginatedErrors" :key="index">
                      <td style="text-align: center;">
                        <i :class="getErrorIcon(erro.tipo).icon"
                          :style="{ color: getErrorIcon(erro.tipo).color, fontSize: '18px' }"></i>
                      </td>
                      <td>
                        <span class="email-address">{{ erro.email }}</span>
                      </td>
                      <td>
                        <span class="cpd-text">{{ erro.cpd || '-' }}</span>
                      </td>
                      <td style="text-align: center;">
                        <span class="error-badge" :style="{
                          background: getErrorIcon(erro.tipo).color + '20',
                          color: getErrorIcon(erro.tipo).color,
                          border: '1px solid ' + getErrorIcon(erro.tipo).color
                        }">
                          {{ getErrorTypeLabel(erro.tipo) }}
                        </span>
                      </td>
                      <td>
                        <span class="error-message">{{ erro.erro }}</span>
                      </td>
                      <td>
                        <span class="error-date">{{ formatDateTime(erro.enviado_em) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Paginação -->
              <div v-if="errorsTotalPages > 1" class="pagination-container">
                <div class="pagination-info">
                  Mostrando {{ errorsShowingFrom }} - {{ errorsShowingTo }} de {{ filteredErrors.length }} erros
                </div>

                <div class="pagination-controls">
                  <button @click="errorsPreviousPage" class="btn btn-sm btn-secondary"
                    :disabled="errorsCurrentPage === 1">
                    <i class="fa-solid fa-chevron-left"></i> Anterior
                  </button>

                  <div class="pagination-pages">
                    <button v-for="page in errorsTotalPages" :key="page" @click="errorsGoToPage(page)"
                      class="btn btn-sm"
                      :class="{ 'btn-primary': page === errorsCurrentPage, 'btn-secondary': page !== errorsCurrentPage }">
                      {{ page }}
                    </button>
                  </div>

                  <button @click="errorsNextPage" class="btn btn-sm btn-secondary"
                    :disabled="errorsCurrentPage === errorsTotalPages">
                    Próximo <i class="fa-solid fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
              <div class="empty-icon" style="color: #10b981;">
                <i class="fa-solid fa-circle-check"></i>
              </div>
              <h3>Nenhum erro encontrado! 🎉</h3>
              <p>Todos os emails foram enviados com sucesso.</p>
            </div>

          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeErrorsModal" class="btn btn-secondary">
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Suspender Campanha -->
    <div v-if="showSuspendModal" class="modal-overlay">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3><i class="fa-solid fa-pause-circle"></i> Suspender Campanha</h3>
          <button @click="showSuspendModal = false" class="btn-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="confirmation-message">
            <i class="fa-solid fa-triangle-exclamation warning-icon"></i>
            <p>Tem certeza que deseja <strong>suspender</strong> a campanha:</p>
            <p class="campaign-name-highlight">{{ campaignToSuspendName }}</p>
            <p class="warning-text">A campanha será pausada e não será enviada até que você a retome.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="showSuspendModal = false" class="btn btn-secondary" :disabled="suspending">
            Cancelar
          </button>
          <button @click="confirmSuspend" class="btn btn-warning" :disabled="suspending">
            <i class="fa-solid fa-pause"></i>
            {{ suspending ? 'Suspendendo...' : 'Sim, Suspender' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Retomar Campanha -->
    <div v-if="showResumeModal" class="modal-overlay">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3><i class="fa-solid fa-play-circle"></i> Retomar Campanha</h3>
          <button @click="showResumeModal = false" class="btn-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="confirmation-message">
            <i class="fa-solid fa-triangle-exclamation warning-icon"></i>
            <p>Tem certeza que deseja <strong>retomar</strong> a campanha:</p>
            <p class="campaign-name-highlight">{{ campaignToResumeName }}</p>
            <p class="warning-text">A campanha voltará para o estado de agendada e será enviada na data programada.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="showResumeModal = false" class="btn btn-secondary" :disabled="resuming">
            Cancelar
          </button>
          <button @click="confirmResume" class="btn btn-success" :disabled="resuming">
            <i class="fa-solid fa-play"></i>
            {{ resuming ? 'Retomando...' : 'Sim, Retomar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Cancelar Campanha -->
    <div v-if="showCancelModal" class="modal-overlay">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3><i class="fa-solid fa-ban"></i> Cancelar Campanha</h3>
          <button @click="showCancelModal = false" class="btn-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="confirmation-message">
            <i class="fa-solid fa-triangle-exclamation warning-icon"></i>
            <p>Tem certeza que deseja <strong>cancelar</strong> a campanha:</p>
            <p class="campaign-name-highlight">{{ campaignToCancelName }}</p>
            <p class="danger-text">⚠️ Esta ação não pode ser desfeita! A campanha será cancelada e não poderá ser
              enviada.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="showCancelModal = false" class="btn btn-secondary" :disabled="canceling">
            Não, Voltar
          </button>
          <button @click="confirmCancel" class="btn btn-danger" :disabled="canceling">
            <i class="fa-solid fa-ban"></i>
            {{ canceling ? 'Cancelando...' : 'Sim, Cancelar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Exclusão -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-triangle-exclamation"></i> Confirmar Exclusão</h3>
          <button @click.stop="showDeleteModal = false" class="modal-close-btn" style="cursor: pointer; z-index: 1000;">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja excluir a campanha:</p>
          <p class="template-name-delete"><strong>{{ campaignToDeleteName }}</strong></p>
          <p class="warning-text">Esta ação não pode ser desfeita.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteModal = false" class="btn btn-secondary">
            Cancelar
          </button>
          <button @click="confirmDelete" class="btn btn-danger" :disabled="deleting">
            {{ deleting ? 'Excluindo...' : 'Sim, Excluir' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Arquivar Campanha -->
    <div v-if="showArchiveModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Arquivar Campanha</h3>
          <button @click="showArchiveModal = false" class="btn-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja arquivar a campanha <strong>{{ campaignToArchiveName }}</strong>?</p>
          <p class="text-muted">A campanha será movida para o arquivo e não aparecerá na listagem de campanhas ativas.
            Você
            poderá desarquivá-la a qualquer momento.</p>
        </div>
        <div class="modal-footer">
          <button @click="showArchiveModal = false" class="btn btn-secondary">Cancelar</button>
          <button @click="confirmArchive" class="btn btn-warning" :disabled="archiving">
            {{ archiving ? 'Arquivando...' : 'Arquivar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Desarquivar Campanha -->
    <div v-if="showUnarchiveModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Desarquivar Campanha</h3>
          <button @click="showUnarchiveModal = false" class="btn-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja desarquivar a campanha <strong>{{ campaignToUnarchiveName }}</strong>?</p>
          <p class="text-muted">A campanha será removida do arquivo e voltará a aparecer na listagem de campanhas.</p>
        </div>
        <div class="modal-footer">
          <button @click="showUnarchiveModal = false" class="btn btn-secondary">Cancelar</button>
          <button @click="confirmUnarchive" class="btn btn-info" :disabled="unarchiving">
            {{ unarchiving ? 'Desarquivando...' : 'Desarquivar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Visualização de Contatos - ATUALIZADO -->
    <div v-if="showContactsModal" class="modal-overlay">
      <div class="modal-content-2 modal-large">
        <div class="modal-header">
          <h3><i class="fa-solid fa-users"></i> {{ contactsModalTitle }}</h3>
          <button @click.stop="closeContactsModalProperly" class="modal-close-btn" style="cursor: pointer;">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loadingContacts" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando contatos...</p>
          </div>

          <div v-else-if="contactsModalData.length > 0" class="contacts-list">
            <div class="contacts-table-wrapper">
              <table class="contacts-table">
                <thead>
                  <tr>
                    <th width="40"></th>
                    <th>Email Principal</th>
                    <th>Nome</th>
                    <th>CPD</th>
                    <th>CPF/CNPJ</th>
                    <th>Razão Social</th>
                    <th>Empresa</th>
                    <th v-if="isViewingClickers">Total Cliques</th>
                    <th v-if="isViewingClickers">Primeiro Clique</th>
                    <th v-if="isViewingClickers">Último Clique</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 🔥 SEM TEMPLATE - DIRETO NO TBODY -->
                  <tr v-for="contact in paginatedContactsModal" :key="'main-' + contact.contato_id"
                    class="contact-row-main">
                    <td>
                      <button v-if="hasSecondaryEmails(contact)" @click="toggleContact(contact.contato_id)"
                        class="btn-toggle">
                        <i class="fa-solid fa-chevron-right"
                          :class="{ 'rotated': isContactExpanded(contact.contato_id) }"></i>
                      </button>
                    </td>

                    <td class="email-principal">
                      <i class="fa-solid fa-envelope"></i>
                      {{ contact.email }}
                      <span v-if="hasSecondaryEmails(contact)" class="badge-cc-count">
                        +{{ contact.emails_cc.length }}
                      </span>
                    </td>

                    <td>{{ contact.nome }} {{ contact.sobrenome }}</td>
                    <td>{{ contact.cpd || '-' }}</td>
                    <td>{{ contact.cpf_cnpj || '-' }}</td>
                    <td>{{ contact.razao_social || '-' }}</td>
                    <td>{{ contact.nome_fantasia || '-' }}</td>

                    <td v-if="isViewingClickers">
                      <span class="badge badge-primary">
                        {{ contact.total_cliques_neste_link || 0 }}
                      </span>
                    </td>
                    <td v-if="isViewingClickers">
                      <span class="text-small">{{ formatDate(contact.primeiro_clique) }}</span>
                    </td>
                    <td v-if="isViewingClickers">
                      <span class="text-small">{{ formatDate(contact.ultimo_clique) }}</span>
                    </td>

                    <td>
                      <span class="badge" :class="contact.ativo ? 'badge-success' : 'badge-danger'">
                        {{ contact.ativo ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 🔥 EMAILS CC - TABELA SEPARADA EMBAIXO -->
            <div v-for="contact in paginatedContactsModal" :key="'cc-group-' + contact.contato_id"
              v-show="isContactExpanded(contact.contato_id)" class="cc-emails-section">
              <div v-if="hasSecondaryEmails(contact)" class="cc-emails-list">
                <div class="cc-header">
                  <i class="fa-solid fa-users"></i>
                  Emails secundários de <strong>{{ contact.email }}</strong>
                </div>
                <div v-for="(emailCC, idx) in contact.emails_cc" :key="'cc-' + contact.contato_id + '-' + idx"
                  class="cc-email-item">
                  <i class="fa-solid fa-arrow-turn-up fa-rotate-90"></i>
                  <span class="badge badge-cc">CC</span>
                  {{ emailCC }}
                </div>
              </div>
            </div>

            <!-- Paginação -->
            <div v-if="contactsModalData.length > contactsPerPage && contactsTotalPages > 1"
              class="pagination-container">
              <div class="pagination-info">
                Mostrando {{ contactsShowingFrom }} - {{ contactsShowingTo }} de {{ contactsModalData.length }} contatos
              </div>

              <div class="pagination-controls">
                <button @click="contactsPreviousPage" class="btn btn-sm btn-secondary"
                  :disabled="contactsCurrentPage === 1">
                  <i class="fa-solid fa-chevron-left"></i> Anterior
                </button>

                <div class="pagination-pages">
                  <button v-for="page in contactsTotalPages" :key="page" @click="contactsGoToPage(page)"
                    class="btn btn-sm"
                    :class="{ 'btn-primary': page === contactsCurrentPage, 'btn-secondary': page !== contactsCurrentPage }">
                    {{ page }}
                  </button>
                </div>

                <button @click="contactsNextPage" class="btn btn-sm btn-secondary"
                  :disabled="contactsCurrentPage === contactsTotalPages">
                  Próximo <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <p class="total-contacts">
              <strong>Total:</strong> {{ contactsModalData.length }} contatos
            </p>
          </div>

          <div v-else class="empty-state">
            <p>Nenhum contato encontrado.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button v-if="isViewingClickers && previousLinksState" @click.stop="goBackToLinksModal" class="btn btn-info"
            style="cursor: pointer; margin-right: auto;">
            <i class="fa-solid fa-arrow-left"></i> Voltar para Links
          </button>

          <button @click.stop="closeContactsModalProperly" class="btn btn-secondary" style="cursor: pointer;">
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Criar Lista -->
    <div v-if="showCreateListModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-list"></i> Criar Nova Lista</h3>
          <button @click="showCreateListModal = false" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nome da Lista *</label>
            <input v-model="newListName" type="text" class="form-control" placeholder="Ex: Clientes VIP"
              @keyup.enter="createList">
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showCreateListModal = false" class="btn btn-secondary" :disabled="creatingList">
            Cancelar
          </button>
          <button @click="createList" class="btn btn-primary" :disabled="creatingList || !newListName.trim()">
            <i class="fa-solid fa-check"></i>
            {{ creatingList ? 'Criando...' : 'Criar Lista' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação: Excluir Contato
    <div v-if="showDeleteContactModal" class="modal-overlay" @click.self="showDeleteContactModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-trash"></i> Excluir Contato</h3>
          <button @click="showDeleteContactModal = false" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <strong>Atenção!</strong> Esta ação é PERMANENTE e não pode ser desfeita.
          </div>

          <p>Você está prestes a excluir permanentemente o contato:</p>
          <p
            style="text-align: center; font-size: 16px; font-weight: bold; color: #dc2626; background: #fee2e2; padding: 10px; border-radius: 6px; margin: 15px 0;">
            {{ contactToDeleteEmail }}
          </p>

          <p>Este contato será removido permanentemente do Brevo e de todas as listas.</p>
          <p><strong>Deseja continuar?</strong></p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteContactModal = false" class="btn btn-secondary" :disabled="deletingContact">
            <i class="fa-solid fa-xmark"></i>
            Cancelar
          </button>
          <button @click="confirmDeleteContact" class="btn btn-danger" :disabled="deletingContact">
            <i class="fa-solid fa-trash"></i>
            {{ deletingContact ? 'Excluindo...' : 'Sim, Excluir' }}
          </button>
        </div>
      </div>
    </div> -->

    <!-- Modal de Confirmação: Excluir Todos
    <div v-if="showDeleteAllContactsModal" class="modal-overlay" @click.self="showDeleteAllContactsModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-trash-can"></i> Excluir Todos os Contatos</h3>
          <button @click="showDeleteAllContactsModal = false" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="alert alert-error">
            <i class="fa-solid fa-circle-info"></i>
            <strong>ATENÇÃO CRÍTICA!</strong> Esta ação é IRREVERSÍVEL!
          </div>

          <p>Você está prestes a excluir <strong style="color: #dc2626;">TODOS os {{ contactsModalData.length }}
              contatos</strong> desta lista:</p>
          <p
            style="text-align: center; font-size: 16px; font-weight: bold; color: #92400e; background: #fef3c7; padding: 10px; border-radius: 6px; margin: 15px 0;">
            {{ currentListName }}
          </p>

          <div
            style="background: #fee2e2; padding: 15px; border-radius: 6px; border-left: 4px solid #dc2626; margin: 15px 0;">
            <p style="margin: 0; font-weight: bold; color: #991b1b;">Esta ação irá:</p>
            <ul style="margin: 10px 0 0 0; color: #991b1b;">
              <li>❌ Excluir PERMANENTEMENTE todos os contatos</li>
              <li>❌ Remover de TODAS as listas do Brevo</li>
              <li>❌ NÃO poderá ser desfeita</li>
            </ul>
          </div>

          <p style="font-weight: bold; color: #dc2626; text-align: center; font-size: 16px;">
            Tem CERTEZA ABSOLUTA que deseja continuar?
          </p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteAllContactsModal = false" class="btn btn-secondary" :disabled="deletingAllContacts">
            <i class="fa-solid fa-xmark"></i>
            Não, Cancelar
          </button>
          <button @click="confirmDeleteAllContacts" class="btn btn-danger" :disabled="deletingAllContacts">
            <i class="fa-solid fa-trash"></i>
            {{ deletingAllContacts ? 'Excluindo...' : 'SIM, EXCLUIR TUDO' }}
          </button>
        </div>
      </div>
    </div> -->

    <!-- Modal: Criar Segmento -->
    <div v-if="showCreateSegmentModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-filter"></i> Criar Novo Segmento</h3>
          <button @click="showCreateSegmentModal = false" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nome do Segmento *</label>
            <input v-model="newSegmentName" type="text" class="form-control" placeholder="Ex: Usuários Ativos"
              @keyup.enter="createSegment">
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showCreateSegmentModal = false" class="btn btn-secondary" :disabled="creatingSegment">
            Cancelar
          </button>
          <button @click="createSegment" class="btn btn-primary" :disabled="creatingSegment || !newSegmentName.trim()">
            <i class="fa-solid fa-check"></i>
            {{ creatingSegment ? 'Criando...' : 'Criar Segmento' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Adicionar Contato -->
    <div v-if="showAddContactModal" class="modal-overlay">
      <div class="modal-content modal-contact">

        <!-- Loading Overlay -->
        <div v-if="addingContact && !showSuccessAnimation" class="loading-overlay">
          <div class="loading-content">
            <div class="spinner"></div>
            <p class="loading-text">Adicionando contato...</p>
            <p class="loading-subtext">Aguarde um momento</p>
          </div>
        </div>

        <!-- Success Animation Overlay -->
        <div v-if="showSuccessAnimation" class="success-overlay">
          <div class="success-content">
            <div class="success-checkmark">
              <div class="checkmark-circle-wrapper">
                <svg class="checkmark-circle-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <circle class="checkmark-circle" cx="50" cy="50" r="45" fill="none" stroke="#10b981"
                    stroke-width="4" />
                </svg>
              </div>
              <div class="checkmark-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <path class="checkmark-check" fill="none" stroke="#10b981" stroke-width="6" stroke-linecap="round"
                    d="M 25,52 L 42,68 L 75,32" />
                </svg>
              </div>
            </div>
            <h3 class="success-title">Contato Adicionado!</h3>
            <p class="success-message">{{ contactAddedName }} foi adicionado com sucesso à lista.</p>
          </div>
        </div>

        <div class="modal-header">
          <h3><i class="fa-solid fa-user-plus"></i> Adicionar Contato à Lista</h3>
          <button @click="closeAddContactModal" class="modal-close-btn" :disabled="addingContact">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Mensagens de Feedback -->
          <div v-if="modalErrorMessage" class="alert alert-error">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <span>{{ modalErrorMessage }}</span>
            <button @click="modalErrorMessage = ''" class="alert-close">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <!-- Informações Básicas -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-user"></i>
              <span>Informações Básicas</span>
            </div>

            <div class="form-group">
              <label>Email do Contato <span class="required">*</span></label>
              <input v-model="newContactEmail" type="email" class="form-control" placeholder="email@exemplo.com"
                @keyup.enter="addContactToList" :disabled="addingContact">
              <small class="form-hint">
                <i class="fa-solid fa-circle-info"></i> Digite um email válido
              </small>
            </div>

            <div class="form-grid">
              <div class="form-group">
                <label>Nome <span class="required">*</span></label>
                <input v-model="newContactFirstName" type="text" class="form-control" placeholder="João"
                  :disabled="addingContact">
              </div>

              <div class="form-group">
                <label>Sobrenome</label>
                <input v-model="newContactLastName" type="text" class="form-control" placeholder="Silva"
                  :disabled="addingContact">
              </div>
            </div>
          </div>

          <!-- Documentos -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-id-card"></i>
              <span>Documentos</span>
            </div>

            <div class="form-grid">
              <div class="form-group">
                <label>CPD <span class="required">*</span></label>
                <input v-model="newContactCpd" type="text" class="form-control" placeholder="00000" @input="formatCPD"
                  maxlength="7" :disabled="addingContact">
                <small class="form-hint">
                  <i class="fa-solid fa-circle-info"></i> Apenas números (7 dígitos)
                </small>
              </div>

              <div class="form-group">
                <label>CPF/CNPJ <span class="required">*</span></label>
                <input v-model="newContactCpf" type="text" class="form-control" placeholder="000.000.000-00"
                  @input="formatCPFCNPJ" maxlength="18" :disabled="addingContact">
                <small class="form-hint">
                  <i class="fa-solid fa-circle-info"></i> CPF (11 dígitos) ou CNPJ (14 dígitos)
                </small>
              </div>
            </div>
          </div>

          <!-- Pagamento -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-link"></i>
              <span>Pagamento</span>
            </div>

            <div class="form-group">
              <label>Link de Pagamento <span class="required">*</span></label>
              <input v-model="newContactLink" type="url" class="form-control" placeholder="https://pagamento.com/link"
                :disabled="addingContact">
              <small class="form-hint">
                <i class="fa-solid fa-circle-info"></i> Informe o link completo (https://...)
              </small>
            </div>
          </div>

          <!-- Informações da Empresa (Dropdown) -->
          <div class="collapsible-wrapper">
            <div class="collapsible-header" @click="!addingContact && toggleEmpresaFields()"
              :class="{ 'disabled': addingContact }">
              <div class="collapsible-title">
                <i class="fa-solid fa-building"></i>
                <span>Informações da Empresa</span>
                <span class="optional-badge">Opcional</span>
              </div>
              <i class="fa-solid fa-chevron-down collapse-arrow" :class="{ 'rotate': showEmpresaFields }"></i>
            </div>

            <transition name="slide-fade">
              <div v-if="showEmpresaFields" class="collapsible-body">
                <div class="form-group">
                  <label>Razão Social</label>
                  <input v-model="newContactRazao" type="text" class="form-control"
                    placeholder="Nome Completo da Empresa LTDA" :disabled="addingContact">
                  <small class="form-hint">
                    <i class="fa-solid fa-circle-info"></i> Razão social completa da empresa
                  </small>
                </div>

                <div class="form-group">
                  <label>Nome Fantasia</label>
                  <input v-model="newContactEmpresa" type="text" class="form-control" placeholder="Nome Fantasia"
                    :disabled="addingContact">
                  <small class="form-hint">
                    <i class="fa-solid fa-circle-info"></i> Nome comercial da empresa
                  </small>
                </div>
              </div>
            </transition>
          </div>

          <!-- Informações Fiscais (Dropdown) -->
          <div class="collapsible-wrapper">
            <div class="collapsible-header" @click="!addingContact && toggleFiscalFields()"
              :class="{ 'disabled': addingContact }">
              <div class="collapsible-title">
                <i class="fa-solid fa-file-invoice"></i>
                <span>Informações Fiscais</span>
                <span class="optional-badge">Opcional</span>
              </div>
              <i class="fa-solid fa-chevron-down collapse-arrow" :class="{ 'rotate': showFiscalFields }"></i>
            </div>

            <transition name="slide-fade">
              <div v-if="showFiscalFields" class="collapsible-body">
                <div class="form-group">
                  <label>Nota Fiscal</label>
                  <input v-model="newContactNotaFiscal" type="text" class="form-control" placeholder="NF-e 000000"
                    :disabled="addingContact">
                  <small class="form-hint">
                    <i class="fa-solid fa-circle-info"></i> Número da nota fiscal
                  </small>
                </div>

                <div class="form-group">
                  <label>Título</label>
                  <input v-model="newContactTitulo" type="text" class="form-control" placeholder="Descrição do título"
                    :disabled="addingContact">
                </div>

                <div class="form-grid">
                  <div class="form-group">
                    <label>
                      <i class="fa-regular fa-calendar"></i> Data de Emissão
                    </label>
                    <input v-model="newContactEmissao" type="date" class="form-control" :disabled="addingContact">
                  </div>

                  <div class="form-group">
                    <label>
                      <i class="fa-regular fa-calendar"></i> Data de Vencimento
                    </label>
                    <input v-model="newContactVencimento" type="date" class="form-control" :disabled="addingContact">
                  </div>
                </div>

                <div class="form-group">
                  <label>
                    <i class="fa-solid fa-brazilian-real-sign"></i> Valor Original
                  </label>
                  <input v-model="newContactValorOriginal" type="text" class="form-control" placeholder="R$ 0,00"
                    @input="onCurrencyInput" :disabled="addingContact">
                  <small class="form-hint">
                    <i class="fa-solid fa-circle-info"></i> Digite apenas números (ex: 150000 = R$ 1.500,00)
                  </small>
                </div>
              </div>
            </transition>
          </div>

          <!-- Lista de Destino -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-list"></i>
              <span>Lista de Destino</span>
            </div>

            <div class="form-group">
              <label>Selecionar Lista <span class="required">*</span></label>
              <select v-model="selectedListForContact" class="form-control" :disabled="addingContact">
                <option :value="null">Selecione uma lista</option>
                <option v-for="list in contactLists" :key="list.id" :value="list.id">
                  {{ list.name }} ({{ list.uniqueSubscribers || 0 }} contatos)
                </option>
              </select>
              <small class="form-hint">
                <i class="fa-solid fa-circle-info"></i> Escolha em qual lista adicionar o contato
              </small>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeAddContactModal" class="btn btn-secondary" :disabled="addingContact">
            <i class="fa-solid fa-xmark"></i> Cancelar
          </button>
          <button @click="addContactToList" class="btn btn-primary" :disabled="addingContact">
            <i class="fa-solid" :class="addingContact ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            {{ addingContact ? 'Adicionando...' : 'Adicionar Contato' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Adicionar Contatos em Massa -->
    <div v-if="showBulkAddContactModal" class="modal-overlay">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3><i class="fa-solid fa-users"></i> Adicionar Contatos em Massa</h3>
          <button @click="closeBulkAddContactModal" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- ✅ MENSAGEM DE ERRO DENTRO DO MODAL -->
          <div v-if="modalErrorMessage" class="alert alert-error" style="margin-bottom: 15px;">
            <span class="icon"><i class="fa-solid fa-triangle-exclamation"></i></span>
            {{ modalErrorMessage }}
            <button @click="modalErrorMessage = ''" class="alert-close">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <!-- ✅ MENSAGEM DE SUCESSO DENTRO DO MODAL -->
          <div v-if="modalSuccessMessage" class="alert alert-success" style="margin-bottom: 15px;">
            <span class="icon"><i class="fa-solid fa-circle-check"></i></span>
            {{ modalSuccessMessage }}
          </div>

          <!-- Selecionar Lista -->
          <div class="form-group">
            <label>Selecionar Lista *</label>
            <select v-model="selectedListForBulkContacts" class="form-control">
              <option :value="null">Selecione uma lista</option>
              <option v-for="list in contactLists" :key="list.id" :value="list.id">
                {{ list.name }} ({{ list.uniqueSubscribers || 0 }} contatos)
              </option>
            </select>
          </div>

          <!-- Textarea de Contatos -->
          <div class="form-group">
            <label>Contatos *</label>
            <textarea v-model="bulkContactEmails" class="form-control" rows="10"
              placeholder='email@exemplo.com, João, Silva, 00000, 000.000.000-00, "https://pagamento.com/link", Razão Social, Nome Fantasia, NF-001, Título, 01/12/2025, 08/12/2025, R$ 1.500,00
email2@exemplo.com, Maria, Santos, 00001, 111.111.111-11, "https://pagamento2.com", Outra Razão, Fantasia 2, NF-002, Título 2, 15/12/2025, 22/12/2025, R$ 2.300,00'
              style="font-family: monospace;">
        </textarea>

            <div
              style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 12px; margin-top: 10px; border-radius: 4px;">
              <strong style="color: #92400e;">
                <i class="fa-solid fa-info-circle"></i> Formato Obrigatório:
              </strong>
              <ul style="margin: 8px 0 0 20px; color: #78350f;">
                <li>Um contato por linha</li>
                <li>Separar campos por <strong>vírgula</strong> ou <strong>ponto-e-vírgula</strong></li>
                <li style="color: #ef4444; font-weight: bold;">
                  ⚠️ Campos OBRIGATÓRIOS: EMAIL, NOME, CPD, CPF/CNPJ e LINK_PAGAMENTO
                </li>
                <li>Campos OPCIONAIS: Sobrenome, Razão Social, Empresa, Nota Fiscal, Título, Emissão, Vencimento, Valor
                  Original</li>
                <li><strong>IMPORTANTE: Coloque links entre aspas: "https://..."</strong></li>
                <li><strong>Datas: Use DD/MM/YYYY (ex: 31/12/2025)</strong></li>
                <li style="color: #059669; font-weight: bold;">
                  📊 Limite: Máximo de 1.000 contatos por importação
                </li>
              </ul>
              <code
                style="display: block; background: white; padding: 8px; margin-top: 8px; border-radius: 4px; font-size: 11px; overflow-x: auto;">
            <strong style="color: #ef4444;">email*</strong>,
            <strong style="color: #ef4444;">nome*</strong>,
            sobrenome,
            <strong style="color: #ef4444;">cpd*</strong>,
            <strong style="color: #ef4444;">cpf_cnpj*</strong>,
            <strong style="color: #ef4444;">link_pagamento*</strong>,
            razao_social,
            empresa,
            nota_fiscal,
            titulo,
            emissao,
            vencimento,
            valor_original
          </code>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeBulkAddContactModal" class="btn btn-secondary" :disabled="addingBulkContacts">
            Cancelar
          </button>
          <button @click="addBulkContactsToList" class="btn btn-primary" :disabled="addingBulkContacts">
            <i class="fa-solid fa-check"></i>
            {{ addingBulkContacts ? 'Adicionando...' : 'Adicionar Contatos' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Preview do Template -->
    <div v-if="showTemplatePreview" class="modal-overlay">
      <div class="modal-box-preview preview-modal">
        <div class="preview-header">
          <h2>
            <i class="fa-solid fa-eye"></i>
            Preview: {{ selectedTemplatePreview?.name }}
          </h2>
          <button @click="closeTemplatePreview" class="btn-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Toggle Desktop/Mobile -->
        <div class="preview-toggle-wrapper">
          <div class="toggle-buttons">
            <button @click="previewMode = 'desktop'" :class="['toggle-btn', { active: previewMode === 'desktop' }]"
              title="Visualização Desktop">
              <i class="fa-solid fa-desktop"></i>
              <span>Desktop</span>
            </button>
            <button @click="previewMode = 'mobile'" :class="['toggle-btn', { active: previewMode === 'mobile' }]"
              title="Visualização Mobile">
              <i class="fa-solid fa-mobile-screen"></i>
              <span>Mobile</span>
            </button>
          </div>
          <div class="width-indicator">
            {{ previewMode === 'desktop' ? '100%' : '400px' }}
          </div>
        </div>

        <!-- Preview Content -->
        <div class="preview-content-wrapper">
          <div v-if="selectedTemplatePreview?.htmlContent" :class="['iframe-container', previewMode]">
            <iframe ref="templatePreviewIframe"
              :srcdoc="selectedTemplatePreview.processedHtml || selectedTemplatePreview.htmlContent"
              class="preview-iframe" frameborder="0" scrolling="yes" sandbox="allow-same-origin"
              @load="adjustIframeHeight">
            </iframe>
          </div>
          <div v-else class="empty-preview">
            <i class="fa-solid fa-file-lines"></i>
            <p>Preview não disponível para este template</p>
          </div>
        </div>

        <!-- Footer com Ações -->
        <div class="preview-footer">
          <button @click="closeTemplatePreview" class="btn btn-secondary">
            <i class="fa-solid fa-xmark"></i>
            Fechar
          </button>
          <button v-if="campaignForm.templateId !== selectedTemplatePreview?.id" @click="selectTemplateFromPreview"
            class="btn btn-primary">
            <i class="fa-solid fa-check"></i>
            Selecionar Este Template
          </button>
          <button v-else class="btn btn-success" disabled>
            <i class="fa-solid fa-check-circle"></i>
            Template Selecionado
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Importação JSON -->
    <div v-if="showImportModal" class="modal-overlay" @click.self="closeImportModal">
      <div class="modal-content-2 modal-large">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-file-import"></i>
            Confirmar Importação de Processos
          </h3>
          <button @click="closeImportModal" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="importModalData" class="import-info">
            <!-- Informações do Arquivo -->
            <div class="info-card">
              <h4><i class="fa-solid fa-file"></i> Informações do Arquivo</h4>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">Arquivo:</span>
                  <span class="value">{{ importModalData.fileInfo.name }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Data:</span>
                  <span class="value">{{ importModalData.fileInfo.date }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Template:</span>
                  <span class="value">{{ importModalData.fileInfo.template_email || 'N/A' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Total de Clientes:</span>
                  <span class="value badge badge-info">
                    {{ importModalData.fileInfo.total_processos }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="label">Total de Contatos (CPDs):</span>
                  <span class="value badge badge-success">
                    {{ importModalData.fileInfo.total_processos }}
                  </span>
                </div>
                <!-- 🆕 NOVO ITEM -->
                <div class="info-item">
                  <span class="label">Total de Emails:</span>
                  <span class="value badge badge-primary">
                    {{ importModalData.fileInfo.total_emails }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="label">Lista de Destino:</span>
                  <span class="value">{{ importModalData.listName }}</span>
                </div>
              </div>
            </div>

            <!-- Estratégia de Importação -->
            <div class="strategy-card">
              <h4>
                <i class="fa-solid fa-info-circle"></i>
                Como funciona a importação
              </h4>
              <div class="strategy-info">
                <div class="strategy-item">
                  <i class="fa-solid fa-check-circle" style="color: #10b981;"></i>
                  <div>
                    <strong>1 Contato por Email</strong>
                    <p>Cada email do processo receberá a campanha individualmente</p>
                  </div>
                </div>
                <div class="strategy-item">
                  <i class="fa-solid fa-check-circle" style="color: #10b981;"></i>
                  <div>
                    <strong>Todos os Títulos Incluídos</strong>
                    <p>Cada contato terá acesso a todos os títulos do processo</p>
                  </div>
                </div>
                <div class="strategy-item">
                  <i class="fa-solid fa-shield-halved" style="color: #6366f1;"></i>
                  <div>
                    <strong>CPF Mascarado (LGPD)</strong>
                    <p>CPFs de pessoas físicas serão protegidos automaticamente (formato: 123.***.01)</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preview dos Contatos -->
            <div class="preview-card">
              <h4>
                <i class="fa-solid fa-eye"></i>
                Preview dos Devedores
              </h4>

              <div class="devedores-accordion">
                <!-- ✅ Agrupar contatos por devedor -->
                <div v-for="(devedor, devedorIndex) in getDevedoresAgrupados()" :key="devedorIndex"
                  class="devedor-card">

                  <!-- Cabeçalho do Devedor (clicável) -->
                  <div class="devedor-header" @click="toggleDevedor(devedorIndex)"
                    :class="{ 'active': devedorExpandido === devedorIndex }">
                    <div class="devedor-info">
                      <span class="devedor-numero">{{ devedorIndex + 1 }}</span>
                      <div class="devedor-title">
                        <strong>{{ devedor.razaoSocial }}</strong>
                        <span class="devedor-cpd" :title="'CPD - Código do Cliente'">
                          <i class="fa-solid fa-hashtag"></i> {{ devedor.cpd }}
                        </span>
                      </div>
                    </div>

                    <div class="devedor-stats">
                      <span class="stat-badge" :title="'Quantidade de emails deste devedor'">
                        <i class="fa-solid fa-envelope"></i>
                        {{ devedor.emails.length }} email(s)
                      </span>
                      <span class="stat-badge" :title="'Quantidade de títulos em aberto'">
                        <i class="fa-solid fa-file-invoice"></i>
                        {{ devedor.titulos }} título(s)
                      </span>
                      <span class="stat-badge stat-valor" :title="'Valor total dos títulos'">
                        <i class="fa-solid fa-brazilian-real-sign"></i>
                        {{ devedor.valorTotal }}
                      </span>
                    </div>

                    <i class="fa-solid fa-chevron-down toggle-icon"
                      :class="{ 'rotated': devedorExpandido === devedorIndex }"></i>
                  </div>

                  <!-- Conteúdo Expandível -->
                  <transition name="accordion">
                    <div v-if="devedorExpandido === devedorIndex" class="devedor-content">

                      <!-- Informações Gerais do Devedor -->
                      <div class="devedor-detalhes">
                        <div class="detalhe-item" v-if="devedor.empresa">
                          <span class="detalhe-label">
                            <i class="fa-solid fa-building"></i> Empresa:
                          </span>
                          <span class="detalhe-valor">{{ devedor.empresa }}</span>
                        </div>

                        <div class="detalhe-item">
                          <span class="detalhe-label">
                            <i
                              :class="devedor.categoria === 'F' ? 'fa-solid fa-shield-halved' : 'fa-solid fa-id-card'"></i>
                            {{ devedor.categoria === 'F' ? 'CPF:' : 'CNPJ:' }}
                          </span>
                          <span class="detalhe-valor">
                            {{ devedor.cpfCnpj }}
                            <span v-if="devedor.categoria === 'F'" class="lgpd-tag-small">LGPD</span>
                          </span>
                        </div>

                        <div class="detalhe-item">
                          <span class="detalhe-label">
                            <i class="fa-solid fa-link"></i> Link de Pagamento:
                          </span>
                          <a :href="devedor.linkPagamento" target="_blank" class="detalhe-link" @click.stop>
                            Ver Link <i class="fa-solid fa-external-link-alt"></i>
                          </a>
                        </div>
                      </div>

                      <!-- Lista de Emails/Contatos -->
                      <div class="emails-list">
                        <h5>
                          <i class="fa-solid fa-users"></i>
                          Contatos que receberão a campanha ({{ devedor.emails.length }})
                        </h5>

                        <div class="email-items">
                          <div v-for="(email, emailIndex) in devedor.emails" :key="emailIndex" class="email-item">
                            <div class="email-icon">
                              <i class="fa-solid fa-envelope"></i>
                            </div>
                            <div class="email-info">
                              <span class="email-address">{{ email }}</span>
                              <span class="email-badge" :class="emailIndex === 0 ? 'badge-primary' : 'badge-secondary'">
                                {{ emailIndex === 0 ? 'Principal' : 'Secundário' }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Lista de Títulos -->
                      <div class="titulos-section">
                        <h5>
                          <i class="fa-solid fa-file-invoice-dollar"></i>
                          Títulos em aberto ({{ devedor.titulos }})
                        </h5>

                        <div class="titulos-grid">
                          <div v-for="(titulo, tituloIndex) in devedor.titulosDetalhes" :key="tituloIndex"
                            class="titulo-card">
                            <div class="titulo-card-header">
                              <span class="titulo-card-badge">Título {{ tituloIndex + 1 }}</span>
                              <span class="titulo-card-numero" :title="'Número do título'">
                                <i class="fa-solid fa-hashtag"></i>
                                {{ titulo.numero }}
                              </span>
                            </div>

                            <div class="titulo-card-body">
                              <div class="titulo-card-item">
                                <span class="titulo-card-label">
                                  <i class="fa-solid fa-calendar"></i> Vencimento:
                                </span>
                                <span class="titulo-card-value">{{ titulo.vencimento }}</span>
                              </div>

                              <div class="titulo-card-item">
                                <span class="titulo-card-label">
                                  <i class="fa-solid fa-brazilian-real-sign"></i> Valor:
                                </span>
                                <span class="titulo-card-value valor-destaque">{{ titulo.valor }}</span>
                              </div>

                              <div class="titulo-card-item">
                                <span class="titulo-card-label">
                                  <i class="fa-solid fa-clock"></i> Atraso:
                                </span>
                                <span class="titulo-card-value"
                                  :class="{ 'atraso-alert': parseInt(titulo.diasAtraso) > 30 }">
                                  {{ titulo.diasAtraso }} dias
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Total dos Títulos -->
                        <div class="titulos-total-box">
                          <i class="fa-solid fa-calculator"></i>
                          <strong>Valor Total dos Títulos:</strong>
                          <span class="total-valor">{{ devedor.valorTotal }}</span>
                        </div>
                      </div>

                    </div>
                  </transition>
                </div>
              </div>

              <!-- Resumo Final -->
              <div class="preview-summary">
                <i class="fa-solid fa-info-circle"></i>
                <strong>Resumo:</strong>
                {{ importModalData.contacts.length }} contatos serão criados para {{ getDevedoresAgrupados().length }}
                devedor(es)
              </div>
            </div>

            <!-- Alerta Final -->
            <div class="alert alert-warning">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <div>
                <strong>Importante:</strong>
                <ul style="margin: 10px 0 0 20px;">
                  <li>Serão criados <strong>{{ importModalData.contacts.length }} contatos</strong> na lista <strong>{{
                    importModalData.listName }}</strong></li>
                  <li>Cada email receberá a campanha individualmente com todos os títulos</li>
                  <li>Contatos duplicados serão atualizados automaticamente</li>
                  <li>Esta ação pode levar alguns minutos</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="backToFileSelection" class="btn btn-secondary" :disabled="confirmingImport">
            <i class="fa-solid fa-arrow-left"></i> Voltar
          </button>
          <button @click="closeImportModal" class="btn btn-secondary" :disabled="confirmingImport">
            <i class="fa-solid fa-xmark"></i> Cancelar
          </button>
          <button @click="confirmImport" class="btn btn-success" :disabled="confirmingImport">
            <i class="fa-solid" :class="confirmingImport ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            {{ confirmingImport ? 'Importando...' : `Importar ${importModalData ? importModalData.contacts.length : 0}
            Contatos` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Editar Lista -->
    <div v-if="showEditListModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-pen"></i> Editar Lista</h3>
          <button @click="showEditListModal = false" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Novo Nome *</label>
            <input v-model="editListName" type="text" class="form-control" placeholder="Nome da lista"
              @keyup.enter="confirmEditList">
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showEditListModal = false" class="btn btn-secondary" :disabled="editingList">Cancelar</button>
          <button @click="confirmEditList" class="btn btn-warning" :disabled="editingList || !editListName.trim()">
            <i class="fa-solid fa-check"></i> {{ editingList ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Excluir Lista -->
    <div v-if="showDeleteListModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="fa-solid fa-trash"></i> Excluir Lista</h3>
          <button @click="showDeleteListModal = false" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja excluir a lista <strong>{{ listToDeleteName }}</strong>?</p>
          <p class="warning-text">⚠️ Todos os contatos da lista serão removidos. Esta ação não pode ser desfeita.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteListModal = false" class="btn btn-secondary"
            :disabled="deletingList">Cancelar</button>
          <button @click="confirmDeleteList" class="btn btn-danger" :disabled="deletingList">
            <i class="fa-solid fa-trash"></i> {{ deletingList ? 'Excluindo...' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 🆕 Modal de Seleção de Arquivo -->
    <div v-if="showFileSelectionModal" class="modal-overlay">
      <div class="modal-content-2 modal-large">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-file-import"></i>
            Selecionar Arquivo para Importação
          </h3>
          <button @click="closeFileSelection" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Informações da Lista -->
          <div class="import-info-card">
            <div class="info-icon">
              <i class="fa-solid fa-list"></i>
            </div>
            <div class="info-content">
              <span class="info-label">Importar para a lista:</span>
              <span class="info-value">{{ currentListNameForImport }}</span>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loadingFiles" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando arquivos disponíveis...</p>
          </div>

          <!-- Lista de Arquivos -->
          <div v-else-if="availableFiles.length > 0" class="files-list">
            <div class="files-header">
              <h4>
                <i class="fa-solid fa-folder-open"></i>
                Arquivos Disponíveis
              </h4>
              <span class="files-count">{{ availableFiles.length }} arquivo(s)</span>
            </div>

            <div class="files-grid">
              <div v-for="file in availableFiles" :key="file.name" class="file-card"
                :class="{ 'selected': selectedFile && selectedFile.name === file.name }" @click="selectFile(file)">
                <!-- Ícone do Arquivo -->
                <div class="file-icon">
                  <i class="fa-solid fa-file-lines"></i>
                </div>

                <!-- Informações do Arquivo -->
                <div class="file-info">
                  <div class="file-name" :title="file.name">
                    {{ file.name }}
                  </div>
                  <div class="file-meta">
                    <span class="file-size">
                      <i class="fa-solid fa-weight-scale"></i>
                      {{ file.size }}
                    </span>
                    <span class="file-date">
                      <i class="fa-solid fa-calendar"></i>
                      {{ file.modified }}
                    </span>
                  </div>
                </div>

                <!-- Indicador de Seleção -->
                <div v-if="selectedFile && selectedFile.name === file.name" class="selection-indicator">
                  <i class="fa-solid fa-check-circle"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <i class="fa-solid fa-inbox"></i>
            </div>
            <h3>Nenhum arquivo disponível</h3>
            <p>Não há arquivos .txt disponíveis para importação no momento.</p>
            <p class="empty-note">
              <i class="fa-solid fa-info-circle"></i>
              Arquivos já importados possuem o sufixo "_importado"
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeFileSelection" class="btn btn-secondary">
            <i class="fa-solid fa-xmark"></i>
            Cancelar
          </button>
          <button @click="confirmFileSelection" class="btn btn-success" :disabled="!selectedFile || loadingImport">
            <i class="fa-solid" :class="loadingImport ? 'fa-spinner fa-spin' : 'fa-arrow-right'"></i>
            {{ loadingImport ? 'Carregando...' : 'Continuar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Progresso de Validação -->
    <div v-if="showValidationProgressModal" class="modal-overlay">
      <div class="modal-content-2 modal-medium">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-spinner fa-spin"></i>
            Validando Emails
          </h3>
        </div>

        <div class="modal-body">
          <!-- Etapas -->
          <div class="validation-stages">
            <div class="stage-item" :class="{ active: validationProgress.stage === 'collecting' }">
              <div class="stage-icon">
                <i class="fa-solid fa-search"></i>
              </div>
              <span>Coletando Emails</span>
            </div>

            <div class="stage-line" :class="{ active: validationProgress.stage !== 'collecting' }"></div>

            <div class="stage-item" :class="{ active: validationProgress.stage === 'validating' }">
              <div class="stage-icon">
                <i class="fa-solid fa-shield-halved"></i>
              </div>
              <span>Validando</span>
            </div>

            <div class="stage-line" :class="{ active: validationProgress.stage === 'completed' }"></div>

            <div class="stage-item" :class="{ active: validationProgress.stage === 'completed' }">
              <div class="stage-icon">
                <i class="fa-solid fa-check-circle"></i>
              </div>
              <span>Concluído</span>
            </div>
          </div>

          <!-- Barra de Progresso -->
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: validationProgress.percentage + '%' }"></div>
          </div>

          <div class="progress-info">
            <span>{{ validationProgress.percentage }}%</span>
            <span class="progress-count">
              {{ validationProgress.current }} / {{ validationProgress.total }} emails
            </span>
          </div>

          <!-- 🔥 TEMPO ESTIMADO E DECORRIDO -->
          <div v-if="validationProgress.tempo_estimado > 0 || validationProgress.tempo_decorrido > 0"
            class="validation-time-info">
            <div v-if="validationProgress.tempo_estimado > 0" class="time-item">
              <i class="fa-solid fa-hourglass-half"></i>
              <span class="time-label">Tempo estimado:</span>
              <span class="time-value">{{ formatTempo(validationProgress.tempo_estimado * 60) }}</span>
            </div>

            <div v-if="validationProgress.tempo_decorrido > 0" class="time-item">
              <i class="fa-solid fa-clock"></i>
              <span class="time-label">Tempo decorrido:</span>
              <span class="time-value">{{ formatTempo(validationProgress.tempo_decorrido) }}</span>
            </div>
          </div>

          <!-- Estatísticas -->
          <div v-if="validationProgress.stage === 'validating' || validationProgress.stage === 'completed'"
            class="validation-stats">
            <div class="stat-card">
              <i class="fa-solid fa-check-circle" style="color: #10b981;"></i>
              <div>
                <span class="stat-label">Existem</span>
                <span class="stat-value">{{ validationProgress.stats.existem }}</span>
              </div>
            </div>

            <div class="stat-card">
              <i class="fa-solid fa-times-circle" style="color: #ef4444;"></i>
              <div>
                <span class="stat-label">Não Existem</span>
                <span class="stat-value">{{ validationProgress.stats.nao_existem }}</span>
              </div>
            </div>

            <div class="stat-card">
              <i class="fa-solid fa-question-circle" style="color: #f59e0b;"></i>
              <div>
                <span class="stat-label">Inconclusivos</span>
                <span class="stat-value">{{ validationProgress.stats.inconclusivos }}</span>
              </div>
            </div>
          </div>

          <div class="validation-message">
            <i class="fa-solid fa-info-circle"></i>
            <p v-if="validationProgress.stage === 'collecting'">
              Coletando emails únicos do arquivo...
            </p>
            <p v-else-if="validationProgress.stage === 'validating'">
              Validando {{ validationProgress.total }} emails via API...
            </p>
            <p v-else>
              Validação concluída! Salvando contatos...
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmailCampaigns',

  data() {
    return {
      showEditListModal: false,
      editListName: '',
      editListId: null,
      editingList: false,
      showDeleteListModal: false,
      listToDelete: null,
      listToDeleteName: '',
      deletingList: false,
      showValidationProgressModal: false,
      validationProgress: {
        stage: '',
        current: 0,
        total: 0,
        percentage: 0,
        tempo_estimado: 0,
        tempo_decorrido: 0,
        stats: {
          existem: 0,
          nao_existem: 0,
          inconclusivos: 0
        }
      },
      emailQuota: null,
      showFileSelectionModal: false,
      availableFiles: [],
      loadingFiles: false,
      selectedFile: null,
      currentListForImport: null,
      currentListNameForImport: '',
      showErrorsModal: false,
      errorsModalTitle: '',
      errorsModalData: {
        total_enviados: 0,
        total_erros: 0,
        percentual_erro: 0,
        erros_por_tipo: {},
        erros_detalhados: []
      },
      loadingErrors: false,
      errorsCurrentPage: 1,
      errorsPerPage: 20,
      selectedErrorType: 'all',
      expandedContacts: [],
      showErrorsDropdown: false,
      showAllErrors: false,
      metricsPolling: null,
      metricsRefreshInterval: 5000,
      showTimeline: false,
      timelineData: [],
      loadingTimeline: false,
      expandedTimelineEmails: [], // Emails expandidos
      timelineEmailsPerPage: 10, // Mostrar 10 emails por vez
      timelineCurrentPage: 1, // Página atual
      eventsPerEmail: 10, // Mostrar 10 eventos por vez
      currentEventPages: {},
      devedorExpandido: 0,
      showImportModal: false,
      importModalData: null,
      loadingImport: false,
      confirmingImport: false,
      showSuccessAnimation: false,
      contactAddedName: '',
      addingContact: false,
      showEmpresaFields: false,
      showFiscalFields: false,
      previousLinksState: null,
      isViewingClickers: false,
      showLinksModal: false,
      linksModalTitle: '',
      linksModalData: [],
      loadingLinks: false,
      selectedCampaignForLinks: null,
      linksCurrentPage: 1,
      linksPerPage: 10,
      modalErrorMessage: '',
      modalSuccessMessage: '',
      contactsCurrentPage: 1,
      contactsPerPage: 20,
      currentTemplatePage: 1,
      listsCurrentPage: 1,
      listsPerPage: 6,
      templatesPerPage: 3,
      showTemplatePreview: false,
      selectedTemplatePreview: null,
      previewMode: 'desktop',
      errors: {},
      isInitialLoad: true,
      lastLoadTime: 0,
      loadThrottle: 5000,
      showArchiveModal: false,
      showUnarchiveModal: false,
      archiving: false,
      unarchiving: false,
      campaignToArchive: null,
      campaignToArchiveName: '',
      campaignToUnarchive: null,
      campaignToUnarchiveName: '',
      campaigns: [],
      campaignsArchived: [],
      loading: false,
      showCampaignForm: false,
      currentCampaignId: null,
      currentStep: 1,
      errorMessage: '',
      successMessage: '',
      filterStatus: 'all',

      // Templates
      activeTemplates: [],
      loadingTemplates: false,

      // Listas e Segmentos
      contactLists: [],
      segments: [],
      loadingLists: false,
      recipientType: 'lists',

      // Form data
      campaignForm: {
        name: '',
        // subject: '',
        previewText: '',
        senderName: '',
        replyTo: 'negociacao@wurth.com.br',
        templateId: null,
        listIds: [],
        segmentIds: [],
        scheduledAt: ''
      },

      configEmail: {
        senderEmail: '',
        senderName: ''
      },
      sendType: 'now',
      sending: false,

      // Métricas
      showMetricsModal: false,
      selectedCampaignMetrics: null,
      loadingMetrics: false,

      // Suspend
      showSuspendModal: false,
      campaignToSuspend: null,
      campaignToSuspendName: '',
      suspending: false,

      // Delete
      showDeleteModal: false,
      campaignToDelete: null,
      campaignToDeleteName: '',
      deleting: false,

      showContactsModal: false,
      contactsModalTitle: '',
      contactsModalData: [],
      loadingContacts: false,

      // Exclusão de contatos
      showDeleteContactModal: false,
      showDeleteAllContactsModal: false,
      contactToDelete: null,
      contactToDeleteEmail: '',
      deletingContact: false,
      deletingAllContacts: false,
      currentListId: null,
      currentListName: '',

      currentPage: 1,
      itemsPerPage: 6,
      campaignToResume: null,
      campaignToResumeName: '',
      showResumeModal: false,
      resuming: false,
      campaignToCancel: null,
      campaignToCancelName: '',
      showCancelModal: false,
      canceling: false,

      // Gerenciamento de Listas e Contatos
      showCreateListModal: false,
      showAddContactModal: false,
      showBulkAddContactModal: false,
      newListName: '',
      newContactEmail: '',
      newContactFirstName: '',
      newContactLastName: '',
      newContactCpd: '',
      newContactCpf: '',
      newContactRazao: '',
      newContactEmpresa: '',
      newContactNotaFiscal: '',
      newContactTitulo: '',
      newContactEmissao: '',
      newContactVencimento: '',
      newContactValorOriginal: '',
      bulkContactEmails: '',
      selectedListForContact: null,
      selectedListForBulkContacts: null,
      creatingList: false,
      addingBulkContacts: false,

      // Gerenciamento de Segmentos
      showCreateSegmentModal: false,
      newSegmentName: '',
      creatingSegment: false,

    };
  },

  computed: {
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 4; // páginas de cada lado
      const pages = [];

      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i);
      }
      return pages;
    },
    quotaPercentage() {
      if (!this.emailQuota || !this.emailQuota.total) return 0;
      return ((this.emailQuota.remaining / this.emailQuota.total) * 100).toFixed(1);
    },
    filteredErrors() {
      if (!this.errorsModalData.erros_detalhados) return [];

      if (this.selectedErrorType === 'all') {
        return this.errorsModalData.erros_detalhados;
      }

      return this.errorsModalData.erros_detalhados.filter(
        erro => erro.tipo === this.selectedErrorType
      );
    },

    // 🔥 ERROS PAGINADOS
    paginatedErrors() {
      const start = (this.errorsCurrentPage - 1) * this.errorsPerPage;
      const end = start + this.errorsPerPage;
      return this.filteredErrors.slice(start, end);
    },

    errorsTotalPages() {
      return Math.ceil(this.filteredErrors.length / this.errorsPerPage);
    },

    errorsShowingFrom() {
      if (this.filteredErrors.length === 0) return 0;
      return (this.errorsCurrentPage - 1) * this.errorsPerPage + 1;
    },

    errorsShowingTo() {
      const to = this.errorsCurrentPage * this.errorsPerPage;
      return Math.min(to, this.filteredErrors.length);
    },
    timelineGroupedByEmail() {
      if (!this.timelineData || this.timelineData.length === 0) {
        return [];
      }

      const grouped = {};

      this.timelineData.forEach(evento => {
        if (!grouped[evento.email]) {
          grouped[evento.email] = {
            email: evento.email,
            eventos: []
          };
        }
        grouped[evento.email].eventos.push(evento);
      });

      // Converte para array e ordena por email
      const result = Object.values(grouped).sort((a, b) => {
        return a.email.localeCompare(b.email);
      });

      return result;
    },

    paginatedTimelineEmails() {
      const start = (this.timelineCurrentPage - 1) * this.timelineEmailsPerPage;
      const end = start + this.timelineEmailsPerPage;
      return this.timelineGroupedByEmail.slice(start, end);
    },

    timelineTotalPages() {
      return Math.ceil(this.timelineGroupedByEmail.length / this.timelineEmailsPerPage);
    },
    linksWithClicks() {
      if (!this.linksModalData || this.linksModalData.length === 0) return 0;
      return this.linksModalData.filter(l => l.totalClicks > 0).length;
    },
    paginatedLinks() {
      if (!this.linksModalData || this.linksModalData.length === 0) {
        return [];
      }
      const start = (this.linksCurrentPage - 1) * this.linksPerPage;
      const end = start + this.linksPerPage;
      return this.linksModalData.slice(start, end);
    },

    linksTotalPages() {
      if (!this.linksModalData || this.linksModalData.length === 0) {
        return 0;
      }
      return Math.ceil(this.linksModalData.length / this.linksPerPage);
    },
    paginatedContactLists() {
      if (!this.contactLists || this.contactLists.length === 0) {
        return [];
      }
      const start = (this.listsCurrentPage - 1) * this.listsPerPage;
      const end = start + this.listsPerPage;
      return this.contactLists.slice(start, end);
    },

    listsTotalPages() {
      if (!this.contactLists || this.contactLists.length === 0) {
        return 0;
      }
      return Math.ceil(this.contactLists.length / this.listsPerPage);
    },

    listsShowingFrom() {
      if (!this.contactLists || this.contactLists.length === 0) return 0;
      return (this.listsCurrentPage - 1) * this.listsPerPage + 1;
    },

    listsShowingTo() {
      if (!this.contactLists || this.contactLists.length === 0) return 0;
      const to = this.listsCurrentPage * this.listsPerPage;
      return Math.min(to, this.contactLists.length);
    },
    paginatedContactsModal() {
      if (!this.contactsModalData || this.contactsModalData.length === 0) {
        return [];
      }
      const start = (this.contactsCurrentPage - 1) * this.contactsPerPage;
      const end = start + this.contactsPerPage;
      return this.contactsModalData.slice(start, end);
    },

    contactsTotalPages() {
      if (!this.contactsModalData || this.contactsModalData.length === 0) {
        return 0;
      }
      return Math.ceil(this.contactsModalData.length / this.contactsPerPage);
    },

    contactsShowingFrom() {
      if (!this.contactsModalData || this.contactsModalData.length === 0) return 0;
      return (this.contactsCurrentPage - 1) * this.contactsPerPage + 1;
    },

    contactsShowingTo() {
      if (!this.contactsModalData || this.contactsModalData.length === 0) return 0;
      const to = this.contactsCurrentPage * this.contactsPerPage;
      return Math.min(to, this.contactsModalData.length);
    },
    paginatedTemplates() {
      const start = (this.currentTemplatePage - 1) * this.templatesPerPage;
      const end = start + this.templatesPerPage;
      return this.activeTemplates.slice(start, end);
    },

    // Total de páginas de templates
    totalTemplatePages() {
      return Math.ceil(this.activeTemplates.length / this.templatesPerPage);
    },

    // Índice inicial dos templates mostrados
    templateStartIndex() {
      return (this.currentTemplatePage - 1) * this.templatesPerPage;
    },

    // Índice final dos templates mostrados
    templateEndIndex() {
      const end = this.currentTemplatePage * this.templatesPerPage;
      return Math.min(end, this.activeTemplates.length);
    },
    paginatedCampaigns() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCampaigns.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredCampaigns.length / this.itemsPerPage);
    },

    showingFrom() {
      return this.filteredCampaigns.length === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    showingTo() {
      const to = this.currentPage * this.itemsPerPage;
      return to > this.filteredCampaigns.length ? this.filteredCampaigns.length : to;
    },

    filteredCampaigns() {
      // ✅ NOVO: Se filtro é 'archived', retorna as campanhas arquivadas
      if (this.filterStatus === 'archived') {
        return this.campaignsArchived;
      }

      // Para outros filtros, usa apenas as campanhas ativas
      if (this.filterStatus === 'all') {
        return this.campaigns;  // Todas as ativas
      } else {
        return this.campaigns.filter(c => c.status === this.filterStatus);
      }
    },

    sentCount() {
      return this.campaigns.filter(c => c.status === 'sent').length;
    },

    scheduledCount() {
      // agora conta scheduled **ou** queued
      return this.campaigns.filter(c => c.status === 'scheduled' || c.status === 'queued').length;
    },

    draftCount() {
      return this.campaigns.filter(c => c.status === 'draft').length;
    },

    archivedCount() {
      return this.campaignsArchived.length;
    },

    allCount() {
      return this.campaigns.filter(c => !c.isArchived).length;
    }

  },

  async mounted() {
    await this.loadCampaignsOnce();
    await this.loadEmailQuota();
    const res = await fetch('/emails/api/brevo/config');
    this.configData = await res.json();
    this.configEmail.senderEmail = this.configData.senderEmail;
    this.configEmail.senderName = this.configData.senderName;

    this.campaignForm.senderName = this.configData.senderName;

  },
  beforeDestroy() {
    this.stopMetricsPolling();
  },

  methods: {
    formatLote(arquivo) {
      if (!arquivo) return '-';
      const match = arquivo.match(/_old\/(.+)$/);
      return match ? match[1] : arquivo;
    },
    openEditListModal(listId, listName) {
      this.editListId = listId;
      this.editListName = listName;
      this.showEditListModal = true;
    },

    async confirmEditList() {
      if (!this.editListName.trim()) return;
      this.editingList = true;
      try {
        const response = await fetch(`/emails/api/listas/${this.editListId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ name: this.editListName })
        });
        const result = await response.json();
        if (result.success) {
          this.successMessage = 'Lista renomeada com sucesso!';
          setTimeout(() => this.successMessage = '', 4000);
          this.showEditListModal = false;
          await this.loadContactLists();
        } else {
          throw new Error(result.message || 'Erro ao renomear');
        }
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.editingList = false;
      }
    },

    openDeleteListModal(listId, listName) {
      this.listToDelete = listId;
      this.listToDeleteName = listName;
      this.showDeleteListModal = true;
    },

    async confirmDeleteList() {
      this.deletingList = true;
      try {
        const response = await fetch(`/emails/api/listas/${this.listToDelete}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });
        const result = await response.json();
        if (result.success) {
          this.successMessage = 'Lista excluída com sucesso!';
          setTimeout(() => this.successMessage = '', 4000);
          this.showDeleteListModal = false;
          await this.loadContactLists();
        } else {
          throw new Error(result.message || 'Erro ao excluir');
        }
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.deletingList = false;
      }
    },
    formatTempo(segundos) {
      if (!segundos || segundos === 0) return '0s';

      const minutos = Math.floor(segundos / 60);
      const segs = Math.round(segundos % 60);

      if (minutos > 0) {
        return `${minutos}min ${segs}s`;
      }

      return `${segs}s`;
    },

    async loadEmailQuota() {
      try {
        const response = await fetch('/emails/api/brevo/account-quota', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) throw new Error('Erro ao carregar quota');

        const result = await response.json();

        if (result.success) {
          this.emailQuota = result.data;
          console.log('✅ Quota carregada:', this.emailQuota);
        }
      } catch (error) {
        console.error('❌ Erro ao carregar quota:', error);
      }
    },

    getQuotaClass() {
      const percentage = this.quotaPercentage;
      if (percentage > 50) return 'quota-good';
      if (percentage > 20) return 'quota-warning';
      return 'quota-danger';
    },
    async openFileSelection(listId, listName) {
      this.currentListForImport = listId;
      this.currentListNameForImport = listName;
      this.showFileSelectionModal = true;
      this.loadingFiles = true;
      this.availableFiles = [];
      this.selectedFile = null;

      try {
        const response = await axios.get('/emails/api/contatos/import/files');

        if (response.data.success) {
          this.availableFiles = response.data.data;
          console.log('✅ Arquivos carregados:', this.availableFiles.length);
        } else {
          throw new Error(response.data.message || 'Erro ao listar arquivos');
        }
      } catch (error) {
        console.error('❌ Erro ao listar arquivos:', error);
        this.errorMessage = error.response?.data?.message || 'Erro ao listar arquivos';
        this.showFileSelectionModal = false;
      } finally {
        this.loadingFiles = false;
      }
    },

    /**
     * 🆕 Seleciona um arquivo
     */
    selectFile(file) {
      this.selectedFile = file;
    },

    /**
     * 🆕 Confirma seleção e carrega preview do arquivo
     */
    async confirmFileSelection() {
      if (!this.selectedFile) {
        this.modalErrorMessage = 'Por favor, selecione um arquivo';
        return;
      }

      this.loadingImport = true;

      try {
        const response = await axios.post('/emails/api/contatos/import/file', {
          filename: this.selectedFile.full_path
        });

        if (response.data.success) {
          this.importModalData = {
            listId: this.currentListForImport,
            listName: this.currentListNameForImport,
            contacts: response.data.contacts,
            fileInfo: response.data.file_info
          };

          // 🔥 NÃO LIMPA O ARQUIVO SELECIONADO
          this.showFileSelectionModal = false;
          this.showImportModal = true;
          console.log('✅ Arquivo carregado para preview');
        } else {
          throw new Error(response.data.message || 'Erro ao carregar arquivo');
        }
      } catch (error) {
        console.error('❌ Erro ao carregar arquivo:', error);
        this.errorMessage = error.response?.data?.message || 'Erro ao carregar arquivo';
      } finally {
        this.loadingImport = false;
      }
    },

    backToFileSelection() {
      this.showImportModal = false;
      this.showFileSelectionModal = true;
      // 🔥 selectedFile mantém o valor, então volta com arquivo selecionado
    },

    /**
     * 🆕 Fecha modal de seleção
     */
    closeFileSelection() {
      this.showFileSelectionModal = false;
      this.selectedFile = null;
      this.currentListForImport = null;
      this.currentListNameForImport = '';
    },

    async viewErrors(campaign) {
      this.loadingErrors = true;
      this.showErrorsModal = true;
      this.errorsModalTitle = `Erros - ${campaign.name}`;
      this.errorsCurrentPage = 1;
      this.selectedErrorType = 'all';

      this.errorsModalData = {
        total_enviados: 0,
        total_erros: 0,
        percentual_erro: 0,
        erros_por_tipo: {},
        erros_detalhados: []
      };

      try {
        const response = await fetch(`/emails/api/campanhas/${campaign.id}/erros-detalhados`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          this.errorsModalData = result.data;
        } else {
          throw new Error(result.message || 'Erro ao carregar dados');
        }

      } catch (error) {
        console.error('❌ Erro ao carregar erros:', error);
        this.errorMessage = `Erro ao carregar dados: ${error.message}`;
        this.showErrorsModal = false;
      } finally {
        this.loadingErrors = false;
      }
    },

    /**
     * 🔍 Filtra erros por tipo
     */
    filterErrorsByType(tipo) {
      this.selectedErrorType = tipo;
      this.errorsCurrentPage = 1; // Reset para primeira página
    },

    /**
     * 📄 Paginação de erros
     */
    errorsPreviousPage() {
      if (this.errorsCurrentPage > 1) {
        this.errorsCurrentPage--;
      }
    },

    errorsNextPage() {
      if (this.errorsCurrentPage < this.errorsTotalPages) {
        this.errorsCurrentPage++;
      }
    },

    errorsGoToPage(page) {
      this.errorsCurrentPage = page;
    },

    /**
     * ❌ Fecha modal de erros
     */
    closeErrorsModal() {
      this.showErrorsModal = false;
      this.errorsModalData = {
        total_enviados: 0,
        total_erros: 0,
        percentual_erro: 0,
        erros_por_tipo: {},
        erros_detalhados: []
      };
      this.selectedErrorType = 'all';
      this.errorsCurrentPage = 1;
    },

    /**
     * 🎨 Retorna ícone e cor por tipo de erro
     */
    getErrorIcon(tipo) {
      const icons = {
        'hard_bounce': { icon: 'fa-solid fa-ban', color: '#dc2626' },
        'soft_bounce': { icon: 'fa-solid fa-exclamation-triangle', color: '#f59e0b' },
        'invalid_email': { icon: 'fa-solid fa-at', color: '#dc2626' },
        'blocked': { icon: 'fa-solid fa-shield-halved', color: '#991b1b' },
        'spam': { icon: 'fa-solid fa-bug', color: '#7c2d12' },
        'complaint': { icon: 'fa-solid fa-flag', color: '#be123c' },
        'unsubscribed': { icon: 'fa-solid fa-user-xmark', color: '#6b7280' },
        'other': { icon: 'fa-solid fa-circle-exclamation', color: '#6b7280' }
      };
      return icons[tipo] || icons['other'];
    },

    /**
     * 🏷️ Retorna label amigável do tipo de erro
     */
    getErrorTypeLabel(tipo) {
      const labels = {
        'hard_bounce': 'Hard Bounce',
        'soft_bounce': 'Soft Bounce',
        'invalid_email': 'Email Inválido',
        'blocked': 'Bloqueado',
        'spam': 'Spam',
        'complaint': 'Reclamação',
        'unsubscribed': 'Descadastrado',
        'other': 'Outros'
      };
      return labels[tipo] || tipo;
    },

    /**
     * 🔗 Abre modal de erros a partir do modal de métricas
     */
    viewErrorsFromMetrics() {
      if (!this.selectedCampaignMetrics || !this.selectedCampaignMetrics.id) {
        this.errorMessage = 'Campanha não encontrada';
        return;
      }

      // Fecha o modal de métricas
      this.showMetricsModal = false;

      // Busca a campanha completa
      const campaign = this.campaigns.find(c => c.id === this.selectedCampaignMetrics.id) ||
        this.campaignsArchived.find(c => c.id === this.selectedCampaignMetrics.id);

      if (!campaign) {
        const tempCampaign = {
          id: this.selectedCampaignMetrics.id,
          name: this.selectedCampaignMetrics.name
        };
        this.viewErrors(tempCampaign);
      } else {
        this.viewErrors(campaign);
      }
    },
    hasSecondaryEmails(contact) {
      return contact.emails_cc && contact.emails_cc.length > 0;
    },

    isContactExpanded(contactId) {
      return this.expandedContacts.includes(contactId);
    },

    toggleContact(contactId) {
      const index = this.expandedContacts.indexOf(contactId);
      if (index > -1) {
        this.expandedContacts.splice(index, 1);
      } else {
        this.expandedContacts.push(contactId);
      }
    },
    getPaginatedEvents(email, eventos) {
      const page = this.currentEventPages[email] || 1;
      const start = (page - 1) * this.eventsPerEmail;
      const end = start + this.eventsPerEmail;
      return eventos.slice(start, end);
    },

    /**
     * 📊 Total de páginas de eventos de um email
     */
    getTotalEventPages(eventos) {
      return Math.ceil(eventos.length / this.eventsPerEmail);
    },

    /**
     * ⬅️ Página anterior de eventos
     */
    previousEventPage(email) {
      const currentPage = this.currentEventPages[email] || 1;
      if (currentPage > 1) {
        this.$set(this.currentEventPages, email, currentPage - 1);
      }
    },

    /**
     * ➡️ Próxima página de eventos
     */
    nextEventPage(email, totalPages) {
      const currentPage = this.currentEventPages[email] || 1;
      if (currentPage < totalPages) {
        this.$set(this.currentEventPages, email, currentPage + 1);
      }
    },

    /**
     * 📄 Ir para página específica de eventos
     */
    goToEventPage(email, page) {
      this.$set(this.currentEventPages, email, page);
    },

    /**
     * 🔽 Expande email e inicializa página
     */
    toggleTimelineEmail(email) {
      const index = this.expandedTimelineEmails.indexOf(email);
      if (index > -1) {
        this.expandedTimelineEmails.splice(index, 1);
        // Limpa a página ao fechar
        this.$delete(this.currentEventPages, email);
      } else {
        this.expandedTimelineEmails.push(email);
        // Inicializa na página 1
        this.$set(this.currentEventPages, email, 1);
      }
    },

    getDevedoresAgrupados() {
      if (!this.importModalData || !this.importModalData.contacts) {
        return [];
      }

      const devedoresMap = {};

      this.importModalData.contacts.forEach(contact => {
        const cpd = contact.attributes.CPD;

        if (!devedoresMap[cpd]) {
          // Extrair detalhes dos títulos
          const titulosNumeros = contact.attributes.TITULO ? contact.attributes.TITULO.split(' | ') : [];
          const titulosVencimentos = contact.attributes.VENCIMENTO ? contact.attributes.VENCIMENTO.split(' | ') : [];
          const titulosValores = contact.attributes.VALOR_ORIGINAL ? contact.attributes.VALOR_ORIGINAL.split(' | ') : [];
          const titulosDiasAtraso = contact.attributes.DIAS_ATRASO ? contact.attributes.DIAS_ATRASO.split(' | ') : [];

          const titulosDetalhes = titulosNumeros.map((numero, idx) => ({
            numero: numero,
            vencimento: titulosVencimentos[idx] || '',
            valor: titulosValores[idx] || '',
            diasAtraso: titulosDiasAtraso[idx] || '0'
          }));

          // 🔥 MONTA ARRAY DE EMAILS: Principal + CC
          const emails = [contact.email]; // Email principal

          // Adiciona emails_cc se existir
          if (contact.emails_cc && Array.isArray(contact.emails_cc)) {
            emails.push(...contact.emails_cc);
          }

          devedoresMap[cpd] = {
            cpd: cpd,
            razaoSocial: contact.attributes.RAZAO_SOCIAL,
            empresa: contact.attributes.NOME_FANTASIA,
            cpfCnpj: contact.attributes.CPF_CNPJ,
            categoria: contact.attributes.CATEGORIA,
            linkPagamento: contact.attributes.LINK_PAGAMENTO,
            emails: emails, // 🔥 Todos os emails (principal + CC)
            titulos: contact.attributes.TOTAL_TITULOS || 0,
            valorTotal: contact.attributes.VALOR_TOTAL,
            titulosDetalhes: titulosDetalhes
          };
        }
      });

      return Object.values(devedoresMap);
    },

    toggleDevedor(index) {
      this.devedorExpandido = this.devedorExpandido === index ? null : index;
    },

    async importLatestFile(listId, listName) {
      console.log('🔵 importLatestFile - Banco Interno:', { listId, listName });
      this.loadingImport = true;

      try {
        const response = await axios.get('/emails/api/contatos/import/latest');
        console.log('🟢 Resposta do servidor:', response.data);

        if (response.data.success) {
          this.importModalData = {
            listId: listId,
            listName: listName,
            contacts: response.data.contacts,
            fileInfo: response.data.file_info
          };

          this.showImportModal = true;
          console.log('✅ Modal de importação aberto');
        } else {
          console.log('🔴 Erro na resposta:', response.data.message);
          Swal.fire({
            type: 'error',
            title: 'Erro ao Carregar Arquivo',
            text: response.data.message || 'Não foi possível carregar o arquivo de importação'
          });
        }
      } catch (error) {
        console.error('🔴 Erro na requisição:', error);
        Swal.fire({
          type: 'error',
          title: 'Erro',
          text: error.response?.data?.message || 'Erro ao carregar arquivo de importação'
        });
      } finally {
        this.loadingImport = false;
      }
    },

    closeImportModal() {
      this.showImportModal = false;
      this.importModalData = null;
      // 🔥 LIMPA TUDO ao fechar
      this.selectedFile = null;
      this.currentListForImport = null;
      this.currentListNameForImport = '';
    },

    getTotalEmails() {
      if (!this.importModalData || !this.importModalData.contacts) {
        return 0;
      }

      let totalEmails = 0;

      this.importModalData.contacts.forEach(contact => {
        // Email principal
        totalEmails++;

        // Emails CC
        if (contact.emails_cc && Array.isArray(contact.emails_cc)) {
          totalEmails += contact.emails_cc.length;
        }
      });

      return totalEmails;
    },

    async confirmImport() {
      if (!this.importModalData || !this.importModalData.contacts.length) {
        return;
      }

      this.confirmingImport = true;
      this.showValidationProgressModal = true;

      const listId = this.importModalData.listId;
      const filename = this.importModalData.fileInfo?.full_path;

      // Reset progresso
      this.validationProgress = {
        stage: 'validating',
        current: 0,
        total: 0,
        percentage: 0,
        tempo_estimado: 0,
        tempo_decorrido: 0,
        stats: { existem: 0, nao_existem: 0, inconclusivos: 0 }
      };

      try {
        const response = await axios.post(
          `/emails/api/contatos/lists/${listId}/iniciar-validacao`,
          { filename: filename }
        );

        if (response.data.success) {
          const sessionKey = response.data.session_key;
          this.validationProgress.total = response.data.total_emails;
          this.validationProgress.tempo_estimado = response.data.tempo_estimado_minutos;

          // 🔒 Garante que /finalizar seja chamado uma única vez
          let finalizando = false;

          const pollInterval = setInterval(async () => {
            // Se já está finalizando, ignora os ticks seguintes
            if (finalizando) return;

            try {
              const progressResponse = await axios.get(
                `/emails/api/validacao/${sessionKey}/progresso`
              );

              // Outro tick pode ter começado a finalizar enquanto este aguardava
              if (finalizando) return;

              if (progressResponse.data.success) {
                const data = progressResponse.data.data;

                this.validationProgress.current = data.emails_processados;
                this.validationProgress.total = data.total_emails;
                this.validationProgress.percentage = data.percentual_geral;
                this.validationProgress.tempo_decorrido = data.tempo_decorrido_segundos;
                this.validationProgress.stats = {
                  existem: data.stats.existem,
                  nao_existem: data.stats.nao_existem,
                  inconclusivos: data.stats.inconclusivos
                };

                if (data.todos_concluidos) {
                  // 🔒 Marca e para o polling ANTES de qualquer await
                  finalizando = true;
                  clearInterval(pollInterval);

                  this.validationProgress.stage = 'completed';
                  this.validationProgress.percentage = 100;

                  const finalizarResponse = await axios.post(
                    `/emails/api/validacao/${sessionKey}/finalizar`
                  );

                  if (finalizarResponse.data.success) {
                    await new Promise(resolve => setTimeout(resolve, 2000));

                    this.closeImportModal();
                    this.showValidationProgressModal = false;

                    const stats = finalizarResponse.data.stats_validacao || {};
                    const tempoRealMinutos = Math.round((stats.tempo_validacao / 60) * 10) / 10;
                    const tempoRealSegundos = stats.tempo_validacao;

                    Swal.fire({
                      type: 'success',
                      title: '✅ Importação Concluída!',
                      html: `
                        <div style="text-align: left; padding: 20px;">
                          <h4>📊 Validação de Emails</h4>
                          <p>✅ <strong>${stats.existem || 0}</strong> emails existem</p>
                          <p>❌ <strong>${stats.nao_existem || 0}</strong> não existem</p>
                          <p>⚠️ <strong>${stats.inconclusivos || 0}</strong> inconclusivos</p>
                          <p>⏱️ Tempo de validação: <strong>${tempoRealMinutos} min (${tempoRealSegundos}s)</strong></p>
                          ${this.validationProgress.tempo_estimado > 0 ?
                            `<p style="color: #6b7280; font-size: 14px;">
                              <em>Tempo estimado era: ${this.validationProgress.tempo_estimado} min</em>
                            </p>` : ''}
                          <hr>
                          <h4>📦 Resultados da Importação</h4>
                          <p>✅ <strong>${finalizarResponse.data.novos_contatos}</strong> novos contatos</p>
                          <p>🔄 <strong>${finalizarResponse.data.contatos_atualizados}</strong> contatos atualizados</p>
                          ${finalizarResponse.data.contatos_inativos > 0 ?
                            `<p>⚠️ <strong>${finalizarResponse.data.contatos_inativos}</strong> contatos inativos (sem email válido)</p>` : ''}
                          <p style="margin-top: 10px; font-weight: bold;">
                            📊 Total de contatos processados: <strong>${finalizarResponse.data.total_contatos}</strong>
                          </p>
                        </div>
                      `,
                      confirmButtonColor: '#10b981',
                      width: '600px'
                    });

                    await this.loadContactLists();
                  } else {
                    // 🔥 Antes o modal ficava travado aberto quando a finalização falhava
                    this.showValidationProgressModal = false;
                    Swal.fire({
                      type: 'error',
                      title: 'Falha na importação',
                      text: finalizarResponse.data.message || 'A importação não foi concluída'
                    });
                  }
                }
              }
            } catch (error) {
              clearInterval(pollInterval);
              this.showValidationProgressModal = false;

              // 409 = outra chamada já está finalizando esta importação; não é erro real
              if (error.response?.status === 409) {
                console.warn('⚠️ Finalização já em andamento, ignorando chamada duplicada');
                return;
              }

              console.error('❌ Erro ao acompanhar/finalizar importação:', error);
              Swal.fire({
                type: 'error',
                title: 'Erro',
                text: error.response?.data?.message || 'Erro ao acompanhar progresso da validação'
              });
            }
          }, 3000);
        }

      } catch (error) {
        console.error('❌ Erro:', error);
        this.showValidationProgressModal = false;
        Swal.fire({
          type: 'error',
          title: 'Erro ao Iniciar Validação',
          text: error.response?.data?.message || error.message
        });
      } finally {
        this.confirmingImport = false;
      }
    },

    importLatestFile(listId, listName) {
      console.log('🔵 Abrindo seleção de arquivos:', { listId, listName });
      this.openFileSelection(listId, listName);
    },

    formatDateToBR(dateString) {
      if (!dateString) return '';
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    },

    toggleEmpresaFields() {
      this.showEmpresaFields = !this.showEmpresaFields;
      console.log('Empresa Fields:', this.showEmpresaFields); // Para debug
    },

    toggleFiscalFields() {
      this.showFiscalFields = !this.showFiscalFields;
      console.log('Fiscal Fields:', this.showFiscalFields); // Para debug
    },

    formatCPF(value) {
      if (!value) return '';
      value = value.replace(/\D/g, '');
      if (value.length <= 11) {
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      }
      return value;
    },

    formatCNPJ(value) {
      if (!value) return '';
      value = value.replace(/\D/g, '');
      value = value.replace(/^(\d{2})(\d)/, '$1.$2');
      value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
      value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
      value = value.replace(/(\d{4})(\d)/, '$1-$2');
      return value;
    },

    formatCPFCNPJ(event) {
      let value = event.target.value.replace(/\D/g, '');

      if (value.length <= 11) {
        // Formatar como CPF
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        // Formatar como CNPJ
        value = value.replace(/^(\d{2})(\d)/, '$1.$2');
        value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
        value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
        value = value.replace(/(\d{4})(\d)/, '$1-$2');
      }

      this.newContactCpf = value;
    },

    formatCPD(event) {
      let value = event.target.value.replace(/\D/g, '');
      // Limita a 7 dígitos
      value = value.substring(0, 7);
      this.newContactCpd = value;
    },

    formatCurrencyValue(value) {
      if (!value) return '';

      // Remove tudo exceto números e ponto/vírgula
      let numericValue = String(value).replace(/[^\d.,]/g, '');

      // Se estiver vazio após limpeza, retorna vazio
      if (!numericValue) return '';

      // Remove pontos que são separadores de milhar (ex: 1.500 -> 1500)
      // Mas mantém o último ponto/vírgula (decimal)
      const parts = numericValue.split(/[.,]/);

      if (parts.length > 2) {
        // Tem múltiplos separadores: ex "1.500.000,00" ou "1,500,000.00"
        // Pega todos menos o último como inteiro, último como decimal
        const lastPart = parts.pop();
        numericValue = parts.join('') + '.' + lastPart;
      } else if (parts.length === 2) {
        // Tem um separador: pode ser decimal ou milhar
        if (parts[1].length <= 2) {
          // Provavelmente é decimal: "1500,00" ou "1500.00"
          numericValue = parts[0] + '.' + parts[1];
        } else {
          // Provavelmente é milhar: "1,500" -> "1500"
          numericValue = parts.join('');
        }
      }

      // Converte para float
      const numberValue = parseFloat(numericValue);

      // Se não for um número válido, retorna vazio
      if (isNaN(numberValue)) return '';

      // Formata para padrão brasileiro
      return numberValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatPhone(event) {
      let value = event.target.value.replace(/\D/g, '');

      if (value.length <= 10) {
        // Telefone fixo: (XX) XXXX-XXXX
        value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
        value = value.replace(/(\d)(\d{4})$/, '$1-$2');
      } else {
        // Celular: (XX) XXXXX-XXXX
        value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
        value = value.replace(/(\d)(\d{4})$/, '$1-$2');
      }

      return value;
    },

    formatDate(event) {
      let value = event.target.value.replace(/\D/g, '');
      value = value.replace(/(\d{2})(\d)/, '$1/$2');
      value = value.replace(/(\d{2})(\d)/, '$1/$2');
      value = value.substring(0, 10);
      return value;
    },

    onCurrencyInput(event) {
      const input = event.target;
      let value = input.value.replace(/\D/g, '');

      if (value === '') {
        this.newContactValorOriginal = '';
        return;
      }

      // Converte para número e formata
      const numberValue = parseInt(value) / 100;
      const formatted = numberValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });

      this.newContactValorOriginal = formatted;
    },

    // Validações
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    validateCPF(cpf) {
      cpf = cpf.replace(/\D/g, '');
      if (cpf.length !== 11) return false;

      // Validação básica de CPF
      if (/^(\d)\1{10}$/.test(cpf)) return false;

      return true;
    },

    validateCNPJ(cnpj) {
      cnpj = cnpj.replace(/\D/g, '');
      return cnpj.length === 14;
    },

    async viewLinkStatistics(campaign) {
      this.loadingLinks = true;
      this.showLinksModal = true;
      this.linksModalTitle = `Estatísticas de Links - ${campaign.name}`;
      this.selectedCampaignForLinks = campaign.id;
      this.linksModalData = [];
      this.linksCurrentPage = 1;

      try {
        const response = await fetch(`/emails/api/campanhas/${campaign.id}/link-statistics`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (!result.success || !result.data) {
          throw new Error(result.message || 'Erro ao carregar estatísticas');
        }

        if (result.data.length === 0) {
          this.errorMessage = 'Nenhum link encontrado nesta campanha.';
          setTimeout(() => this.errorMessage = '', 5000);
          this.showLinksModal = false;
          return;
        }

        // 🔥 PROCESSA OS DADOS DOS LINKS
        this.linksModalData = result.data.map(link => ({
          url: link.url,
          totalClicks: link.total_cliques,
          uniqueClicks: link.cliques_unicos,
          clickRate: ((link.cliques_unicos / (campaign.statistics?.globalStats?.sent || 1)) * 100).toFixed(1) + '%',
          clickers: link.clickers || []
        }));

        console.log('✅ Links carregados:', this.linksModalData.length);

      } catch (error) {
        console.error('❌ Erro:', error);
        this.errorMessage = `Erro: ${error.message}`;
        this.showLinksModal = false;
      } finally {
        this.loadingLinks = false;
      }
    },

    async viewLinksFromMetrics() {
      if (!this.selectedCampaignMetrics || !this.selectedCampaignMetrics.id) {
        this.errorMessage = 'Campanha não encontrada';
        return;
      }

      // Fecha o modal de métricas
      this.showMetricsModal = false;

      // Busca a campanha completa
      const campaign = this.campaigns.find(c => c.id === this.selectedCampaignMetrics.id) ||
        this.campaignsArchived.find(c => c.id === this.selectedCampaignMetrics.id);

      if (!campaign) {
        // Se não encontrar, cria um objeto temporário com os dados que temos
        const tempCampaign = {
          id: this.selectedCampaignMetrics.id,
          name: this.selectedCampaignMetrics.name
        };
        await this.viewLinkStatistics(tempCampaign);
      } else {
        await this.viewLinkStatistics(campaign);
      }
    },

    // ✅ NOVO: Método auxiliar para pegar o total de enviados
    getTotalSentForCampaign(campaignId) {
      const campaign = this.campaigns.find(c => c.id === campaignId);
      return campaign?.statistics?.globalStats?.sent ||
        campaign?.stats?.sent ||
        0;
    },

    // ✅ ADICIONAR ESTES MÉTODOS:

    calculateTotalClicks() {
      if (!this.linksModalData || this.linksModalData.length === 0) return 0;
      return this.linksModalData.reduce((sum, link) => sum + (link.totalClicks || 0), 0);
    },

    calculateTotalUniqueClicks() {
      if (!this.linksModalData || this.linksModalData.length === 0) return 0;
      return this.linksModalData.reduce((sum, link) => sum + (link.uniqueClicks || 0), 0);
    },

    // ✅ NOVO: Visualizar quem clicou em um link específico
    viewLinkClickers(link) {
      console.log('👥 Ver clickers do link:', link.url);

      // ✅ NOVO: Salva o estado completo do modal de links
      this.previousLinksState = {
        data: [...this.linksModalData], // Cópia dos dados
        title: this.linksModalTitle,
        page: this.linksCurrentPage,
        campaignId: this.selectedCampaignForLinks
      };

      this.isViewingClickers = true; // Marca que está vendo clickers

      this.showLinksModal = false; // Fecha o modal de links
      this.showContactsModal = true; // Abre o modal de contatos
      this.contactsModalTitle = `Contatos que clicaram: ${this.truncateUrl(link.url, 50)}`;
      this.contactsModalData = link.clickers || [];
      this.resetContactsPagination();

      console.log('✅ Total de clickers:', this.contactsModalData.length);
    },

    closeLinksModalProperly() {
      this.showLinksModal = false;
      this.previousLinksState = null;
      this.isViewingClickers = false;
    },

    // ✅ NOVO: Fecha o modal de contatos e limpa o estado
    closeContactsModalProperly() {
      this.showContactsModal = false;

      // Se estava vendo clickers, limpa o estado
      if (this.isViewingClickers) {
        this.isViewingClickers = false;
        this.previousLinksState = null;
      }
    },

    // ✅ NOVO: Voltar do modal de clickers para o modal de links
    goBackToLinksModal() {
      if (!this.previousLinksState) return;

      // Fecha o modal de contatos/clickers
      this.showContactsModal = false;
      this.isViewingClickers = false;

      // Restaura os dados do modal de links (SEM fazer nova requisição)
      this.linksModalData = this.previousLinksState.data;
      this.linksModalTitle = this.previousLinksState.title;
      this.linksCurrentPage = this.previousLinksState.page;
      this.selectedCampaignForLinks = this.previousLinksState.campaignId;

      // Reabre o modal de links
      this.showLinksModal = true;

      // Limpa o estado salvo
      this.previousLinksState = null;

      console.log('✅ Voltou para o modal de links sem recarregar');
    },

    // Adicione este método auxiliar se não tiver
    truncateUrl(url, maxLength) {
      if (!url) return '';
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength - 3) + '...';
    },

    linksPreviousPage() {
      if (this.linksCurrentPage > 1) {
        this.linksCurrentPage--;
      }
    },

    linksNextPage() {
      if (this.linksCurrentPage < this.linksTotalPages) {
        this.linksCurrentPage++;
      }
    },

    linksGoToPage(page) {
      this.linksCurrentPage = page;
    },
    processTemplateVariables(htmlContent) {
      if (!htmlContent) return '';

      let processedHtml = htmlContent;

      const isInsideHref = (text, index) => {
        const before = text.substring(Math.max(0, index - 80), index);
        return /href\s*=\s*["'][^"']*$/i.test(before);
      };

      const highlightSpan = (variableName) => {
        // 🔥 Estilo roxo mais próximo da imagem
        return `<span style="background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%); color: white; padding: 4px 12px; border-radius: 6px; font-weight: 700; font-size: 0.85em; display: inline; margin: 0 3px; box-shadow: 0 2px 4px rgba(124, 58, 237, 0.4); white-space: nowrap; font-family: 'Segoe UI', Arial, sans-serif; vertical-align: middle; text-transform: uppercase; letter-spacing: 0.5px;"><strong>${variableName}</strong></span>`;
      };

      // 🔥 IMPORTANTE: Substitui {{ contact.VARIAVEL }} e {{ VARIAVEL }} mas mantém só o nome
      processedHtml = processedHtml.replace(/\{\{\s*contact\.([A-Z_]+)\s*\}\}/gi, (match, variableName, offset) => {
        if (isInsideHref(processedHtml, offset)) return match;
        return highlightSpan(variableName);
      });

      processedHtml = processedHtml.replace(/\{\{\s*([A-Z_]+)\s*\}\}/gi, (match, variableName, offset) => {
        if (isInsideHref(processedHtml, offset)) return match;
        return highlightSpan(variableName);
      });

      return processedHtml;
    },

    listsPreviousPage() {
      if (this.listsCurrentPage > 1) {
        this.listsCurrentPage--;
      }
    },

    listsNextPage() {
      if (this.listsCurrentPage < this.listsTotalPages) {
        this.listsCurrentPage++;
      }
    },

    listsGoToPage(page) {
      this.listsCurrentPage = page;
    },

    resetListsPagination() {
      this.listsCurrentPage = 1;
    },
    contactsPreviousPage() {
      if (this.contactsCurrentPage > 1) {
        this.contactsCurrentPage--;
      }
    },

    contactsNextPage() {
      if (this.contactsCurrentPage < this.contactsTotalPages) {
        this.contactsCurrentPage++;
      }
    },

    contactsGoToPage(page) {
      this.contactsCurrentPage = page;
    },

    resetContactsPagination() {
      this.contactsCurrentPage = 1;
    },

    goToTemplatePage(page) {
      if (page >= 1 && page <= this.totalTemplatePages) {
        this.currentTemplatePage = page;
      }
    },

    // Ir para página anterior de templates
    previousTemplatePage() {
      if (this.currentTemplatePage > 1) {
        this.goToTemplatePage(this.currentTemplatePage - 1);
      }
    },

    // Ir para próxima página de templates
    nextTemplatePage() {
      if (this.currentTemplatePage < this.totalTemplatePages) {
        this.goToTemplatePage(this.currentTemplatePage + 1);
      }
    },
    openTemplatePreview(template) {
      this.selectedTemplatePreview = {
        ...template,
        // Processa o HTML antes de atribuir
        processedHtml: this.processTemplateVariables(template.htmlContent)
      };

      this.showTemplatePreview = true;
      this.previewMode = 'desktop'; // Sempre abre em desktop
    },

    closeTemplatePreview() {
      this.showTemplatePreview = false;
      this.selectedTemplatePreview = null;
      this.previewMode = 'desktop';
    },

    selectTemplateFromPreview() {
      if (this.selectedTemplatePreview) {
        this.selectTemplate(this.selectedTemplatePreview.id);
        this.closeTemplatePreview();
      }
    },

    // NOVO: Ajustar altura do iframe automaticamente
    adjustIframeHeight() {
      this.$nextTick(() => {
        const iframe = this.$refs.templatePreviewIframe;
        if (iframe && iframe.contentWindow) {
          try {
            const contentHeight = iframe.contentWindow.document.body.scrollHeight;
            iframe.style.height = contentHeight + 'px';
          } catch (e) {
            // Erro de cross-origin, mantém altura padrão
            console.log('Não foi possível ajustar altura do iframe');
          }
        }
      });
    },
    // Adicione esses métodos no bloco methods
    openResumeModal(campaignId, campaignName) {
      this.campaignToResume = campaignId;
      this.campaignToResumeName = campaignName;
      this.showResumeModal = true;
    },

    async confirmResume() {
      if (!this.campaignToResume) return;

      this.resuming = true;

      try {
        // ✅ NOVO: Busca a campanha para pegar a data de agendamento
        const campaignResponse = await fetch(`/api/brevo/campaigns/${this.campaignToResume}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || ''
          }
        });

        const campaignData = await campaignResponse.json();
        let scheduledAt = null;

        // ✅ NOVO: Valida se a data de agendamento é maior que agora
        if (campaignData.data && campaignData.data.scheduledAt) {
          const scheduledDate = new Date(campaignData.data.scheduledAt);
          const now = new Date();

          // Se a data agendada for menor que agora, usa a data atual
          if (scheduledDate < now) {
            scheduledAt = now.toISOString();
            console.log('Data de agendamento no passado. Usando data atual:', scheduledAt);
          } else {
            scheduledAt = campaignData.data.scheduledAt;
            console.log('Data de agendamento válida:', scheduledAt);
          }
        }

        const csrfToken = document.querySelector('meta[name="csrf-token"]');

        // ✅ NOVO: Envia a data atualizada se necessário
        const response = await fetch(`/api/brevo/campaigns/${this.campaignToResume}/resume`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken ? csrfToken.content : ''
          },
          body: JSON.stringify({
            scheduledAt: scheduledAt  // ← NOVO: Envia a data validada
          })
        });

        console.log('Response Status:', response.status);

        const result = await response.json();
        console.log('Response Body:', result);

        if (!response.ok) {
          throw new Error(result.message || `Erro HTTP: ${response.status}`);
        }

        if (result.success) {
          this.successMessage = 'Campanha retomada com sucesso!';
          setTimeout(() => this.successMessage = '', 5000);
          this.showResumeModal = false;
          this.campaignToResume = null;
          this.campaignToResumeName = '';
          await this.loadCampaignsOnce();
          await new Promise(resolve => setTimeout(resolve, 2000));
          await this.loadCampaignsArchived();
        }
      } catch (error) {
        console.error('Erro completo:', error);
        this.errorMessage = `Erro ao retomar campanha: ${error.message}`;
        this.showResumeModal = false;
      } finally {
        this.resuming = false;
      }
    },

    openCancelModal(campaignId, campaignName) {
      this.campaignToCancel = campaignId;
      this.campaignToCancelName = campaignName;
      this.showCancelModal = true;
    },

    async confirmCancel() {
      if (!this.campaignToCancel) return;

      this.canceling = true;

      try {
        const response = await fetch(`/api/brevo/campaigns/${this.campaignToCancel}/cancel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Campanha cancelada com sucesso!';
          setTimeout(() => this.successMessage = '', 5000);
          this.showCancelModal = false;
          this.campaignToCancel = null;
          this.campaignToCancelName = '';
          await this.loadCampaignsOnce();
          await new Promise(resolve => setTimeout(resolve, 2000));
          await this.loadCampaignsArchived();
        }
      } catch (error) {
        console.error('Erro ao cancelar campanha:', error);
        this.errorMessage = `Erro ao cancelar campanha: ${error.message}`;
        this.showCancelModal = false;
      } finally {
        this.canceling = false;
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    resetPagination() {
      this.currentPage = 1;
    },

    async viewListContacts(listId, listName) {
      this.contactsModalTitle = `Contatos de: ${listName}`;
      this.currentListId = listId;
      this.currentListName = listName;
      this.showContactsModal = true;
      this.resetContactsPagination();
      this.loadingContacts = true;
      this.contactsModalData = [];
      this.errorMessage = null;

      try {
        const response = await fetch(`/emails/api/listas/${listId}/contatos`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result?.success) {
          // ✅ Ordena os contatos pelo email
          this.contactsModalData = (result.data || []).sort((a, b) => {
            if (!a.email) return 1;
            if (!b.email) return -1;
            return a.email.localeCompare(b.email);
          });

          console.log('✅ Contatos carregados:', this.contactsModalData.length);
        } else {
          throw new Error(result?.message || 'Erro ao carregar contatos');
        }

      } catch (error) {
        console.error('❌ Erro ao carregar contatos:', error);
        this.errorMessage = `Erro ao carregar contatos: ${error.message}`;
      } finally {
        this.loadingContacts = false;
      }
    },


    openDeleteContactModal(contactEmail) {
      this.contactToDelete = contactEmail;
      this.contactToDeleteEmail = contactEmail;
      this.showDeleteContactModal = true;
    },

    /**
     * Confirma e executa a exclusão PERMANENTE de um contato
     */
    async confirmDeleteContact() {
      if (!this.contactToDelete) return;

      this.deletingContact = true;

      try {
        const response = await fetch(`/api/brevo/contacts/${encodeURIComponent(this.contactToDelete)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = `✅ Contato "${this.contactToDeleteEmail}" excluído com sucesso!`;
          setTimeout(() => this.successMessage = '', 5000);

          // Fecha o modal de confirmação
          this.showDeleteContactModal = false;

          // Remove da lista local
          this.contactsModalData = this.contactsModalData.filter(
            contact => contact.email !== this.contactToDelete
          );

          // Limpa as variáveis
          const emailExcluido = this.contactToDeleteEmail;
          this.contactToDelete = null;
          this.contactToDeleteEmail = '';

          // ✅ NOVO: Recarrega a lista de contatos para atualizar a contagem
          if (this.currentListId) {
            await this.reloadCurrentListContacts();
          }

          // ✅ NOVO: Recarrega as listas para atualizar o contador
          await this.loadContactLists();

          // Fecha o modal se não houver mais contatos
          if (this.contactsModalData.length === 0) {
            this.showContactsModal = false;
          }

          console.log(`✅ Contato ${emailExcluido} excluído e contagem atualizada`);
        }
      } catch (error) {
        console.error('❌ Erro ao excluir contato:', error);
        this.errorMessage = `Erro ao excluir contato: ${error.message}`;
        this.showDeleteContactModal = false;
      } finally {
        this.deletingContact = false;
      }
    },

    /**
     * Abre o modal para confirmar exclusão permanente de todos os contatos
     */
    openDeleteAllContactsModal() {
      if (this.contactsModalData.length === 0) {
        this.errorMessage = 'Não há contatos para excluir.';
        return;
      }
      this.showDeleteAllContactsModal = true;
    },

    /**
     * Confirma e executa a exclusão PERMANENTE de todos os contatos
     */
    async confirmDeleteAllContacts() {
      this.deletingAllContacts = true;

      try {
        const emails = this.contactsModalData.map(c => c.email);
        if (emails.length === 0) return;

        const response = await fetch('/api/brevo/contacts/bulk-delete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ emails })
        });

        if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);

        const result = await response.json();

        // Mostra detalhamento de sucesso e falhas
        const { success, failed } = result.results;

        this.successMessage = `✅ ${success.length} contatos excluídos com sucesso`;
        if (failed.length > 0) {
          this.errorMessage = `⚠️ ${failed.length} falharam:\n` +
            failed.map(f => `${f.email} (${f.error})`).join('\n');
        }

        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 10000);

        this.showDeleteAllContactsModal = false;
        this.showContactsModal = false;
        this.contactsModalData = [];

        await this.loadContactLists();

      } catch (error) {
        console.error('❌ Erro ao excluir todos os contatos:', error);
        this.errorMessage = `Erro ao excluir contatos: ${error.message}`;
        this.showDeleteAllContactsModal = false;
      } finally {
        this.deletingAllContacts = false;
      }
    },

    /**
     * ✅ NOVO MÉTODO: Recarrega a lista de contatos atual (sem fechar o modal)
     */
    async reloadCurrentListContacts() {
      if (!this.currentListId) return;

      try {
        console.log(`🔄 Recarregando contatos da lista ${this.currentListId}...`);

        this.loadingContacts = true;

        const response = await fetch(`/api/brevo/contacts/lists/${this.currentListId}/contacts`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result?.success) {
          this.contactsModalData = result.data || [];
          console.log(`✅ Contatos recarregados: ${this.contactsModalData.length} contatos`);
        }
      } catch (error) {
        console.error('❌ Erro ao recarregar contatos:', error);
      } finally {
        this.loadingContacts = false;
      }
    },

    async viewSegmentContacts(segmentId, segmentName) {
      this.contactsModalTitle = `Contatos do segmento: ${segmentName}`;
      this.showContactsModal = true;
      this.loadingContacts = true;
      this.contactsModalData = [];

      try {
        const response = await fetch(`/api/brevo/segments/${segmentId}/contacts`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.contactsModalData = result.data || [];
        } else {
          throw new Error(result.message || 'Erro ao carregar contatos');
        }
      } catch (error) {
        console.error('Erro ao carregar contatos:', error);
        this.errorMessage = `Erro ao carregar contatos: ${error.message}`;
        this.showContactsModal = false;
      } finally {
        this.loadingContacts = false;
      }
    },

    async loadCampaignsArchived() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await fetch('/emails/api/campanhas/campanhas-archived', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.campaignsArchived = result.data || [];
        } else {
          throw new Error(result.message || 'Erro ao carregar campanhas');
        }
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error);
        this.errorMessage = `Erro ao carregar campanhas: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },
    async loadCampaigns() {
      this.loading = true;
      this.errorMessage = '';

      await this.loadEmailQuota();

      try {
        // Chamada para campanhas ativas
        const response = await fetch('/emails/api/campanhas', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.campaigns = result.data || [];
          // ADICIONAR: Carregar arquivadas de forma silenciosa
          await this.loadCampaignsArchivedSilent();
        } else {
          throw new Error(result.message || 'Erro ao carregar campanhas');
        }
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error);
        this.errorMessage = `Erro ao carregar campanhas: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },

    async loadCampaignsOnce() {
      const now = Date.now();

      // Se não é o carregamento inicial e o último carregamento foi há menos de 5 segundos, ignora
      if (!this.isInitialLoad && (now - this.lastLoadTime) < this.loadThrottle) {
        console.log('Carregamento ignorado (throttle)');
        return;
      }

      this.isInitialLoad = false;
      this.lastLoadTime = now;
      await this.loadCampaigns();
    },

    async loadCampaignsArchivedSilent() {
      try {
        const response = await fetch('/emails/api/campanhas/campanhas-archived', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            this.campaignsArchived = result.data || [];
          }
        }
      } catch (error) {
        console.error('Erro ao carregar campanhas arquivadas:', error);
        // Erro silencioso, não exibe mensagem
      }
    },

    async loadTemplates() {
      this.loadingTemplates = true;

      try {
        const response = await fetch('/emails/api/brevo/templates', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.activeTemplates = (result.data || []).filter(t => t.isActive);
        }
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
        this.errorMessage = `Erro ao carregar templates: ${error.message}`;
      } finally {
        this.loadingTemplates = false;
      }
    },

    async loadContactLists() {
      this.loadingLists = true;
      this.resetListsPagination();

      try {
        const response = await fetch('/emails/api/listas', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.contactLists = result.data || [];
          console.log('✅ Listas carregadas do banco interno:', this.contactLists.length);
        }
      } catch (error) {
        console.error('❌ Erro ao carregar listas:', error);
        this.errorMessage = `Erro ao carregar listas: ${error.message}`;
      } finally {
        this.loadingLists = false;
      }
    },


    // async loadSegments() {
    //   try {
    //     const response = await fetch('/api/brevo/segments', {
    //       method: 'GET',
    //       headers: {
    //         'Content-Type': 'application/json',
    //         'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
    //       }
    //     });

    //     if (!response.ok) {
    //       throw new Error(`Erro HTTP: ${response.status}`);
    //     }

    //     const result = await response.json();

    //     if (result.success) {
    //       this.segments = result.data || [];
    //     }
    //   } catch (error) {
    //     console.error('Erro ao carregar segmentos:', error);
    //   }
    // },

    async createList() {
      if (!this.newListName.trim()) {
        this.errorMessage = 'Por favor, digite um nome para a lista';
        setTimeout(() => this.errorMessage = '', 5000);
        return;
      }

      this.creatingList = true;
      try {
        // 🔥 NOVO: Pega o nome do template selecionado
        const templateSelecionado = this.activeTemplates.find(
          t => t.id === this.campaignForm.templateId
        );

        const descricao = templateSelecionado
          ? templateSelecionado.name
          : '';

        const response = await fetch('/emails/api/listas', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            name: this.newListName,
            descricao: descricao // 🔥 ENVIA A DESCRIÇÃO
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Erro ao criar lista');
        }

        const result = await response.json();

        this.successMessage = '✅ Lista criada com sucesso no banco interno!';
        setTimeout(() => this.successMessage = '', 5000);
        this.newListName = '';
        this.showCreateListModal = false;

        await this.loadContactLists();

        console.log('✅ Lista criada:', result.data);
      } catch (error) {
        console.error('❌ Erro ao criar lista:', error);
        this.errorMessage = error.message || 'Erro ao criar lista';
        setTimeout(() => this.errorMessage = '', 5000);
      } finally {
        this.creatingList = false;
      }
    },

    async createSegment() {
      if (!this.newSegmentName.trim()) {
        this.errorMessage = 'Por favor, digite um nome para o segmento';
        setTimeout(() => this.errorMessage = '', 5000);
        return;
      }

      this.creatingSegment = true;
      try {
        const response = await fetch('/api/brevo/contacts/segments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            name: this.newSegmentName,
            categoryType: 'transactional'
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Erro ao criar segmento');
        }

        this.successMessage = 'Segmento criado com sucesso!';
        setTimeout(() => this.successMessage = '', 5000);
        this.newSegmentName = '';
        this.showCreateSegmentModal = false;

        // await this.loadSegments();
      } catch (error) {
        console.error('Erro ao criar segmento:', error);
        this.errorMessage = error.message || 'Erro ao criar segmento';
        setTimeout(() => this.errorMessage = '', 5000);
      } finally {
        this.creatingSegment = false;
      }
    },

    async addContactToList() {
      this.modalErrorMessage = '';
      this.modalSuccessMessage = '';

      // ✅ Validações (permanecem iguais)
      if (!this.newContactEmail.trim()) {
        this.modalErrorMessage = 'Por favor, digite um email';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.newContactEmail)) {
        this.modalErrorMessage = 'Por favor, digite um email válido';
        return;
      }

      if (!this.newContactFirstName.trim()) {
        this.modalErrorMessage = 'Por favor, digite o nome';
        return;
      }

      if (!this.newContactCpd.trim()) {
        this.modalErrorMessage = 'Por favor, digite o CPD';
        return;
      }

      if (!this.newContactCpf.trim()) {
        this.modalErrorMessage = 'Por favor, digite o CPF/CNPJ';
        return;
      }

      if (!this.newContactLink.trim()) {
        this.modalErrorMessage = 'Por favor, digite o link de pagamento';
        return;
      }

      if (!this.selectedListForContact) {
        this.modalErrorMessage = 'Por favor, selecione uma lista';
        return;
      }

      this.addingContact = true;

      try {
        const attributes = {
          NOME: this.newContactFirstName.trim(),
          SOBRENOME: this.newContactLastName.trim(),
          CPD: this.newContactCpd.trim(),
          CPF_CNPJ: this.newContactCpf.trim(),
          LINK_PAGAMENTO: this.newContactLink.trim(),
          RAZAO_SOCIAL: this.newContactRazao.trim(),
          EMPRESA: this.newContactEmpresa.trim(),
          NOTA_FISCAL: this.newContactNotaFiscal.trim(),
          TITULO: this.newContactTitulo.trim(),
          EMISSAO: this.formatDateToBR(this.newContactEmissao),
          VENCIMENTO: this.formatDateToBR(this.newContactVencimento),
          VALOR_ORIGINAL: this.formatCurrencyValue(this.newContactValorOriginal)
        };

        // Remove campos vazios
        Object.keys(attributes).forEach(key => {
          if (attributes[key] === '' || attributes[key] === null || attributes[key] === undefined) {
            delete attributes[key];
          }
        });

        // ✅ NOVA ROTA: Salva no banco interno
        const response = await fetch(`/emails/api/contatos/lists/${this.selectedListForContact}/contacts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            email: this.newContactEmail,
            attributes: attributes
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Erro ao adicionar contato');
        }

        // ✅ Animação de sucesso
        this.contactAddedName = `${this.newContactFirstName} ${this.newContactLastName || ''}`.trim();
        this.showSuccessAnimation = true;
        this.addingContact = false;

        setTimeout(async () => {
          await this.loadContactLists();
          this.resetContactForm();
          this.showAddContactModal = false;
        }, 2500);

      } catch (error) {
        console.error('❌ Erro ao adicionar contato:', error);
        this.modalErrorMessage = error.message || 'Erro ao adicionar contato';
        this.addingContact = false;
      }
    },
    // ✅ Novo método para resetar o formulário
    resetContactForm() {
      this.newContactEmail = '';
      this.newContactFirstName = '';
      this.newContactLastName = '';
      this.newContactCpf = '';
      this.newContactCpd = '';
      this.newContactRazao = '';
      this.newContactEmpresa = '';
      this.newContactNotaFiscal = '';
      this.newContactTitulo = '';
      this.newContactEmissao = '';
      this.newContactVencimento = '';
      this.newContactValorOriginal = '';
      this.newContactLink = '';
      this.selectedListForContact = null;
      this.modalSuccessMessage = '';
      this.modalErrorMessage = '';
      this.showSuccessAnimation = false;
      this.contactAddedName = '';
      this.showEmpresaFields = false;
      this.showFiscalFields = false;
    },

    // ✅ Atualiza o método de fechar modal
    // closeAddContactModal() {
    //   if (!this.addingContact && !this.showSuccessAnimation) {
    //     this.showAddContactModal = false;
    //     this.resetContactForm();
    //   }
    // },


    async addBulkContactsToList() {
      this.modalErrorMessage = '';
      this.modalSuccessMessage = '';

      if (!this.bulkContactEmails.trim()) {
        this.modalErrorMessage = 'Por favor, digite os contatos';
        return;
      }

      if (!this.selectedListForBulkContacts) {
        this.modalErrorMessage = 'Por favor, selecione uma lista';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const lines = this.bulkContactEmails.split('\n').filter(line => line.trim());

      if (lines.length > 1000) {
        this.modalErrorMessage = `Limite excedido! Máximo de 1.000 contatos por vez.`;
        return;
      }

      this.modalSuccessMessage = `📊 Detectados ${lines.length} contatos. Validando...`;

      const contacts = [];
      const invalidLines = [];

      lines.forEach((line, index) => {
        const csvRegex = /(?:^|[,;])\s*(?:"([^"]*)"|([^,;]*))/g;
        const parts = [];
        let match;

        while ((match = csvRegex.exec(line)) !== null) {
          parts.push(match[1] !== undefined ? match[1] : match[2]);
        }

        if (parts[0] === '') parts.shift();

        const email = parts[0]?.trim() || '';
        const NOME = parts[1]?.trim() || '';
        const SOBRENOME = parts[2]?.trim() || '';
        const CPD = parts[3]?.trim() || '';
        const CPF_CNPJ = parts[4]?.trim() || '';
        const LINK_PAGAMENTO = parts[5]?.trim() || '';
        const RAZAO_SOCIAL = parts[6]?.trim() || '';
        const EMPRESA = parts[7]?.trim() || '';
        const NOTA_FISCAL = parts[8]?.trim() || '';
        const TITULO = parts[9]?.trim() || '';
        const EMISSAO = parts[10]?.trim() || '';
        const VENCIMENTO = parts[11]?.trim() || '';
        const VALOR_ORIGINAL = parts[12]?.trim() || '';

        // Validações
        if (!email || !emailRegex.test(email)) {
          invalidLines.push(`Linha ${index + 1}: Email inválido`);
          return;
        }
        if (!NOME) {
          invalidLines.push(`Linha ${index + 1}: NOME obrigatório`);
          return;
        }
        if (!CPD) {
          invalidLines.push(`Linha ${index + 1}: CPD obrigatório`);
          return;
        }
        if (!CPF_CNPJ) {
          invalidLines.push(`Linha ${index + 1}: CPF/CNPJ obrigatório`);
          return;
        }
        if (!LINK_PAGAMENTO) {
          invalidLines.push(`Linha ${index + 1}: LINK_PAGAMENTO obrigatório`);
          return;
        }

        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
          }
          return dateStr;
        };

        const attributes = {
          NOME, SOBRENOME, CPD, CPF_CNPJ, LINK_PAGAMENTO,
          RAZAO_SOCIAL, EMPRESA, NOTA_FISCAL, TITULO,
          EMISSAO: formatDate(EMISSAO),
          VENCIMENTO: formatDate(VENCIMENTO),
          VALOR_ORIGINAL: this.formatCurrencyValue(VALOR_ORIGINAL)
        };

        Object.keys(attributes).forEach(key => {
          if (attributes[key] === '' || attributes[key] === null) {
            delete attributes[key];
          }
        });

        contacts.push({ email, attributes });
      });

      if (invalidLines.length > 0) {
        this.modalSuccessMessage = '';
        this.modalErrorMessage = invalidLines.slice(0, 5).join('\n') +
          (invalidLines.length > 5 ? `\n... e mais ${invalidLines.length - 5} erros` : '');
        return;
      }

      if (contacts.length === 0) {
        this.modalSuccessMessage = '';
        this.modalErrorMessage = 'Nenhum contato válido encontrado';
        return;
      }

      this.modalSuccessMessage = `✅ ${contacts.length} contatos validados! Enviando...`;
      this.addingBulkContacts = true;

      try {
        const startTime = Date.now();

        // ✅ NOVA ROTA: Bulk no banco interno
        const response = await fetch(`/emails/api/contatos/lists/${this.selectedListForBulkContacts}/contacts/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ contacts })
        });

        const result = await response.json();
        const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);

        if (result.success) {
          this.modalSuccessMessage = `✅ ${contacts.length} contatos salvos no banco interno em ${elapsedTime}s!`;

          setTimeout(() => {
            this.bulkContactEmails = '';
            this.selectedListForBulkContacts = null;
            this.modalSuccessMessage = '';
            this.showBulkAddContactModal = false;
          }, 3000);

          await this.loadContactLists();
        } else {
          this.modalSuccessMessage = '';
          this.modalErrorMessage = result.message || 'Erro ao adicionar contatos';
        }

      } catch (error) {
        console.error('❌ Erro ao adicionar contatos em massa:', error);
        this.modalSuccessMessage = '';
        this.modalErrorMessage = `Erro: ${error.message}`;
      } finally {
        this.addingBulkContacts = false;
      }
    },

    closeAddContactModal() {
      this.showAddContactModal = false;
      this.modalErrorMessage = '';
      this.modalSuccessMessage = '';
      this.newContactEmail = '';
      this.newContactFirstName = '';
      this.newContactLastName = '';
      this.newContactCpf = '';
      this.newContactCpd = '';
      this.newContactRazao = '';
      this.newContactEmpresa = '';
      this.newContactNotaFiscal = '';
      this.newContactTitulo = '';
      this.newContactEmissao = '';
      this.newContactVencimento = '';
      this.newContactValorOriginal = '';
      this.newContactLink = '';
      this.selectedListForContact = null;
    },

    closeBulkAddContactModal() {
      this.showBulkAddContactModal = false;
      this.modalErrorMessage = '';
      this.modalSuccessMessage = '';
      this.bulkContactEmails = '';
      this.selectedListForBulkContacts = null;
    },

    createNewCampaign() {
      this.resetForm();
      this.currentStep = 1;
      this.currentTemplatePage = 1;
      this.showCampaignForm = true;

      this.campaignForm.senderName = this.configEmail.senderName;
      this.campaignForm.replyTo = 'negociacao@wurth.com.br';

      this.loadTemplates();
      this.loadContactLists();
      // this.loadSegments();
    },

    async editCampaign(campaignId) {
      this.loading = true;

      try {
        const response = await fetch(`/emails/api/campanhas/${campaignId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);

        const result = await response.json();

        if (result.success) {
          const campaign = result.data;
          this.currentCampaignId = campaignId;

          // Carrega templates, listas e segmentos ANTES de abrir o form
          await Promise.all([
            this.loadTemplates(),
            this.loadContactLists(),
            // this.loadSegments()
          ]);

          // Preenche o formulário com os dados da campanha
          this.campaignForm = {
            name: campaign.nome || '',
            previewText: campaign.previewText || '',
            senderName: campaign.sender?.name || this.configEmail.senderName || '',
            senderEmail: campaign.sender?.email || this.configEmail.senderEmail || '',
            replyTo: campaign.replyTo || '',
            templateId: campaign.templateId || null,
            listIds: campaign.recipients?.lists || [],
            segmentIds: campaign.recipients?.segments || [],
            scheduledAt: campaign.scheduledAt || ''
          };

          this.currentStep = 1; // Sempre começa no passo 1
          this.sendType = 'now';
          this.errors = {};
          this.showCampaignForm = true;

          console.log('✅ Campanha carregada para edição:', this.campaignForm);
        } else {
          throw new Error(result.message || 'Erro ao carregar campanha');
        }
      } catch (error) {
        console.error('❌ Erro ao editar campanha:', error);
        this.errorMessage = `Erro ao carregar campanha: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },

    async closeCampaignForm() {
      this.showCampaignForm = false;
      this.currentCampaignId = null;
      this.currentTemplatePage = 1;
      this.resetForm();
      this.loadCampaignsOnce();
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.loadCampaignsArchived();
    },

    resetForm() {
      this.errors = {};
      this.currentCampaignId = null;
      this.campaignForm = {
        name: '',
        // subject: '',
        previewText: '',
        senderName: '',
        senderEmail: '',
        replyTo: 'negociacao@wurth.com.br',
        templateId: null,
        listIds: [],
        segmentIds: [],
        scheduledAt: ''
      };
      this.sendType = 'now';
      this.recipientType = 'lists';
      this.currentStep = 1;
    },

    nextStep() {
      if (this.validateStep()) {
        this.currentStep++;
        this.errors = {}; // limpa erros ao avançar
        window.scrollTo(0, 0);
      }
    },

    async previousStep() {
      const vindoDoPasso4 = this.currentStep === 4;

      this.currentStep--;
      this.errors = {};

      if (vindoDoPasso4 && this.recipientType === 'lists') {
        await this.recarregarListasESelecao();
      }
    },

    async recarregarListasESelecao() {
      await this.loadContactLists();

      const idsAtivos = this.contactLists.map(l => l.id);
      const removidas = this.campaignForm.listIds.filter(id => !idsAtivos.includes(id));

      if (removidas.length > 0) {
        this.campaignForm.listIds = this.campaignForm.listIds.filter(id => idsAtivos.includes(id));

        Swal.fire({
          type: 'warning',
          title: 'Lista indisponível',
          text: 'A lista selecionada já foi usada em outro envio e foi removida da seleção. Escolha outra lista.',
          confirmButtonColor: '#f59e0b'
        });
      }
    },

    validateStep() {
      this.errors = {}; // limpa antes de validar

      switch (this.currentStep) {
        // Step 1: Informações básicas
        case 1:
          if (!this.campaignForm.name?.trim()) {
            this.errors.name = "O nome da campanha é obrigatório.";
          }
          // if (!this.campaignForm.subject?.trim()) {
          //   this.errors.subject = "O assunto do email é obrigatório.";
          // }
          if (!this.configEmail.senderName?.trim()) {
            this.errors.senderName = "O nome do remetente é obrigatório.";
          }
          if (!this.configEmail.senderEmail?.trim()) {
            this.errors.senderEmail = "O email do remetente é obrigatório.";
          } else if (!this.validateEmail(this.configEmail.senderEmail)) {
            this.errors.senderEmail = "Informe um email de remetente válido.";
          }

          if (this.campaignForm.replyTo && !this.validateEmail(this.campaignForm.replyTo)) {
            this.errors.replyTo = "O email de resposta não é válido.";
          }

          return Object.keys(this.errors).length === 0;

        // Step 2: Template
        case 2:
          if (!this.campaignForm.templateId) {
            this.errors.templateId = "Selecione um template para continuar.";
          }
          return Object.keys(this.errors).length === 0;

        // Step 3: Contatos
        case 3:
          if (
            this.campaignForm.listIds.length === 0 &&
            this.campaignForm.segmentIds.length === 0
          ) {
            this.errors.recipients = "Selecione ao menos uma lista ou segmento de contatos.";
          }
          return Object.keys(this.errors).length === 0;

        // Step 4: Agendamento
        case 4:
          if (this.sendType === "schedule") {
            if (!this.campaignForm.scheduledAt) {
              this.errors.scheduledAt = "Informe uma data e hora para o agendamento.";
            } else {
              const selectedDate = new Date(this.campaignForm.scheduledAt);
              if (selectedDate <= new Date()) {
                this.errors.scheduledAt = "A data de agendamento deve ser no futuro.";
              }
            }
          }
          return Object.keys(this.errors).length === 0;

        default:
          return true;
      }
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    validateCurrentStep() {
      if (this.currentStep === 1) {
        if (!this.campaignForm.name ||
          !this.campaignForm.senderName || !this.campaignForm.senderEmail) {
          this.errorMessage = 'Preencha todos os campos obrigatórios';
          setTimeout(() => this.errorMessage = '', 5000);
          return false;
        }
      } else if (this.currentStep === 2) {
        if (!this.campaignForm.templateId) {
          this.errorMessage = 'Selecione um template';
          setTimeout(() => this.errorMessage = '', 5000);
          return false;
        }
      } else if (this.currentStep === 3) {
        if (this.campaignForm.listIds.length === 0 && this.campaignForm.segmentIds.length === 0) {
          this.errorMessage = 'Selecione pelo menos uma lista ou segmento';
          setTimeout(() => this.errorMessage = '', 5000);
          return false;
        }
      }
      return true;
    },

    selectTemplate(templateId) {
      this.campaignForm.templateId = templateId;
    },

    toggleList(listId) {
      const index = this.campaignForm.listIds.indexOf(listId);
      if (index > -1) {
        this.campaignForm.listIds.splice(index, 1);
      } else {
        this.campaignForm.listIds.push(listId);
      }
    },

    toggleSegment(segmentId) {
      const index = this.campaignForm.segmentIds.indexOf(segmentId);
      if (index > -1) {
        this.campaignForm.segmentIds.splice(index, 1);
      } else {
        this.campaignForm.segmentIds.push(segmentId);
      }
    },

    getTotalRecipients() {
      let total = 0;

      this.campaignForm.listIds.forEach(listId => {
        const list = this.contactLists.find(l => l.id === listId);
        if (list) total += list.uniqueSubscribers || 0;
      });

      return total;
    },

    getTemplateName(templateId) {
      const template = this.activeTemplates.find(t => t.id === templateId);
      return template ? template.name : 'N/A';
    },

    getSendButtonText() {
      if (this.sending) return 'Enviando...';
      if (this.sendType === 'now') return 'Enviar Agora';
      if (this.sendType === 'schedule') return 'Agendar Envio';
      return 'Salvar Rascunho';
    },

    async saveCampaign() {
      if (!this.validateCurrentStep()) return;

      if (this.sendType === 'schedule' && !this.campaignForm.scheduledAt) {
        Swal.fire({
          type: 'warning',
          title: 'Data obrigatória',
          text: 'Selecione data e hora para agendamento'
        });
        return;
      }

      this.sending = true;
      this.errorMessage = '';

      try {
        let scheduledAt = null;
        if (this.sendType === 'schedule' && this.campaignForm.scheduledAt) {
          scheduledAt = new Date(this.campaignForm.scheduledAt).toISOString();
        }

        const payload = {
          name: this.campaignForm.name,
          previewText: this.campaignForm.previewText || null,
          templateId: this.campaignForm.templateId,
          listIds: this.campaignForm.listIds,
          sender: {
            name: this.campaignForm.senderName,
            email: this.configEmail.senderEmail
          },
          replyTo: this.campaignForm.replyTo || null,
          scheduledAt: scheduledAt
        };

        const url = this.currentCampaignId
          ? `/emails/api/campanhas/${this.currentCampaignId}`
          : '/emails/api/campanhas';

        const method = this.currentCampaignId ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Erro ao salvar campanha');
        }

        const campanhaId = result.data.id;
        this.currentCampaignId = campanhaId;

        if (this.sendType === 'now') {
          const sendResponse = await fetch(`/emails/api/campanhas/${campanhaId}/send`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            }
          });

          const sendResult = await sendResponse.json();

          if (!sendResult.success) {
            if (sendResult.code === 'LISTA_JA_ENVIADA') {
              this.sending = false;

              await Swal.fire({
                type: 'error',
                title: 'Lista já utilizada',
                text: sendResult.message,
                confirmButtonText: 'Escolher outra lista',
                confirmButtonColor: '#ef4444'
              });

              this.campaignForm.listIds = [];
              this.currentStep = 3;
              await this.loadContactLists();
              return;
            }

            throw new Error(sendResult.message || 'Erro ao enviar campanha');
          }

          this.successMessage = '✅ Campanha sendo enviada em background!';

          setTimeout(() => {
            this.showCampaignForm = false;
            this.resetForm();
            this.loadCampaigns();
            this.loadEmailQuota();
            this.sending = false;
          }, 2000);
        }
        else if (this.sendType === 'schedule') {
          this.successMessage = `📅 Campanha agendada para ${new Date(scheduledAt).toLocaleString('pt-BR')}`;

          setTimeout(() => {
            this.showCampaignForm = false;
            this.resetForm();
            this.loadCampaigns();
            this.loadEmailQuota();
            this.sending = false;
          }, 2000);
        }
        else {
          this.successMessage = '✅ Campanha salva como rascunho!';

          setTimeout(() => {
            this.showCampaignForm = false;
            this.resetForm();
            this.loadCampaigns();
            this.loadEmailQuota();
            this.sending = false;
          }, 2000);
        }

      } catch (error) {
        console.error('❌ Erro:', error);
        this.sending = false;

        Swal.fire({
          type: 'error',
          title: 'Não foi possível enviar',
          text: error.message || 'Erro ao salvar campanha',
          confirmButtonColor: '#ef4444'
        });
      }
    },

    async sendCampaignNow(campaignId) {
      try {
        const response = await fetch(`/emails/api/campanhas/${campaignId}/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || `Erro HTTP: ${response.status}`);
        }

        this.successMessage = 'Campanha enviada com sucesso!';
        setTimeout(() => {
          this.closeCampaignForm();
        }, 2000);
      } catch (error) {
        console.error('Erro ao enviar campanha:', error);
        Swal.fire({
          type: 'error',
          title: 'Não foi possível enviar',
          text: error.message || 'Erro ao enviar campanha',
          confirmButtonColor: '#ef4444'
        });
      }
    },

    async viewDetailedMetrics(campaign) {
      this.loadingMetrics = true;
      this.showMetricsModal = true;
      this.selectedCampaignMetrics = null;

      try {
        const response = await fetch(`/emails/api/campanhas/${campaign.id}/metricas`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          const data = result.data;

          this.selectedCampaignMetrics = {
            id: campaign.id,
            name: data.campanha.nome,
            status: data.campanha.status,

            globalStats: {
              total_esperado: data.metricas.total_esperado,
              sent: data.metricas.total_enviados,
              delivered: data.metricas.total_entregues,
              errors: data.metricas.total_erros,
              uniqueViews: data.metricas.aberturas_unicas,
              totalViews: data.metricas.aberturas_totais,
              uniqueClicks: data.metricas.cliques_unicos,
              totalClicks: data.metricas.cliques_totais,
            },

            progresso: data.progresso,
            taxas: data.taxas,

            // 🔥 NOVO: ERROS DETALHADOS
            erros: data.erros,

            hasData: data.metricas.total_enviados > 0
          };

          // 🔥 INICIA POLLING APENAS SE MODAL ESTIVER ABERTO E STATUS = SENDING
          if (data.campanha.status === 'sending') {
            this.startMetricsPolling(campaign.id);
          }

        } else {
          throw new Error(result.message || 'Nenhuma métrica disponível');
        }
      } catch (error) {
        console.error('❌ Erro ao carregar métricas:', error);
        this.errorMessage = `Erro ao carregar métricas: ${error.message}`;
        this.showMetricsModal = false;
      } finally {
        this.loadingMetrics = false;
      }
    },

    /**
 * 🔄 Inicia polling de métricas em tempo real
 */
    startMetricsPolling(campaignId) {
      // Limpa polling anterior
      this.stopMetricsPolling();

      console.log('🔄 Iniciando polling de métricas (modal aberto)...', campaignId);

      this.metricsPolling = setInterval(async () => {
        // 🔥 SÓ CONTINUA SE O MODAL AINDA ESTIVER ABERTO
        if (!this.showMetricsModal) {
          console.log('⚠️ Modal fechado, parando polling');
          this.stopMetricsPolling();
          return;
        }

        try {
          const response = await fetch(`/emails/api/campanhas/${campaignId}/metricas-realtime`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            }
          });

          if (!response.ok) throw new Error('Erro no polling');

          const result = await response.json();

          if (result.success) {
            // Atualiza métricas em tempo real
            if (this.selectedCampaignMetrics) {
              this.selectedCampaignMetrics.globalStats.sent = result.enviados;
              this.selectedCampaignMetrics.globalStats.delivered = result.entregues;
              this.selectedCampaignMetrics.globalStats.uniqueViews = result.abertos;
              this.selectedCampaignMetrics.globalStats.uniqueClicks = result.clicados;
              this.selectedCampaignMetrics.globalStats.errors = result.erros;
              this.selectedCampaignMetrics.status = result.status;

              // 🔥 Atualiza progresso
              if (this.selectedCampaignMetrics.progresso) {
                const totalEsperado = this.selectedCampaignMetrics.globalStats.total_esperado;
                this.selectedCampaignMetrics.progresso.enviados = result.enviados;
                this.selectedCampaignMetrics.progresso.percentual = result.enviados > 0 ? 100 : 0;
                this.selectedCampaignMetrics.progresso.faltam = 0;
              }
            }

            // Para o polling se não estiver mais enviando
            if (result.status !== 'sending') {
              console.log('✅ Envio finalizado, parando polling');
              this.stopMetricsPolling();
              await this.refreshMetrics(campaignId);

              // 🔥 Recarrega lista de campanhas
              await this.loadCampaignsOnce();
            }
          }
        } catch (error) {
          console.error('❌ Erro no polling:', error);
        }
      }, this.metricsRefreshInterval);
    },

    /**
     * 🛑 Para o polling
     */
    stopMetricsPolling() {
      if (this.metricsPolling) {
        clearInterval(this.metricsPolling);
        this.metricsPolling = null;
        console.log('🛑 Polling parado');
      }
    },


    /**
     * 🔄 Recarrega métricas completas
     */
    async refreshMetrics(campaignId) {
      try {
        const response = await fetch(`/emails/api/campanhas/${campaignId}/metricas`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) return;

        const result = await response.json();

        if (result.success && this.selectedCampaignMetrics) {
          const data = result.data;

          this.selectedCampaignMetrics.globalStats = {
            total_esperado: data.metricas.total_esperado,
            sent: data.metricas.total_enviados,
            delivered: data.metricas.total_entregues,
            errors: data.metricas.total_erros,
            uniqueViews: data.metricas.aberturas_unicas,
            totalViews: data.metricas.aberturas_totais,
            uniqueClicks: data.metricas.cliques_unicos,
            totalClicks: data.metricas.cliques_totais,
          };

          this.selectedCampaignMetrics.progresso = data.progresso;
          this.selectedCampaignMetrics.taxas = data.taxas;
          this.selectedCampaignMetrics.status = data.campanha.status;
        }
      } catch (error) {
        console.error('❌ Erro ao recarregar métricas:', error);
      }
    },

    async loadTimeline(campaignId) {
      this.loadingTimeline = true;
      this.showTimeline = true;
      this.timelineData = [];
      this.expandedTimelineEmails = [];
      this.timelineCurrentPage = 1;

      try {
        const response = await fetch(`/emails/api/campanhas/${campaignId}/timeline`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) throw new Error('Erro ao carregar timeline');

        const result = await response.json();

        if (result.success) {
          this.timelineData = result.data;
        }
      } catch (error) {
        console.error('❌ Erro ao carregar timeline:', error);
        this.errorMessage = 'Erro ao carregar linha do tempo';
      } finally {
        this.loadingTimeline = false;
      }
    },

    /**
     * 🔽 Expande/colapsa eventos de um email
     */
    toggleTimelineEmail(email) {
      const index = this.expandedTimelineEmails.indexOf(email);
      if (index > -1) {
        this.expandedTimelineEmails.splice(index, 1);
      } else {
        this.expandedTimelineEmails.push(email);
      }
    },

    /**
     * ⬅️ Página anterior
     */
    timelinePreviousPage() {
      if (this.timelineCurrentPage > 1) {
        this.timelineCurrentPage--;
      }
    },

    /**
     * ➡️ Próxima página
     */
    timelineNextPage() {
      if (this.timelineCurrentPage < this.timelineTotalPages) {
        this.timelineCurrentPage++;
      }
    },

    /**
     * 📄 Ir para página específica
     */
    timelineGoToPage(page) {
      this.timelineCurrentPage = page;
    },

    /**
     * 🔗 Trunca URL
     */
    truncateUrl(url, maxLength) {
      if (!url || url.length <= maxLength) return url;
      return url.substring(0, maxLength) + '...';
    },

    closeMetricsModal() {
      this.showMetricsModal = false;
      this.stopMetricsPolling(); // 🔥 PARA O POLLING
      this.selectedCampaignMetrics = null;
      this.showTimeline = false;
      this.timelineData = [];
    },

    openSuspendModal(campaignId, campaignName) {
      this.campaignToSuspend = campaignId;
      this.campaignToSuspendName = campaignName;
      this.showSuspendModal = true;
    },

    async confirmSuspend() {
      if (!this.campaignToSuspend) return;

      this.suspending = true;

      try {
        const response = await fetch(`/api/brevo/campaigns/${this.campaignToSuspend}/suspend`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Campanha suspensa com sucesso!';
          setTimeout(() => this.successMessage = '', 5000);
          this.showSuspendModal = false;
          this.campaignToSuspend = null;
          this.campaignToSuspendName = '';
          await this.loadCampaignsOnce();
          await new Promise(resolve => setTimeout(resolve, 2000));
          await this.loadCampaignsArchived();
        }
      } catch (error) {
        console.error('Erro ao suspender campanha:', error);
        this.errorMessage = `Erro ao suspender campanha: ${error.message}`;
        this.showSuspendModal = false;
      } finally {
        this.suspending = false;
      }
    },

    openArchiveModal(campaignId, campaignName) {
      this.campaignToArchive = campaignId;
      this.campaignToArchiveName = campaignName;
      this.showArchiveModal = true;
    },

    async confirmArchive() {
      if (!this.campaignToArchive) return;

      this.archiving = true;

      try {
        // 🔥 NOVA ROTA: Banco interno
        const response = await fetch(`/emails/api/campanhas/${this.campaignToArchive}/arquivar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Campanha arquivada com sucesso!';
          setTimeout(() => this.successMessage = '', 5000);
          this.showArchiveModal = false;
          this.campaignToArchive = null;
          this.campaignToArchiveName = '';
          await this.loadCampaignsOnce();
          await new Promise(resolve => setTimeout(resolve, 2000));
          await this.loadCampaignsArchived();
        }
      } catch (error) {
        console.error('Erro ao arquivar campanha:', error);
        this.errorMessage = `Erro ao arquivar campanha: ${error.message}`;
        this.showArchiveModal = false;
      } finally {
        this.archiving = false;
      }
    },

    openUnarchiveModal(campaignId, campaignName) {
      this.campaignToUnarchive = campaignId;
      this.campaignToUnarchiveName = campaignName;
      this.showUnarchiveModal = true;
    },

    async confirmUnarchive() {
      if (!this.campaignToUnarchive) return;

      this.unarchiving = true;

      try {
        // 🔥 NOVA ROTA: Banco interno
        const response = await fetch(`/emails/api/campanhas/${this.campaignToUnarchive}/desarquivar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Campanha desarquivada com sucesso!';
          setTimeout(() => this.successMessage = '', 5000);
          this.showUnarchiveModal = false;
          this.campaignToUnarchive = null;
          this.campaignToUnarchiveName = '';
          await this.loadCampaignsOnce();
          await new Promise(resolve => setTimeout(resolve, 2000));
          await this.loadCampaignsArchived();
        }
      } catch (error) {
        console.error('Erro ao desarquivar campanha:', error);
        this.errorMessage = `Erro ao desarquivar campanha: ${error.message}`;
        this.showUnarchiveModal = false;
      } finally {
        this.unarchiving = false;
      }
    },

    openDeleteModal(campaignId, campaignName) {
      this.campaignToDelete = campaignId;
      this.campaignToDeleteName = campaignName;
      this.showDeleteModal = true;
    },

    async confirmDelete() {
      if (!this.campaignToDelete) return;

      this.deleting = true;

      try {
        // 🔥 NOVA ROTA: Banco interno
        const response = await fetch(`/emails/api/campanhas/${this.campaignToDelete}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Campanha excluída com sucesso!';
          setTimeout(() => this.successMessage = '', 5000);
          this.showDeleteModal = false;
          this.campaignToDelete = null;
          this.campaignToDeleteName = '';
          await this.loadCampaignsOnce();
          await new Promise(resolve => setTimeout(resolve, 2000));
          await this.loadCampaignsArchived();
        }
      } catch (error) {
        console.error('Erro ao excluir campanha:', error);
        this.errorMessage = `Erro ao excluir campanha: ${error.message}`;
        this.showDeleteModal = false;
      } finally {
        this.deleting = false;
      }
    },
    getStatusClass(status) {
      const classes = {
        'sent': 'status-sent',
        'sending': 'status-process',
        'failed': 'status-failed',
        'scheduled': 'status-queued',
        'draft': 'status-draft',
        'queued': 'status-queued',
        'suspended': 'status-suspended',
        'archived': 'status-archived'
      };
      return classes[status] || 'status-default';
    },

    getStatusIcon(status) {
      const icons = {
        'sent': 'fa-solid fa-check-circle',
        'in_process': 'fa-solid fa-clock',
        'scheduled': 'fa-solid fa-clock',
        'draft': 'fa-solid fa-file-lines',
        'queued': 'fa-solid fa-hourglass-half',
        'suspended': 'fa-solid fa-pause-circle',
        'archived': 'fa-solid fa-archive'
      };
      return icons[status] || 'fa-solid fa-circle';
    },

    getStatusLabel(status) {
      const labels = {
        'sent': 'Enviada',
        'sending': 'Em andamento',
        'failed': 'Falha',
        'scheduled': 'Agendada',
        'draft': 'Rascunho',
        'queued': 'Agendada',
        'suspended': 'Suspensa',
        'archived': 'Arquivada'
      };
      return labels[status] || status;
    },

    getEmptyStateMessage() {
      const messages = {
        'all': 'Nenhuma campanha encontrada',
        'sent': 'Nenhuma campanha enviada',
        'scheduled': 'Nenhuma campanha agendada',
        'draft': 'Nenhum rascunho encontrado',
        'archived': 'Nenhuma campanha arquivada'
      };
      return messages[this.filterStatus] || 'Nenhuma campanha encontrada';
    },

    formatNumber(num) {
      if (!num && num !== 0) return '0';
      return new Intl.NumberFormat('pt-BR').format(num);
    },

    calculateRate(part, total) {
      if (!total || total === 0) return '0%';
      const rate = (part / total) * 100;
      return `${rate.toFixed(1)}%`;
    },

    formatDate(dateString) {
      if (!dateString) return '-';

      if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        return dateString;
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Data não disponível';

        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return 'Data não disponível';
      }
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';

      try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return dateTimeString;
      }
    },

    formatDateBR(dateString) {
      if (!dateString) return '';

      try {
        // Divide YYYY-MM-DD
        const [year, month, day] = dateString.split('-');

        // Cria data em horário local (sem conversão de fuso)
        const date = new Date(year, month - 1, day);

        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (error) {
        console.error('Erro ao formatar data:', error);
        return dateString;
      }
    },

    truncateUrl(url) {
      if (!url) return '';
      if (url.length <= 50) return url;
      return url.substring(0, 47) + '...';
    }
  },
  watch: {
    filterStatus() {
      this.resetPagination();
    }
  },
};
</script>

<style scoped>
.quota-badge {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  box-shadow: 0 3px 12px rgba(14, 165, 233, 0.15);
  transition: all 0.3s ease;
  max-width: 360px;
  margin: 0 auto;
}

.quota-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 16px rgba(14, 165, 233, 0.25);
}

.quota-badge-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 22px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.quota-badge-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 0;
}

.quota-badge-header {
  font-size: 10px;
  font-weight: 700;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  line-height: 1;
  white-space: nowrap;
}

.quota-badge-values {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.quota-badge-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.quota-badge-label {
  font-size: 8px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1;
  white-space: nowrap;
}

.quota-badge-number {
  font-size: 17px;
  font-weight: 700;
  font-family: 'Segoe UI', Arial, sans-serif;
  line-height: 1;
  white-space: nowrap;
}

.quota-badge-remaining {
  color: #0c4a6e;
}

.quota-badge-total {
  color: #475569;
}

.quota-badge-separator {
  font-size: 20px;
  font-weight: 300;
  color: #94a3b8;
  margin: 0 4px;
  line-height: 1;
  padding-bottom: 2px;
}

.quota-badge-progress {
  width: 100%;
  height: 6px;
  background: #e0f2fe;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  margin-top: 3px;
}

.quota-badge-progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease, background 0.3s ease;
}

.quota-badge-progress-bar.quota-good {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.quota-badge-progress-bar.quota-warning {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.quota-badge-progress-bar.quota-danger {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  animation: quota-pulse-danger 2s ease-in-out infinite;
}

/* 🆕 FOOTER COM PORCENTAGEM E DATA DE RENOVAÇÃO */
.quota-badge-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.quota-badge-percentage {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  line-height: 1;
  white-space: nowrap;
}

.quota-badge-renewal {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1;
  white-space: nowrap;
}

.quota-badge-renewal i {
  font-size: 9px;
  color: #0ea5e9;
}

@keyframes quota-pulse-danger {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

/* Responsivo */
@media (max-width: 768px) {
  .quota-badge {
    max-width: 300px;
    padding: 10px 16px;
    gap: 12px;
  }

  .quota-badge-icon {
    width: 38px;
    height: 38px;
    font-size: 18px;
  }

  .quota-badge-number {
    font-size: 15px;
  }

  .quota-badge-header {
    font-size: 9px;
  }

  .quota-badge-label {
    font-size: 7px;
  }

  .quota-badge-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .quota-badge-percentage {
    font-size: 8px;
  }

  .quota-badge-renewal {
    font-size: 7px;
  }
}

/* Modal de Seleção de Arquivos */
.import-info-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 4px solid #3b82f6;
  border-radius: 12px;
  margin-bottom: 25px;
}

.info-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-label {
  font-size: 12px;
  color: #1e40af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 18px;
  font-weight: 700;
  color: #1e3a8a;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e5e7eb;
}

.files-header h4 {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
}

.files-count {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.files-grid {
  display: grid;
  gap: 15px;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
}

.file-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.file-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.file-card.selected {
  border-color: #10b981;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.file-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.file-card.selected .file-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-meta {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.file-size,
.file-date {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.selection-indicator {
  width: 35px;
  height: 35px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  animation: scaleIn 0.3s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }

  to {
    transform: scale(1);
  }
}

.empty-note {
  margin-top: 15px;
  padding: 12px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  color: #92400e;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Tooltip para métricas */
.metric-label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tooltip-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  cursor: help;
  color: #6b7280;
  transition: color 0.2s;
}

.tooltip-icon:hover {
  color: #3b82f6;
}

.tooltip-icon i {
  font-size: 12px;
}

/* Tooltip nativo aprimorado */
.tooltip-icon[title] {
  cursor: help;
}

/* Se quiser um tooltip customizado mais bonito */
.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip-custom {
  visibility: hidden;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #1f2937;
  color: white;
  text-align: center;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  width: 220px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.3s, visibility 0.3s;
}

.tooltip-custom::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #1f2937 transparent transparent transparent;
}

.tooltip-wrapper:hover .tooltip-custom {
  visibility: visible;
  opacity: 1;
}

.errors-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.summary-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.summary-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.summary-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

/* 🔍 FILTROS DE TIPO DE ERRO */
.error-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 30px;
}

.error-type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.error-type-card:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: translateY(-2px);
}

.error-type-card.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #2563eb;
  color: white;
}

.error-type-card.active .error-type-label,
.error-type-card.active .error-type-count {
  color: white;
}

.error-type-card i {
  font-size: 24px;
}

.error-type-card.active i {
  color: white !important;
}

.error-type-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.error-type-count {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

/* 📋 LISTA DE ERROS */
.errors-list-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.errors-list-header {
  margin-bottom: 20px;
}

.errors-list-header h4 {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  color: #111827;
  margin: 0;
}

.filter-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-left: auto;
}

.errors-table-wrapper {
  overflow-x: auto;
  margin-bottom: 20px;
}

.errors-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  /* 🔥 Mudado para auto para melhor distribuição */
}

.errors-table th {
  background: #f9fafb;
  padding: 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}

/* 🔥 Define larguras específicas para cada coluna */
.errors-table th:nth-child(1) {
  width: 50px;
}

/* Ícone */
.errors-table th:nth-child(2) {
  width: 200px;
}

/* Email */
.errors-table th:nth-child(3) {
  width: 80px;
}

/* CPD */
.errors-table th:nth-child(4) {
  width: 80px;
}

/* Tipo */
.errors-table th:nth-child(5) {
  width: auto;
  min-width: 350px;
}

/* 🔥 Mensagem (maior) */
.errors-table th:nth-child(6) {
  width: 150px;
}

/* Data/Hora */

.errors-table td {
  text-align: center;
  padding: 14px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.errors-table tbody tr {
  transition: background-color 0.2s;
}

.errors-table tbody tr:hover {
  background-color: #f9fafb;
}

.email-address {
  font-family: monospace;
  color: #374151;
  font-size: 13px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cpd-text {
  display: inline-block;
  font-family: monospace;
  color: white;
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.error-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  word-wrap: break-word;
  white-space: normal;
  display: block;
}

.error-date {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
}

/* ========================================
   📈 TIMELINE VISUAL - COM LINHA CONECTORA
   ======================================== */

.timeline-container {
  margin-top: 15px;
}

/* Info Banner */
.timeline-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 600;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
}

.timeline-info i {
  color: #3b82f6;
  font-size: 16px;
}

/* ========================================
   EMAIL ACCORDION
   ======================================== */
.timeline-accordion {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.timeline-email-group {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.timeline-email-group:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

/* Header do Email - MELHORADO */
.timeline-email-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.timeline-email-header:hover {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
}

.timeline-email-avatar {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.timeline-email-avatar i {
  color: white;
  font-size: 18px;
}

.timeline-email-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.timeline-email-address {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.timeline-email-meta {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.timeline-chevron {
  color: #3b82f6;
  font-size: 16px;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.timeline-chevron.rotated {
  transform: rotate(180deg);
}

/* ========================================
   EVENTOS COM LINHA VISUAL
   ======================================== */
.timeline-events-wrapper {
  padding: 25px 20px;
  background: #fafafa;
}

.timeline-events-list {
  position: relative;
}

/* Item de Evento */
.timeline-event-item {
  position: relative;
  display: flex;
  gap: 16px;
  padding-left: 12px;
  margin-bottom: 20px;
}

.timeline-event-item:last-child {
  margin-bottom: 0;
}

/* Linha Vertical Conectora */
.timeline-line {
  position: absolute;
  left: 28px;
  top: 48px;
  width: 3px;
  height: calc(100% + 20px);
  background: linear-gradient(180deg, #e5e7eb 0%, #d1d5db 100%);
  border-radius: 2px;
}

/* Dot do Evento */
.timeline-event-dot {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  font-size: 16px;
  box-shadow: 0 0 0 4px #fafafa, 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 2;
  transition: all 0.3s ease;
}

.timeline-event-item:hover .timeline-event-dot {
  transform: scale(1.1);
  box-shadow: 0 0 0 6px #fafafa, 0 4px 12px rgba(0, 0, 0, 0.2);
}

.dot-blue {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.dot-green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.dot-orange {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.dot-purple {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.dot-red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

/* Card do Evento */
.timeline-event-card {
  flex: 1;
  background: white;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-left: 4px solid transparent;
  transition: all 0.3s ease;
}

.timeline-event-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateX(4px);
}

.card-blue {
  border-left-color: #3b82f6;
}

.card-green {
  border-left-color: #10b981;
}

.card-orange {
  border-left-color: #f59e0b;
}

.card-purple {
  border-left-color: #8b5cf6;
}

.card-red {
  border-left-color: #ef4444;
}

/* Header do Card */
.event-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.event-type {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.event-time {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}

/* Detalhes do Evento */
.event-detail {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.link-detail {
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
}

.link-detail i {
  color: #3b82f6;
}

.link-detail a {
  color: #1e40af;
  text-decoration: none;
  font-weight: 600;
  word-break: break-all;
}

.link-detail a:hover {
  text-decoration: underline;
  color: #1e3a8a;
}

.info-detail {
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
  color: #92400e;
  font-weight: 500;
}

.info-detail i {
  color: #f59e0b;
}

.error-detail {
  background: #fee2e2;
  border-left: 3px solid #ef4444;
  color: #991b1b;
  font-weight: 600;
}

.error-detail i {
  color: #ef4444;
}

/* ========================================
   PAGINAÇÃO
   ======================================== */
.timeline-pagination {
  margin-top: 25px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.pagination-info {
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 15px;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pagination-pages {
  display: flex;
  gap: 6px;
  align-items: center;
}

.pagination-pages span {
  color: #6b7280;
  font-weight: 700;
}

/* ========================================
   EMPTY STATE
   ======================================== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  border: 2px dashed #e5e7eb;
}

.empty-state i {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 15px;
}

.empty-state p {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
}

/* ========================================
   ANIMAÇÕES
   ======================================== */
.slide-fade-enter-active {
  transition: all 0.4s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ========================================
   RESPONSIVO
   ======================================== */
@media (max-width: 768px) {
  .timeline-email-avatar {
    width: 36px;
    height: 36px;
  }

  .timeline-email-avatar i {
    font-size: 16px;
  }

  .timeline-email-address {
    font-size: 13px;
  }

  .timeline-event-dot {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .timeline-line {
    left: 24px;
  }

  .event-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .pagination-controls {
    flex-direction: column;
  }
}

.events-pagination-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f0f9ff;
  border-radius: 6px;
  font-size: 12px;
  color: #0c4a6e;
  font-weight: 600;
  margin-bottom: 15px;
  border-left: 3px solid #0ea5e9;
}

.events-pagination-info i {
  color: #0ea5e9;
}

.events-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e5e7eb;
}

.events-pagination-pages {
  display: flex;
  gap: 4px;
}

.btn-xs {
  padding: 4px 10px;
  font-size: 11px;
  border-radius: 4px;
  font-weight: 600;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

/* .btn-xs:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
} */

.btn-xs.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-xs.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-xs:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Texto pequeno para datas */
.text-small {
  font-size: 11px;
  color: #6b7280;
}

.links-table {
  overflow-x: auto;
  margin-bottom: 20px;
}

.links-table table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.links-table thead {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
}

.links-table th {
  padding: 12px 15px;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.links-table tbody tr {
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;
}

.links-table tbody tr:hover {
  background: #f9fafb;
}

.links-table td {
  padding: 12px 15px;
  font-size: 14px;
  color: #374151;
}

.link-url {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.link-url:hover {
  color: #2563eb;
  text-decoration: underline;
}

/* Resumo de Links */
.links-summary {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #1e3a8a;
}

.summary-item i {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
}

.summary-item strong {
  flex: 1;
}

.summary-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #1e3a8a;
  padding: 5px 12px;
  background: white;
  border-radius: 8px;
  min-width: 50px;
  text-align: center;
}

.summary-value.highlight {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.modal-contact .loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  border-radius: 12px;
}

.modal-contact .loading-content {
  text-align: center;
}

.modal-contact .spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.modal-contact .loading-text {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.modal-contact .loading-subtext {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* ========================================
   📊 MODAL DE MÉTRICAS - ESTILOS
   ======================================== */

/* Badge de Enviando */
.badge-sending {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 10px;
  animation: pulse 2s infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

/* ========================================
   🔥 BARRA DE PROGRESSO
   ======================================== */
.progress-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-label {
  font-size: 14px;
  font-weight: 600;
  color: #0c4a6e;
}

.progress-percentage {
  font-size: 18px;
  font-weight: 700;
  color: #0369a1;
}

.progress-bar-container {
  width: 100%;
  height: 24px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%);
  transition: width 0.5s ease;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.progress-bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.progress-info {
  display: flex;
  justify-content: space-around;
  gap: 15px;
  flex-wrap: wrap;
}

.progress-info span {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0c4a6e;
  font-weight: 500;
}

.progress-info i {
  color: #0284c7;
}

/* ========================================
   📊 MÉTRICAS PRINCIPAIS
   ======================================== */
.metrics-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.metric-box {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.metric-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.metric-box.success {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #86efac;
}

.metric-box.info {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-color: #93c5fd;
}

.metric-box i {
  font-size: 32px;
  color: #6b7280;
  margin-top: 5px;
}

.metric-box.success i {
  color: #10b981;
}

.metric-box.info i {
  color: #3b82f6;
}

.metric-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.metric-value {
  display: block;
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.metric-value.large {
  font-size: 36px;
}

.metric-sublabel {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin-left: 6px;
}

.metric-sublabel-extra {
  display: block;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 6px;
}

/* ========================================
   📈 DETALHES DAS MÉTRICAS
   ======================================== */
.metrics-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.metric-detail-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.metric-detail-card h5 {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f3f4f6;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-row span:first-child {
  font-size: 14px;
  color: #6b7280;
}

.metric-row span:last-child {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}

.badge-success {
  background: #dcfce7;
  color: #166534;
}

.badge-info {
  background: #dbeafe;
  color: #1e40af;
}

.badge-primary {
  background: #dbeafe;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.text-danger {
  color: #dc2626 !important;
}

/* ========================================
   📈 LINHA DO TEMPO
   ======================================== */
.timeline-toggle {
  text-align: center;
  margin: 25px 0;
}

.timeline-section {
  margin-top: 25px;
  padding-top: 25px;
  border-top: 2px solid #e5e7eb;
}

.timeline-section h5 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.timeline-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.timeline-item {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  position: relative;
}

.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 19px;
  top: 45px;
  width: 2px;
  height: calc(100% + 10px);
  background: #e5e7eb;
}

.timeline-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
  z-index: 1;
  position: relative;
}

.timeline-blue {
  background: #3b82f6;
}

.timeline-green {
  background: #10b981;
}

.timeline-orange {
  background: #f59e0b;
}

.timeline-purple {
  background: #8b5cf6;
}

.timeline-red {
  background: #ef4444;
}

.timeline-content {
  flex: 1;
  background: #f9fafb;
  padding: 12px 15px;
  border-radius: 8px;
  border-left: 3px solid #e5e7eb;
}

.timeline-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.timeline-title strong {
  font-size: 13px;
  color: #111827;
  font-weight: 700;
}

.timeline-email {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.timeline-time {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 4px;
}

.timeline-message {
  font-size: 12px;
  color: #dc2626;
  margin-top: 8px;
  padding: 6px 10px;
  background: #fee2e2;
  border-radius: 6px;
}

/* Scrollbar personalizada */
.timeline-list::-webkit-scrollbar {
  width: 6px;
}

.timeline-list::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.timeline-list::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 3px;
}

.timeline-list::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.modal-contact .success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  border-radius: 12px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-contact .success-content {
  text-align: center;
  animation: scaleIn 0.4s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ✅ Checkmark Container */
.modal-contact .success-checkmark {
  width: 100px;
  height: 100px;
  margin: 0 auto 24px;
  position: relative;
}

/* ✅ Circle Wrapper */
.modal-contact .checkmark-circle-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.modal-contact .checkmark-circle-svg {
  width: 100%;
  height: 100%;
}

/* ✅ Animated Circle */
.modal-contact .checkmark-circle {
  stroke-dasharray: 283;
  /* Circunferência: 2 * π * 45 */
  stroke-dashoffset: 283;
  animation: drawCircle 0.6s ease-out forwards;
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

/* ✅ Checkmark Icon Wrapper */
.modal-contact .checkmark-icon {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-contact .checkmark-icon svg {
  width: 60%;
  height: 60%;
}

/* ✅ Animated Checkmark */
.modal-contact .checkmark-check {
  stroke-dasharray: 80;
  stroke-dashoffset: 80;
  animation: drawCheck 0.4s ease-out 0.6s forwards;
}

@keyframes drawCheck {
  to {
    stroke-dashoffset: 0;
  }
}

/* ✅ Success Title */
.modal-contact .success-title {
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
  margin: 0 0 12px 0;
  animation: slideUpFade 0.5s ease 0.8s both;
}

/* ✅ Success Message */
.modal-contact .success-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  animation: slideUpFade 0.5s ease 1s both;
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ✅ Pulse effect no círculo quando completar */
.modal-contact .checkmark-circle-wrapper::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid #10b981;
  transform: translate(-50%, -50%) scale(0.9);
  opacity: 0;
  animation: pulse 0.6s ease-out 1s;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.9);
    opacity: 0.8;
  }

  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

.modal-contact .success-title {
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
  margin: 0 0 12px 0;
}

.modal-contact .success-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.modal-contact .form-control:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
  opacity: 0.6;
}

.modal-contact .collapsible-header.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.modal-contact .collapsible-header.disabled:hover {
  background: white;
}

.modal-contact {
  max-width: 600px;
  width: 100%;
}

.modal-contact .modal-body {
  padding: 20px 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Header do Modal de Contatos */
.modal-contact .modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
}

.modal-contact .modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.modal-contact .modal-header h3 i {
  color: #6366f1;
  font-size: 20px;
}

/* Seções do Formulário */
.modal-contact .form-section {
  margin-bottom: 24px;
}

.modal-contact .form-section:last-child {
  margin-bottom: 0;
}

/* Header das Seções */
.modal-contact .section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f3f4f6;
}

.modal-contact .section-header i {
  color: #6366f1;
  font-size: 18px;
}

.modal-contact .section-header span {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

/* ========================================
   COLLAPSIBLE (DROPDOWN) - NOVA ABORDAGEM
   ======================================== */

.modal-contact .collapsible-wrapper {
  margin-bottom: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.modal-contact .collapsible-header {
  padding: 14px 16px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;
  user-select: none;
}

.modal-contact .collapsible-header:hover {
  background: #f9fafb;
}

.modal-contact .collapsible-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.modal-contact .collapsible-title i {
  color: #6366f1;
  font-size: 18px;
}

.modal-contact .collapsible-title span:first-of-type {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.modal-contact .optional-badge {
  font-size: 11px;
  font-weight: 500;
  background: #f3f4f6;
  color: #6b7280;
  padding: 3px 10px;
  border-radius: 12px;
  margin-left: auto;
}

.modal-contact .collapse-arrow {
  color: #6b7280;
  font-size: 14px;
  transition: transform 0.3s ease;
}

.modal-contact .collapse-arrow.rotate {
  transform: rotate(180deg);
}

.modal-contact .collapsible-body {
  padding: 16px;
  background: white;
  border-top: 1px solid #e5e7eb;
}

/* Transição Vue */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Form Groups */
.modal-contact .form-group {
  margin-bottom: 16px;
}

.modal-contact .form-group:last-child {
  margin-bottom: 0;
}

.modal-contact .form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.modal-contact .required {
  color: #ef4444;
  margin-left: 2px;
}

.modal-contact .form-hint {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* Grid para campos lado a lado */
.modal-contact .form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Form Controls */
.modal-contact .form-control {
  width: 100%;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  transition: all 0.2s;
  background: white;
}

.modal-contact .form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.modal-contact .form-control::placeholder {
  color: #9ca3af;
}

/* Alerts */
.modal-contact .alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
}

.modal-contact .alert i {
  font-size: 16px;
  flex-shrink: 0;
}

.modal-contact .alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.modal-contact .alert-success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.modal-contact .alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.5;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: opacity 0.2s;
}

.modal-contact .alert-close:hover {
  opacity: 1;
}

/* Modal Footer */
.modal-contact .modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f9fafb;
}

/* Botões */
.modal-contact .btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.modal-contact .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-contact .btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.modal-contact .btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.modal-contact .btn-primary {
  background: #6366f1;
  color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.modal-contact .btn-primary:hover:not(:disabled) {
  background: #4f46e5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.modal-contact .btn-primary:active:not(:disabled) {
  background: #4338ca;
}

/* Botão Fechar */
.modal-contact .modal-close-btn {
  background: #f3f4f6;
  color: #6b7280;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-contact .modal-close-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

/* Scrollbar */
.modal-contact .modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-contact .modal-body::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.modal-contact .modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.modal-contact .modal-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsividade */
@media (max-width: 768px) {
  .modal-contact {
    max-width: 100%;
    margin: 10px;
    max-height: calc(100vh - 20px);
  }

  .modal-contact .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-contact .modal-body {
    max-height: calc(100vh - 180px);
    padding: 16px 20px;
  }

  .modal-contact .modal-header {
    padding: 16px 20px;
  }

  .modal-contact .modal-footer {
    padding: 12px 20px;
    flex-direction: column-reverse;
  }

  .modal-contact .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-contact .modal-header h3 {
    font-size: 16px;
  }

  .modal-contact .modal-header h3 i {
    font-size: 18px;
  }

  .modal-contact .collapsible-title span:first-of-type {
    font-size: 14px;
  }

  .modal-contact .collapsible-title i {
    font-size: 16px;
  }
}

.link-url-contacts {
  position: relative;
  color: #3b82f6;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: color 0.2s;
}

.link-url-contacts:hover {
  color: #2563eb;
  text-decoration: underline;
}

/* Tooltip customizado (opcional) */
.link-url-contacts[title]:hover::after {
  content: attr(title);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: 12px;
  z-index: 1000;
  margin-bottom: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Seta do tooltip */
.link-url-contacts[title]:hover::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #1f2937;
  margin-bottom: 2px;
}


/* Adicione no seu CSS */
.link-url {
  color: #3b82f6;
  text-decoration: none;
  word-break: break-all;
}

.link-url:hover {
  text-decoration: underline;
}

.links-table table {
  width: 100%;
  border-collapse: collapse;
}

.links-table th {
  background: #f3f4f6;
  padding: 12px;
  text-align: left;
  font-weight: 700;
  border-bottom: 2px solid #e5e7eb;
  color: #000
}

.links-table td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.links-table tbody tr:hover {
  background: #f9fafb;
}

.alert {
  padding: 12px 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.alert-error {
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.alert-success {
  background-color: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
}

.alert .icon {
  font-size: 20px;
}

.alert-close {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  color: inherit;
  opacity: 0.7;
}

.alert-close:hover {
  opacity: 1;
}

/* Form row para campos lado a lado */
.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-row .form-group {
  margin-bottom: 0;
}

/* Header da seção com botões de ação */
.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.section-header-with-actions h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.5rem;
  color: #333;
}

/* Botões no topo direito */
.actions-top-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.actions-top-right .btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.actions-top-right .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.actions-top-right .btn i {
  font-size: 16px;
}

.actions-top-right .btn-success {
  background: #28a745;
  color: white;
}

.actions-top-right .btn-success:hover {
  background: #218838;
}

.actions-top-right .btn-primary {
  background: #007bff;
  color: white;
}

.actions-top-right .btn-primary:hover {
  background: #0056b3;
}

.actions-top-right .btn-info {
  background: #17a2b8;
  color: white;
}

.actions-top-right .btn-info:hover {
  background: #138496;
}

/* Grid de listas e segmentos */
.lists-grid,
.segments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

/* Estilo do textarea no modal de adição em massa */
.modal-content textarea.form-control {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
}



.template-pagination {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.pagination-info-template {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 500;
}

.pagination-controls-template {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pagination-numbers {
  display: flex;
  gap: 0.5rem;
}

/* Botões de navegação (Anterior/Próximo) */
.btn-pagination {
  min-width: auto;
  height: 38px;
  padding: 0 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #6c757d;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 8px !important;
}

.btn-pagination:hover:not(:disabled) {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
  border-radius: 8px !important;
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6c757d;
  border-radius: 8px !important;
}

/* Botões de números das páginas */
.btn-pagination-number {
  min-width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #495057;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px !important;
}

.btn-pagination-number:hover {
  background: #343a40;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(73, 80, 87, 0.3);
  border-radius: 8px !important;
}

.btn-pagination-number.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  transform: translateY(-1px);
  border-radius: 8px !important;
}

@media (max-width: 480px) {
  .pagination-info-template {
    font-size: 0.8125rem;
  }

  .btn-pagination {
    height: 34px;
    padding: 0 0.625rem;
    font-size: 0.75rem;
    border-radius: 8px !important;
  }

  .btn-pagination-number {
    min-width: 34px;
    height: 34px;
    font-size: 0.875rem;
    border-radius: 8px !important;
  }
}

.template-select-card {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.template-select-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.template-select-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Thumbnail */
.template-thumbnail {
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.thumbnail-image {
  background: #fff;
  border-radius: 6px;
  padding: 6px;
  width: 100%;
  height: 85%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.thumbnail-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.thumbnail-placeholder,
.thumbnail-empty {
  font-size: 60px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.thumbnail-placeholder i,
.thumbnail-empty i {
  font-size: 48px;
}

.thumbnail-placeholder span {
  font-size: 12px;
}

/* Informações do Template */
.template-select-info {
  padding: 15px;
  flex: 1;
  cursor: pointer;
}

.template-select-info h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.template-subject {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Ações do Template */
.template-actions {
  padding: 0 15px 15px 15px;
  display: flex;
  gap: 8px;
}

.template-actions .btn {
  flex: 1;
  justify-content: center;
}

/* ========================================
   ✅ INDICADOR DE SELEÇÃO (SEMPRE VISÍVEL)
   ======================================== */
.selected-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.selected-indicator-templates {
  position: absolute;
  top: 35px;
  right: 2px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.selected-indicator i {
  font-size: 28px;
  color: #d1d5db;
  transition: all 0.3s ease;
  margin-top: -50px;
  margin-left: 50px;
  background-color: white;
  border-radius: 50%;
}

.selected-indicator.is-selected i {
  background-color: white;
  color: #10b981;
  animation: checkBounce 0.4s ease;
}

.selected-indicator-templates i {
  font-size: 28px;
  color: #d1d5db;
  transition: all 0.3s ease;
  margin-top: -50px;
  margin-left: 50px;
  background-color: white;
  border-radius: 50%;
}

.selected-indicator-templates.is-selected i {
  background-color: white;
  color: #10b981;
  animation: checkBounce 0.4s ease;
}

/* Animação ao selecionar */
@keyframes checkBounce {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }
}

/* Hover no card inteiro */
.list-select-card:hover .selected-indicator i {
  color: #9ca3af;
  /* Cinza médio no hover quando não selecionado */
}

.list-select-card:hover .selected-indicator.is-selected i {
  color: #059669;
  /* Verde mais escuro no hover quando selecionado */
}

/* Modal de Preview */
.template-preview-modal {
  width: 90vw;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
}

.template-preview-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
  background: #f9fafb;
}

.template-preview-iframe-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  padding: 20px;
}

.template-preview-iframe {
  width: 100%;
  max-width: 600px;
  height: 100%;
  min-height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.empty-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #9ca3af;
}

.empty-preview i {
  font-size: 64px;
}

/* Grid de Templates */
.templates-selection {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

/* Responsivo */
@media (max-width: 768px) {
  .actions-top-right {
    flex-direction: column;
    align-items: stretch;
  }

  .actions-top-right .btn {
    width: 100%;
    justify-content: center;
  }

  .lists-grid,
  .segments-grid {
    grid-template-columns: 1fr;
  }

  .form-row .form-group {
    margin-bottom: 15px;
  }

  .modern-header {
    flex-direction: column;
    gap: 1rem;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .campaigns-grid {
    grid-template-columns: 1fr;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .campaign-actions {
    flex-direction: column;
  }

  .campaign-actions .btn {
    width: 100%;
    justify-content: center;
  }


  .metrics-overview {
    grid-template-columns: 1fr;
  }

  .progress-steps {
    overflow-x: auto;
  }

  .modal-box-preview {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
  }

  .preview-toggle-wrapper {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .toggle-buttons {
    width: 100%;
    justify-content: center;
  }

  .width-indicator {
    text-align: center;
  }

  .iframe-container.mobile {
    width: 100%;
  }

  .preview-content-wrapper {
    padding: 15px;
  }

  .toggle-btn span {
    display: none;
  }

  .pagination-pages {
    max-width: 100%;
    overflow-x: auto;
    padding: 0.25rem 0;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 0.75rem;
  }

  .template-pagination {
    padding: 1rem;
  }

  .pagination-controls-template {
    gap: 0.75rem;
  }

  .pagination-numbers {
    overflow-x: auto;
    padding: 0.5rem 0;
  }

  .btn-pagination {
    padding: 0 0.75rem;
    font-size: 0.8125rem;
    border-radius: 8px !important;
  }

  .btn-pagination-number {
    min-width: 36px;
    height: 36px;
    border-radius: 8px !important;
  }

  .templates-selection {
    grid-template-columns: 1fr;
  }

  .template-preview-modal {
    width: 95vw;
    height: 95vh;
  }

  .template-preview-iframe {
    max-width: 100%;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.modal-box-preview {
  background: white;
  border-radius: 16px;
  width: 95vw;
  max-width: 1400px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 2px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.preview-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
}

.preview-header i {
  font-size: 22px;
}

.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  color: #333;
  transform: scale(1.05);
}

/* Toggle Desktop/Mobile */
.preview-toggle-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 30px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.toggle-buttons {
  display: flex;
  gap: 8px;
  background: white;
  padding: 4px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  transition: all 0.2s ease;
}

.toggle-btn i {
  font-size: 16px;
}

.toggle-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.toggle-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.width-indicator {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  padding: 8px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

/* Área de Preview */
.preview-content-wrapper {
  flex: 1;
  overflow: auto;
  background: #f3f4f6;
  padding: 30px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.iframe-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  height: fit-content;
}

.iframe-container.desktop {
  width: 100%;
  max-width: 900px;
}

.iframe-container.mobile {
  width: 400px;
  max-width: 100%;
}

.preview-iframe {
  width: 100%;
  min-height: 600px;
  border: none;
  display: block;
  background: white;
}

.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 80px 40px;
  color: #9ca3af;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.empty-preview i {
  font-size: 64px;
  opacity: 0.5;
}

.empty-preview p {
  font-size: 16px;
  margin: 0;
}

/* Footer do Modal */
.preview-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 30px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn i {
  font-size: 14px;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-success {
  background: #10b981;
  color: white;
  cursor: default;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Animação de Entrada */
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-box-preview {
  animation: modalFadeIn 0.2s ease;
}

.is-invalid {
  border-color: #e74c3c !important;
  background-color: #fff6f6;
}

.error-msg {
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 4px;
  display: block;
}

/* Status de Arquivo */
.campaign-archived-status {
  margin-top: 12px;
  padding: 10px;
  background-color: #fff3cd;
  border-left: 3px solid #856404;
  border-radius: 4px !important;
  font-size: 0.9rem;
}

/* Modal Styles (se não tiver já) */
.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}


.text-muted {
  color: #999;
}

.campaign-queued {
  margin-top: 12px;
  padding: 10px;
  background-color: #dafaff;
  border-left: 3px solid #17a2b8;
  border-radius: 4px !important;
  font-size: 0.9rem;
}

.campaign-suspended {
  margin-top: 12px;
  padding: 10px;
  background-color: #FFEBEE;
  border-left: 3px solid #C62828;
  border-radius: 4px !important;
  font-size: 0.9rem;
}

.campaign-queued i {
  color: #17a2b8;
  margin-right: 6px;
}

.cc-emails-section {
  margin: 10px 0;
  animation: slideDown 0.3s ease;
}

.cc-emails-list {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}

.cc-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 600;
  margin-bottom: 10px;
}

.cc-header i {
  color: #3b82f6;
}

.cc-email-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #374151;
  border-left: 3px solid #6b7280;
}

.cc-email-item:last-child {
  margin-bottom: 0;
}

.cc-email-item i {
  color: #9ca3af;
  font-size: 12px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contacts-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
}

.contacts-table table {
  width: 100%;
  border-collapse: collapse;
}

.contacts-table th {
  background: #f3f4f6;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.contacts-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.contact-row-main {
  background: white;
}

.contact-row-main:hover {
  background: #f9fafb;
}

.contact-row-secondary {
  background: #fafafa;
}

.contact-row-secondary .email-cc {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 40px;
  font-size: 13px;
  color: #6b7280;
}

.btn-toggle {
  width: 24px;
  height: 24px;
  border: none;
  background: #f3f4f6;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-toggle i {
  font-size: 10px;
  transition: transform 0.3s;
}

.btn-toggle i.rotated {
  transform: rotate(90deg);
}

.badge-cc-count {
  background: #f59e0b;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 8px;
}

.badge-cc {
  background: #6b7280;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
}

.badge {
  padding: 4px 8px;
  border-radius: 12px !important;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-success {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.badge-danger {
  background-color: #FFEBEE;
  color: #C62828;
}

.total-contacts {
  margin-top: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px !important;
  text-align: center;
}

.campaigns-manager {
  min-height: 100vh;
}

.campaigns-view {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.modern-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  background: #ffffff;
  border-radius: 16px !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.modern-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.icon-circle {
  background: #3b82f6;
  color: #fff;
  width: 50px;
  height: 50px;
  border-radius: 50% !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

/* Filter Buttons */
.filter-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #444;
  background: #ffffff;
  padding: 6px 12px;
  border-radius: 8px !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.filter-label i {
  color: #6c63ff;
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-link {
  background: transparent;
  color: #667eea;
  padding: 8px 12px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-refresh {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-refresh:hover {
  background-color: #e5e7eb;
}

/* Alert
.alert {
  padding: 15px 20px;
  border-radius: 8px !important;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideIn 0.3s ease;
}

.alert-error {
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
}

.alert-success {
  background-color: #efe;
  color: #3c3;
  border: 1px solid #cfc;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
}

.alert-close:hover {
  opacity: 1;
} */

@keyframes slideIn {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #667eea;
  border-radius: 50% !important;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Campaigns Grid */
.campaigns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  background-color: #ffffff;
  padding: 20px;
  border-radius: 16px !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.campaign-card {
  background: white;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.campaign-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.campaign-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.campaign-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 12px !important;
  padding: 4px 10px;
}

/* Status badges */
.status-sent {
  background-color: #E8F5E9;
  color: #2E7D32;
  border: 1px solid #C8E6C9;
}

.status-process {
  background-color: #E3F2FD;
  /* Azul suave - RECOMENDADO */
  color: #1565C0;
  border: 1px solid #BBDEFB;
}

.status-draft {
  background-color: #F5F5F5;
  color: #616161;
  border: 1px solid #E0E0E0;
}

.status-failed {
  background-color: #e5b9b9;
  color: #f12d2d;
  border: 1px solid #ba0b0b;
}

.status-queued {
  background-color: #b0edf7;
  color: #17a2b8;
  border: 1px solid #61ddf0;
}

.status-suspended {
  background-color: #FFEBEE;
  color: #C62828;
  border: 1px solid #FFCDD2;
}

.status-archived {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffe285;
}

.campaign-id {
  font-size: 11px;
  color: #999;
}

.campaign-info {
  padding: 15px;
}

.campaign-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.campaign-subject {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 10px 15px;
  border-radius: 8px !important;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.campaign-scheduled i {
  font-size: 16px;
}

.scheduled-date {
  font-weight: 600;
  margin-left: 5px;
}

.campaign-sent-date {
  margin-top: 12px;
  padding: 10px;
  background-color: #E8F5E9;
  border-left: 3px solid #43A047;
  border-radius: 4px !important;
  font-size: 0.9rem;
}

.campaign-sent-date i {
  font-size: 14px;
}

/* Botão warning para suspender */
.btn-warning {
  background: #f59e0b;
  color: white;
  border: none;
}

.btn-warning:hover:not(:disabled) {
  background: #d97706;
}

/* Destaque para data no resumo */
.highlight-date {
  color: #667eea;
  font-weight: 600;
  font-size: 15px;
}

.form-text {
  display: block;
  margin-top: 5px;
  font-size: 12px;
  color: #6b7280;
}

.campaign-metrics {
  padding: 15px;
  background: #f8f9fa;
  border-top: 1px solid #f0f0f0;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-item i {
  font-size: 16px;
  color: #667eea;
}

.metric-content {
  display: flex;
  flex-direction: column;
}

.metric-label {
  font-size: 10px;
  color: #999;
  text-transform: uppercase;
  font-weight: 600;
}

.metric-value {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.metric-percent {
  font-size: 11px;
  color: #28a745;
  font-weight: 600;
}

.campaign-actions {
  padding: 15px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Empty State */
.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 80px;
  color: #dee2e6;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 24px;
  color: #666;
  margin: 10px 0;
}

.empty-state p {
  font-size: 16px;
  color: #999;
}

/* Form */
.form-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
  font-weight: 600;
}

.form-content {
  background: white;
  border-radius: 12px !important;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-section h3 {
  margin: 0 0 20px 0;
  font-size: 20px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #555;
  font-size: 14px;
}

.form-control {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 6px !important;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px !important;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50% !important;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.step.active .step-number {
  background: #667eea;
  color: white;
}

.step.completed .step-number {
  background: #28a745;
  color: white;
}

.step-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.step-line {
  width: 60px;
  height: 2px;
  background: #e9ecef;
  margin: 0 10px;
}

.step-line.completed {
  background: #28a745;
}

/* Templates/Lists/Segments Selection */
.templates-selection,
.lists-selection,
.segments-selection {
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin: 20px 0;
}

.template-select-card,
.list-select-card,
.segment-select-card {
  border: 2px solid #e9ecef;
  border-radius: 8px !important;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.template-select-card:hover,
.list-select-card:hover,
.segment-select-card:hover {
  border-color: #667eea;
  background: #f8f9fa;
}

.template-select-card.selected,
.list-select-card.selected,
.segment-select-card.selected {
  border-color: #667eea;
  background: #e7f1ff;
}

.template-select-preview {
  height: 100px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.preview-mini {
  font-size: 36px;
  color: white;
  opacity: 0.8;
}

.template-select-info h4,
.list-select-info h4,
.segment-select-info h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #333;
}

.template-select-info p {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.list-count,
.segment-count {
  margin: 5px 0 0 0;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
}

.recipient-summary {
  margin: 20px 0;
  padding: 15px;
  background: #e7f1ff;
  border-radius: 8px !important;
  text-align: center;
  font-size: 16px;
  color: #667eea;
  font-weight: 600;
}

/* Campaign Summary */
.campaign-summary {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px !important;
  margin: 20px 0;
}

.campaign-summary h4 {
  margin: 0 0 15px 0;
  color: #333;
}

.campaign-summary ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.campaign-summary li {
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.campaign-summary li:last-child {
  border-bottom: none;
}

/* Recipient/Send Options */
.recipient-options,
.send-options {
  margin: 20px 0;
}

.recipient-options label,
.send-options label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;
  padding: 12px;
  border-radius: 6px !important;
  margin-bottom: 10px;
}

.recipient-options label:hover,
.send-options label:hover {
  background: #f8f9fa;
}

.recipient-options input[type="radio"],
.send-options input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.schedule-options {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px !important;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-large {
  max-width: 800px;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-close-btn {
  cursor: pointer !important;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  transition: color 0.2s;
  z-index: 1000;
}

.modal-close-btn:hover {
  color: #000;
}

.modal-body {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.highlight-text {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 5px !important;
  font-size: 16px;
  color: #333;
}

.warning-text {
  color: #dc3545;
  font-weight: 500;
  font-size: 14px;
}

/* Detailed Metrics */
.detailed-metrics h4 {
  margin: 0 0 20px 0;
  font-size: 20px;
  color: #333;
}

.metrics-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.metric-box {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px !important;
  display: flex;
  align-items: center;
  gap: 15px;
}

.metric-box.success {
  background: #d4edda;
}

.metric-box.info {
  background: #d1ecf1;
}

.metric-box i {
  font-size: 32px;
  color: #667eea;
}

.metric-value.large {
  font-size: 28px;
  font-weight: 700;
  color: #333;
  display: block;
}

.metric-sublabel {
  font-size: 12px;
  color: #666;
  display: block;
}

.metrics-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.metric-detail-card {
  background: white;
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 8px !important;
}

.metric-detail-card h5 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  border-bottom: 2px solid #667eea;
  padding-bottom: 8px;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
  font-size: 14px;
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-row span:first-child {
  color: #666;
}

.metric-row span:last-child {
  font-weight: 600;
  color: #333;
}

.pagination-container {
  width: 100%;
  box-sizing: border-box;
  margin: 0;
}

.pagination-info {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pagination-pages {
  display: flex;
  gap: 0.25rem;
}

.pagination-controls .btn {
  min-width: 40px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-controls .btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-weight: 600;
}


/* Estilos adicionais para textos de confirmação */
.danger-text {
  color: #dc3545;
  font-weight: 600;
  padding: 0.75rem;
  background: #fee;
  border-radius: 6px;
  margin-top: 0.5rem;
}

.warning-text {
  color: #856404;
  background: #fff3cd;
  padding: 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.9rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  z-index: 10000;
  animation: modalSlideIn 0.3s ease;
}

.modal-content-2 {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 1000px;
  width: 90%;
  max-height: 100%;
  position: relative;
  z-index: 10000;
  animation: modalSlideIn 0.3s ease;
}

.modal-small {
  max-width: 500px;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  width: 32px;
  height: 32px;
  border-radius: 6px;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.confirmation-message {
  text-align: center;
}

.confirmation-message i.warning-icon {
  font-size: 3rem;
  color: #f59e0b;
  margin-bottom: 1rem;
}

.campaign-name-highlight {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  padding: 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  margin: 1rem 0;
}

.warning-text {
  color: #856404;
  background: #fff3cd;
  padding: 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.9rem;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Garantir que botões sejam clicáveis */
.modal-footer .btn {
  pointer-events: auto;
  cursor: pointer;
  z-index: 1;
}

.modal-footer .btn:disabled {
  pointer-events: none;
  opacity: 0.6;
}

/* ========== MODAL DE IMPORTAÇÃO JSON ========== */
.info-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.info-card h4 {
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item .label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.info-item .value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

/* Strategy Card */
.strategy-card {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.strategy-card h4 {
  margin: 0 0 15px 0;
  color: #1e40af;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.strategy-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.strategy-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.strategy-item i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.strategy-item strong {
  display: block;
  color: #1f2937;
  font-size: 14px;
  margin-bottom: 4px;
}

.strategy-item p {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

/* Preview Card */
.preview-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.preview-card h4 {
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.contacts-preview {
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 500px;
  overflow-y: auto;
}

.contact-preview-item {
  display: flex;
  gap: 15px;
  padding: 15px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.contact-number {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
}

.contact-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-main strong {
  font-size: 16px;
  color: #1f2937;
}

.contact-main .email {
  font-size: 13px;
  color: #3b82f6;
  font-family: 'Courier New', monospace;
}

.contact-main .email-type {
  display: inline-block;
  padding: 2px 8px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.contact-meta,
.contact-financial {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.titulo-preview {
  padding: 12px;
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 6px;
}

.titulo-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: #92400e;
  font-size: 13px;
}

.titulo-details {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #78350f;
}

.titulo-details span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.titulo-details .atraso {
  background: #fee2e2;
  padding: 3px 8px;
  border-radius: 4px;
  color: #991b1b;
  font-weight: 600;
}

.contact-link .btn-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.contact-link .btn-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.preview-more {
  text-align: center;
  padding: 14px;
  background: #eff6ff;
  border-radius: 6px;
  color: #3b82f6;
  font-weight: 600;
  margin-top: 15px;
}

.badge-protected {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-family: 'Courier New', monospace;
  font-weight: 700;
}

.lgpd-tag {
  background: rgba(255, 255, 255, 0.25);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  letter-spacing: 0.5px;
  margin-left: 4px;
}

/* ========== LISTA DE TÍTULOS ========== */
.titulos-list {
  background: #fef3c7;
  border-radius: 8px;
  padding: 15px;
  margin-top: 12px;
}

.titulos-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #92400e;
  font-size: 14px;
  font-weight: 600;
}

.titulo-item {
  background: white;
  border-left: 3px solid #f59e0b;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.2s ease;
}

.titulo-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.titulo-item:last-child {
  margin-bottom: 0;
}

.titulo-item-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.titulo-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.titulo-numero {
  color: #92400e;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.titulo-item-details {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #78350f;
  padding-left: 4px;
}

.titulo-item-details span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.titulo-item-details strong {
  margin-right: 2px;
}

.titulo-item-details .atraso {
  background: #fee2e2;
  padding: 3px 8px;
  border-radius: 4px;
  color: #991b1b;
  font-weight: 600;
}

/* ✅ VALOR TOTAL */
.titulo-total {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 2px dashed #f59e0b;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #92400e;
}

.titulo-total .valor-destaque {
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 15px;
  margin-left: auto;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
}

/* ========== TOOLTIPS ========== */
.contact-meta [title],
.contact-financial [title],
.titulo-item [title],
.contact-main [title],
.contact-link [title] {
  cursor: help;
  position: relative;
}

.contact-meta [title]:hover,
.contact-financial [title]:hover,
.titulo-item [title]:hover,
.contact-main [title]:hover {
  opacity: 0.9;
}

/* Badge styles melhorados */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.contact-main .email {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ========== ACCORDION DE DEVEDORES ========== */
.devedores-accordion {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.devedor-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.devedor-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.devedor-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  cursor: pointer;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  transition: all 0.3s ease;
}

.devedor-header:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
}

.devedor-header.active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-bottom: 2px solid #3b82f6;
}

.devedor-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.devedor-numero {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
}

.devedor-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.devedor-title strong {
  font-size: 16px;
  color: #1f2937;
}

.devedor-cpd {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

.devedor-stats {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.stat-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}

.stat-badge.stat-valor {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-color: #dc2626;
  color: #991b1b;
}

.toggle-icon {
  font-size: 18px;
  color: #6b7280;
  transition: transform 0.3s ease;
}

.toggle-icon.rotated {
  transform: rotate(180deg);
}

/* ========== CONTEÚDO EXPANDÍVEL ========== */
.devedor-content {
  padding: 20px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

/* ========== DETALHES DO DEVEDOR ========== */
.devedor-detalhes {
  background: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detalhe-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.detalhe-item:last-child {
  border-bottom: none;
}

.detalhe-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #4b5563;
  font-size: 13px;
}

.detalhe-valor {
  color: #1f2937;
  font-weight: 600;
}

.detalhe-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.detalhe-link:hover {
  color: #2563eb;
  gap: 8px;
}

.lgpd-tag-small {
  background: rgba(99, 102, 241, 0.2);
  color: #4f46e5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 6px;
}

/* ========== LISTA DE EMAILS ========== */
.emails-list {
  background: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.emails-list h5 {
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.email-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.email-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid #3b82f6;
  transition: all 0.2s ease;
}

.email-item:hover {
  background: #f3f4f6;
  transform: translateX(4px);
}

.email-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.email-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.email-address {
  font-family: monospace;
  color: #374151;
  font-size: 14px;
  word-break: break-all;
  white-space: normal;
  /* Permite quebra */
}

.email-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.email-badge.badge-primary {
  background: #dbeafe;
  color: #1e40af;
}

.email-badge.badge-secondary {
  background: #f3f4f6;
  color: #6b7280;
}


/* ========== TÍTULOS ========== */
.titulos-section {
  background: white;
  border-radius: 8px;
  padding: 15px;
}

.titulos-section h5 {
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.titulos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
}

.titulo-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #f59e0b;
  transition: all 0.2s ease;
}

.titulo-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.titulo-card-header {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.titulo-card-badge {
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.titulo-card-numero {
  color: white;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.titulo-card-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.titulo-card-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.titulo-card-label {
  font-size: 12px;
  color: #78350f;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.titulo-card-value {
  font-size: 12px;
  color: #92400e;
  font-weight: 700;
}

.titulo-card-value.valor-destaque {
  color: #dc2626;
  font-size: 14px;
}

.titulo-card-value.atraso-alert {
  color: #dc2626;
  background: #fee2e2;
  padding: 2px 8px;
  border-radius: 4px;
}

.titulos-total-box {
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 2px solid #dc2626;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #991b1b;
}

.titulos-total-box .total-valor {
  margin-left: auto;
  font-size: 18px;
  font-weight: 700;
  color: #7f1d1d;
}

/* ========== RESUMO FINAL ========== */
.preview-summary {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 4px solid #3b82f6;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* ========== BOTÕES DA LISTA (LADO A LADO) ========== */
.list-actions-buttons {
  display: flex;
  gap: 1.5px;
  margin-top: 12px;
}

.list-actions-buttons .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}

/* Botões principais crescem igualmente */
.list-actions-buttons .btn-info,
.list-actions-buttons .btn-success {
  flex: 1;
  padding: 8px 6px;
}

/* Botões de ícone: tamanho fixo quadrado */
.list-actions-buttons .btn-warning,
.list-actions-buttons .btn-danger {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  padding: 0;
}

.list-actions-buttons .btn-info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.list-actions-buttons .btn-info:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(37, 99, 235, 0.3);
}

.list-actions-buttons .btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.list-actions-buttons .btn-success:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(5, 150, 105, 0.3);
}

.list-actions-buttons .btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.list-actions-buttons .btn-warning:hover:not(:disabled) {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(217, 119, 6, 0.3);
}

.list-actions-buttons .btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.list-actions-buttons .btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
}

.list-actions-buttons .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.list-actions-buttons .btn i {
  font-size: 13px;
}


.list-select-card {
  position: relative;
  /* Necessário para posicionar o tooltip */
}

/* Tooltip padrão (escondido) */
.list-select-card::before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 12px);
  /* Posiciona acima do card */
  left: 50%;
  transform: translateX(-50%) scale(0.9);

  /* Estilo */
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  /* Sombra */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;

  /* Animação */
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
}

/* Seta do tooltip */
.list-select-card::after {
  content: '';
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%) scale(0.9);

  /* Triângulo */
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #1f2937;

  /* Animação */
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
}

/* Mostra tooltip ao passar o mouse */
.list-select-card:hover::before,
.list-select-card:hover::after {
  opacity: 1;
  transform: translateX(-50%) scale(1);
}

/* ✨ Animação de entrada suave */
.list-select-card:hover::before {
  animation: tooltipBounce 0.3s ease-out;
}

@keyframes tooltipBounce {
  0% {
    transform: translateX(-50%) scale(0.9) translateY(5px);
    opacity: 0;
  }

  60% {
    transform: translateX(-50%) scale(1.02) translateY(-2px);
  }

  100% {
    transform: translateX(-50%) scale(1) translateY(0);
    opacity: 1;
  }
}

.list-select-card.selected::after {
  border-top-color: #059669;
}

/* 📱 Mobile: Desabilita tooltip em telas pequenas */
@media (max-width: 768px) {

  .list-select-card::before,
  .list-select-card::after {
    display: none;
  }
}

.validation-stages {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 30px;
}

.stage-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0.4;
  transition: opacity 0.3s;
}

.stage-item.active {
  opacity: 1;
}

.stage-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.3s;
}

.stage-item.active .stage-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.stage-line {
  width: 60px;
  height: 2px;
  background: #e5e7eb;
  transition: background 0.3s;
}

.stage-line.active {
  background: #3b82f6;
}

.progress-bar-container {
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 15px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  transition: width 0.3s ease;
}

.progress-info {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
}

.validation-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin: 20px 0;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.stat-card i {
  font-size: 24px;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.validation-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  margin-top: 20px;
}

.validation-message i {
  font-size: 20px;
  color: #3b82f6;
}

.validation-message p {
  margin: 0;
  color: #1e40af;
  font-weight: 500;
}

.validation-time-info {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin: 20px 0;
  padding: 15px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 8px;
  border: 1px solid #d1d5db;
}

.time-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.time-item i {
  color: #6366f1;
  font-size: 16px;
}

.time-label {
  font-weight: 500;
  color: #6b7280;
}

.time-value {
  font-weight: 700;
  color: #1f2937;
  background: white;
  padding: 4px 12px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-width: 70px;
  text-align: center;
}

/* Progress count */
.progress-count {
  color: #6b7280;
  font-size: 14px;
  margin-left: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .validation-time-info {
    flex-direction: column;
    gap: 10px;
  }

  .time-item {
    justify-content: space-between;
  }
}


/* ========================================
   🎯 MENSAGEM INFORMATIVA DE SELEÇÃO
   ======================================== */
.selection-reminder {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  margin-bottom: 50px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  animation: slideDown 0.5s ease-out;
}

.selection-reminder-icon {
  font-size: 28px;
  animation: bounce 2s ease-in-out infinite;
}

.selection-reminder-text {
  flex: 1;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.5;
}

/* Animações */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

/* Responsivo */
@media (max-width: 768px) {
  .selection-reminder {
    padding: 12px 16px;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }

  .selection-reminder-text {
    font-size: 13px;
  }

  .selection-reminder-icon {
    font-size: 32px;
  }
}

@media (max-width: 576px) {
  .selection-reminder {
    padding: 10px 14px;
    margin-bottom: 16px;
  }

  .selection-reminder-text {
    font-size: 12px;
  }
}

/* ========================================
   💬 TOOLTIP NO HOVER DO CARD
   ======================================== */
.list-select-card {
  position: relative;
}

/* Tooltip (escondido por padrão) */
.list-select-card::before {
  content: 'Selecione uma lista de contatos para continuar';
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease, transform 0.3s ease;
  z-index: 1000;
}

/* Seta do tooltip */
.list-select-card::after {
  content: '';
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #1e293b;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  z-index: 1000;
}

/* Mostra tooltip no hover */
.list-select-card:hover::before,
.list-select-card:hover::after {
  opacity: 1;
}

/* Animação suave de entrada */
.list-select-card:hover::before {
  animation: tooltipFadeIn 0.3s ease;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-5px);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 🎨 Tooltip quando lista está SELECIONADA */
.list-select-card.selected::before {
  content: "✓ Lista selecionada! Clique novamente para desmarcar";
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  display: block;
  /* ✅ Garante que aparece */
}

.list-select-card.selected::after {
  border-top-color: #059669;
  display: block;
  /* ✅ Garante que a seta aparece */
}

/* Mobile: Esconde tooltip em telas pequenas */
@media (max-width: 768px) {

  .list-select-card:hover::before,
  .list-select-card:hover::after {
    display: none;
  }
}

.campaign-list-info {
  margin-top: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-left: 3px solid #0ea5e9;
  border-radius: 8px !important;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.campaign-list-info-item {
  margin: 0;
  font-size: 13px;
  color: #0c4a6e;
  display: flex;
  align-items: flex-start;
  gap: 7px;
  line-height: 1.4;
}

.campaign-list-info-item i {
  color: #0284c7;
  font-size: 13px;
  margin-top: 1px;
  flex-shrink: 0;
}

.campaign-list-info-item strong {
  color: #075985;
  white-space: nowrap;
  flex-shrink: 0;
}

.lote-text {
  font-family: monospace;
  font-size: 11.5px;
  background: rgba(255, 255, 255, 0.7);
  padding: 2px 6px;
  border-radius: 4px !important;
  cursor: default;
  word-break: break-word;
}
</style>